import { Matrix3, Vector3, Vector4, DataTexture, RGBAFormat, RedFormat, LinearFilter, FloatType, UnsignedIntType, UnsignedByteType, Mesh, BufferGeometry, Triangle, BufferAttribute, Vector2, Float32BufferAttribute, ShaderMaterial, UniformsUtils, InterleavedBufferAttribute } from "three";
import { BufferGeometryUtils as BufferGeometryUtils$1 } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter.js";
import { FloatTextureLinearFilterSupport, OctreeBufferGeometrySerializer, BufferGeometrySerializer, OctreeBufferGeometry, BufferGeometryUtils, OctreeMeshSkinned, OctreeMesh } from "./three-js-extra.module.js";

class Signal {
  #slots = [];

  /**
   * Removes the given function from the listeners
   * @param {function} slot the function to be removed
   */
  #unsubscribe(slot) {
    this.#slots = this.#slots.filter((item) => item !== slot);
  }

  /**
   * Subscribes the given function to the signal and returns an unsubscribing function
   * @param {function} slot function called whenever the signal is raised.
   * @returns {function} unsubscribing function
   */
  on(slot) {
    this.#slots = [...this.#slots, slot];
    return () => this.#unsubscribe(slot);
  }

  /**
   * Raises a new value. All listeners will be called with this value as parameter.
   * @param {any} value Value passed to the listeners.
   */
  emit(value = null) {
    this.#slots.forEach((slot) => slot(value));
  }

  readonly() {
    const self = this;
    return {
      on: (slot) => self.on(slot)
    };
  }
}

const __extends$4 =
  (undefined && undefined.t) ||
  (function () {
    var extendStatics = function (n, t) {
      extendStatics =
        Object.setPrototypeOf ||
        ({
          __proto__: []
        } instanceof Array &&
          function (n, t) {
            n.__proto__ = t;
          }) ||
        function (n, t) {
          for (const r in t) if (Object.prototype.hasOwnProperty.call(t, r)) n[r] = t[r];
        };
      return extendStatics(n, t);
    };
    return function (n, t) {
      if (typeof t !== "function" && t !== null) throw new TypeError(`Class extends value ${String(t)} is not a constructor or null`);
      extendStatics(n, t);
      function __() {
        this.constructor = n;
      }
      n.prototype = t === null ? Object.create(t) : ((__.prototype = t.prototype), new __());
    };
  })();

const ContainerIterator = (function () {
  function ContainerIterator(n) {
    if (n === void 0) {
      n = 0;
    }
    this.iteratorType = n;
  }
  ContainerIterator.prototype.equals = function (n) {
    return this.o === n.o;
  };
  return ContainerIterator;
})();

const Base = (function () {
  function Base() {
    this.i = 0;
  }
  Object.defineProperty(Base.prototype, "length", {
    get() {
      return this.i;
    },
    enumerable: false,
    configurable: true
  });
  Base.prototype.size = function () {
    return this.i;
  };
  Base.prototype.empty = function () {
    return this.i === 0;
  };
  return Base;
})();

const Container = (function (n) {
  __extends$4(Container, n);
  function Container() {
    return (n !== null && n.apply(this, arguments)) || this;
  }
  return Container;
})(Base);

function throwIteratorAccessError() {
  throw new RangeError("Iterator access denied!");
}

const __extends$3 =
  (undefined && undefined.t) ||
  (function () {
    var extendStatics = function (e, t) {
      extendStatics =
        Object.setPrototypeOf ||
        ({
          __proto__: []
        } instanceof Array &&
          function (e, t) {
            e.__proto__ = t;
          }) ||
        function (e, t) {
          for (const n in t) if (Object.prototype.hasOwnProperty.call(t, n)) e[n] = t[n];
        };
      return extendStatics(e, t);
    };
    return function (e, t) {
      if (typeof t !== "function" && t !== null) throw new TypeError(`Class extends value ${String(t)} is not a constructor or null`);
      extendStatics(e, t);
      function __() {
        this.constructor = e;
      }
      e.prototype = t === null ? Object.create(t) : ((__.prototype = t.prototype), new __());
    };
  })();

const TreeNode = (function () {
  function TreeNode(e, t, n) {
    if (n === void 0) {
      n = 1;
    }
    this.Y = undefined;
    this.Z = undefined;
    this.tt = undefined;
    this.p = e;
    this.H = t;
    this.ee = n;
  }
  TreeNode.prototype.L = function () {
    let e = this;
    const t = e.tt.tt === e;
    if (t && e.ee === 1) {
      e = e.Z;
    } else if (e.Y) {
      e = e.Y;
      while (e.Z) {
        e = e.Z;
      }
    } else {
      if (t) {
        return e.tt;
      }
      let n = e.tt;
      while (n.Y === e) {
        e = n;
        n = e.tt;
      }
      e = n;
    }
    return e;
  };
  TreeNode.prototype.m = function () {
    let e = this;
    if (e.Z) {
      e = e.Z;
      while (e.Y) {
        e = e.Y;
      }
      return e;
    }
    let t = e.tt;
    while (t.Z === e) {
      e = t;
      t = e.tt;
    }
    if (e.Z !== t) {
      return t;
    }
    return e;
  };
  TreeNode.prototype.te = function () {
    const e = this.tt;
    const t = this.Z;
    const n = t.Y;
    if (e.tt === this) e.tt = t;
    else if (e.Y === this) e.Y = t;
    else e.Z = t;
    t.tt = e;
    t.Y = this;
    this.tt = t;
    this.Z = n;
    if (n) n.tt = this;
    return t;
  };
  TreeNode.prototype.ne = function () {
    const e = this.tt;
    const t = this.Y;
    const n = t.Z;
    if (e.tt === this) e.tt = t;
    else if (e.Y === this) e.Y = t;
    else e.Z = t;
    t.tt = e;
    t.Z = this;
    this.tt = t;
    this.Y = n;
    if (n) n.tt = this;
    return t;
  };
  return TreeNode;
})();

const TreeNodeEnableIndex = (function (e) {
  __extends$3(TreeNodeEnableIndex, e);
  function TreeNodeEnableIndex() {
    const t = (e !== null && e.apply(this, arguments)) || this;
    t.rt = 1;
    return t;
  }
  TreeNodeEnableIndex.prototype.te = function () {
    const t = e.prototype.te.call(this);
    this.ie();
    t.ie();
    return t;
  };
  TreeNodeEnableIndex.prototype.ne = function () {
    const t = e.prototype.ne.call(this);
    this.ie();
    t.ie();
    return t;
  };
  TreeNodeEnableIndex.prototype.ie = function () {
    this.rt = 1;
    if (this.Y) {
      this.rt += this.Y.rt;
    }
    if (this.Z) {
      this.rt += this.Z.rt;
    }
  };
  return TreeNodeEnableIndex;
})(TreeNode);

const __extends$2 =
  (undefined && undefined.t) ||
  (function () {
    var extendStatics = function (e, i) {
      extendStatics =
        Object.setPrototypeOf ||
        ({
          __proto__: []
        } instanceof Array &&
          function (e, i) {
            e.__proto__ = i;
          }) ||
        function (e, i) {
          for (const r in i) if (Object.prototype.hasOwnProperty.call(i, r)) e[r] = i[r];
        };
      return extendStatics(e, i);
    };
    return function (e, i) {
      if (typeof i !== "function" && i !== null) throw new TypeError(`Class extends value ${String(i)} is not a constructor or null`);
      extendStatics(e, i);
      function __() {
        this.constructor = e;
      }
      e.prototype = i === null ? Object.create(i) : ((__.prototype = i.prototype), new __());
    };
  })();

const TreeContainer = (function (e) {
  __extends$2(TreeContainer, e);
  function TreeContainer(i, r) {
    if (i === void 0) {
      i = function (e, i) {
        if (e < i) return -1;
        if (e > i) return 1;
        return 0;
      };
    }
    if (r === void 0) {
      r = false;
    }
    const t = e.call(this) || this;
    t.rr = undefined;
    t.j = i;
    t.enableIndex = r;
    t.re = r ? TreeNodeEnableIndex : TreeNode;
    t.u = new t.re();
    return t;
  }
  TreeContainer.prototype.$ = function (e, i) {
    let r = this.u;
    while (e) {
      const t = this.j(e.p, i);
      if (t < 0) {
        e = e.Z;
      } else if (t > 0) {
        r = e;
        e = e.Y;
      } else return e;
    }
    return r;
  };
  TreeContainer.prototype.tr = function (e, i) {
    let r = this.u;
    while (e) {
      const t = this.j(e.p, i);
      if (t <= 0) {
        e = e.Z;
      } else {
        r = e;
        e = e.Y;
      }
    }
    return r;
  };
  TreeContainer.prototype.er = function (e, i) {
    let r = this.u;
    while (e) {
      const t = this.j(e.p, i);
      if (t < 0) {
        r = e;
        e = e.Z;
      } else if (t > 0) {
        e = e.Y;
      } else return e;
    }
    return r;
  };
  TreeContainer.prototype.nr = function (e, i) {
    let r = this.u;
    while (e) {
      const t = this.j(e.p, i);
      if (t < 0) {
        r = e;
        e = e.Z;
      } else {
        e = e.Y;
      }
    }
    return r;
  };
  TreeContainer.prototype.se = function (e) {
    while (true) {
      const i = e.tt;
      if (i === this.u) return;
      if (e.ee === 1) {
        e.ee = 0;
        return;
      }
      if (e === i.Y) {
        var r = i.Z;
        if (r.ee === 1) {
          r.ee = 0;
          i.ee = 1;
          if (i === this.rr) {
            this.rr = i.te();
          } else i.te();
        } else {
          if (r.Z && r.Z.ee === 1) {
            r.ee = i.ee;
            i.ee = 0;
            r.Z.ee = 0;
            if (i === this.rr) {
              this.rr = i.te();
            } else i.te();
            return;
          }
          if (r.Y && r.Y.ee === 1) {
            r.ee = 1;
            r.Y.ee = 0;
            r.ne();
          } else {
            r.ee = 1;
            e = i;
          }
        }
      } else {
        var r = i.Y;
        if (r.ee === 1) {
          r.ee = 0;
          i.ee = 1;
          if (i === this.rr) {
            this.rr = i.ne();
          } else i.ne();
        } else {
          if (r.Y && r.Y.ee === 1) {
            r.ee = i.ee;
            i.ee = 0;
            r.Y.ee = 0;
            if (i === this.rr) {
              this.rr = i.ne();
            } else i.ne();
            return;
          }
          if (r.Z && r.Z.ee === 1) {
            r.ee = 1;
            r.Z.ee = 0;
            r.te();
          } else {
            r.ee = 1;
            e = i;
          }
        }
      }
    }
  };
  TreeContainer.prototype.U = function (e) {
    if (this.i === 1) {
      this.clear();
      return;
    }
    let i = e;
    while (i.Y || i.Z) {
      if (i.Z) {
        i = i.Z;
        while (i.Y) i = i.Y;
      } else {
        i = i.Y;
      }
      const r = e.p;
      e.p = i.p;
      i.p = r;
      const t = e.H;
      e.H = i.H;
      i.H = t;
      e = i;
    }
    if (this.u.Y === i) {
      this.u.Y = i.tt;
    } else if (this.u.Z === i) {
      this.u.Z = i.tt;
    }
    this.se(i);
    let n = i.tt;
    if (i === n.Y) {
      n.Y = undefined;
    } else n.Z = undefined;
    this.i -= 1;
    this.rr.ee = 0;
    if (this.enableIndex) {
      while (n !== this.u) {
        n.rt -= 1;
        n = n.tt;
      }
    }
  };
  TreeContainer.prototype.ir = function (e) {
    const i = typeof e === "number" ? e : undefined;
    const r = typeof e === "function" ? e : undefined;
    const t = typeof e === "undefined" ? [] : undefined;
    let n = 0;
    let s = this.rr;
    const f = [];
    while (f.length || s) {
      if (s) {
        f.push(s);
        s = s.Y;
      } else {
        s = f.pop();
        if (n === i) return s;
        t && t.push(s);
        r && r(s, n, this);
        n += 1;
        s = s.Z;
      }
    }
    return t;
  };
  TreeContainer.prototype.fe = function (e) {
    while (true) {
      const i = e.tt;
      if (i.ee === 0) return;
      const r = i.tt;
      if (i === r.Y) {
        var t = r.Z;
        if (t && t.ee === 1) {
          t.ee = i.ee = 0;
          if (r === this.rr) return;
          r.ee = 1;
          e = r;
          continue;
        } else if (e === i.Z) {
          e.ee = 0;
          if (e.Y) {
            e.Y.tt = i;
          }
          if (e.Z) {
            e.Z.tt = r;
          }
          i.Z = e.Y;
          r.Y = e.Z;
          e.Y = i;
          e.Z = r;
          if (r === this.rr) {
            this.rr = e;
            this.u.tt = e;
          } else {
            var n = r.tt;
            if (n.Y === r) {
              n.Y = e;
            } else n.Z = e;
          }
          e.tt = r.tt;
          i.tt = e;
          r.tt = e;
          r.ee = 1;
        } else {
          i.ee = 0;
          if (r === this.rr) {
            this.rr = r.ne();
          } else r.ne();
          r.ee = 1;
          return;
        }
      } else {
        var t = r.Y;
        if (t && t.ee === 1) {
          t.ee = i.ee = 0;
          if (r === this.rr) return;
          r.ee = 1;
          e = r;
          continue;
        } else if (e === i.Y) {
          e.ee = 0;
          if (e.Y) {
            e.Y.tt = r;
          }
          if (e.Z) {
            e.Z.tt = i;
          }
          r.Z = e.Y;
          i.Y = e.Z;
          e.Y = r;
          e.Z = i;
          if (r === this.rr) {
            this.rr = e;
            this.u.tt = e;
          } else {
            var n = r.tt;
            if (n.Y === r) {
              n.Y = e;
            } else n.Z = e;
          }
          e.tt = r.tt;
          i.tt = e;
          r.tt = e;
          r.ee = 1;
        } else {
          i.ee = 0;
          if (r === this.rr) {
            this.rr = r.te();
          } else r.te();
          r.ee = 1;
          return;
        }
      }
      if (this.enableIndex) {
        i.ie();
        r.ie();
        e.ie();
      }
      return;
    }
  };
  TreeContainer.prototype.v = function (e, i, r) {
    if (this.rr === undefined) {
      this.i += 1;
      this.rr = new this.re(e, i, 0);
      this.rr.tt = this.u;
      this.u.tt = this.u.Y = this.u.Z = this.rr;
      return this.i;
    }
    let t;
    const n = this.u.Y;
    const s = this.j(n.p, e);
    if (s === 0) {
      n.H = i;
      return this.i;
    }
    if (s > 0) {
      n.Y = new this.re(e, i);
      n.Y.tt = n;
      t = n.Y;
      this.u.Y = t;
    } else {
      const f = this.u.Z;
      const h = this.j(f.p, e);
      if (h === 0) {
        f.H = i;
        return this.i;
      }
      if (h < 0) {
        f.Z = new this.re(e, i);
        f.Z.tt = f;
        t = f.Z;
        this.u.Z = t;
      } else {
        if (r !== undefined) {
          const u = r.o;
          if (u !== this.u) {
            const a = this.j(u.p, e);
            if (a === 0) {
              u.H = i;
              return this.i;
            }
            if (a > 0) {
              const o = u.L();
              const l = this.j(o.p, e);
              if (l === 0) {
                o.H = i;
                return this.i;
              }
              if (l < 0) {
                t = new this.re(e, i);
                if (o.Z === undefined) {
                  o.Z = t;
                  t.tt = o;
                } else {
                  u.Y = t;
                  t.tt = u;
                }
              }
            }
          }
        }
        if (t === undefined) {
          t = this.rr;
          while (true) {
            const v = this.j(t.p, e);
            if (v > 0) {
              if (t.Y === undefined) {
                t.Y = new this.re(e, i);
                t.Y.tt = t;
                t = t.Y;
                break;
              }
              t = t.Y;
            } else if (v < 0) {
              if (t.Z === undefined) {
                t.Z = new this.re(e, i);
                t.Z.tt = t;
                t = t.Z;
                break;
              }
              t = t.Z;
            } else {
              t.H = i;
              return this.i;
            }
          }
        }
      }
    }
    if (this.enableIndex) {
      let d = t.tt;
      while (d !== this.u) {
        d.rt += 1;
        d = d.tt;
      }
    }
    this.fe(t);
    this.i += 1;
    return this.i;
  };
  TreeContainer.prototype.ar = function (e, i) {
    while (e) {
      const r = this.j(e.p, i);
      if (r < 0) {
        e = e.Z;
      } else if (r > 0) {
        e = e.Y;
      } else return e;
    }
    return e || this.u;
  };
  TreeContainer.prototype.clear = function () {
    this.i = 0;
    this.rr = undefined;
    this.u.tt = undefined;
    this.u.Y = this.u.Z = undefined;
  };
  TreeContainer.prototype.updateKeyByIterator = function (e, i) {
    const r = e.o;
    if (r === this.u) {
      throwIteratorAccessError();
    }
    if (this.i === 1) {
      r.p = i;
      return true;
    }
    const t = r.m().p;
    if (r === this.u.Y) {
      if (this.j(t, i) > 0) {
        r.p = i;
        return true;
      }
      return false;
    }
    const n = r.L().p;
    if (r === this.u.Z) {
      if (this.j(n, i) < 0) {
        r.p = i;
        return true;
      }
      return false;
    }
    if (this.j(n, i) >= 0 || this.j(t, i) <= 0) return false;
    r.p = i;
    return true;
  };
  TreeContainer.prototype.eraseElementByPos = function (e) {
    if (e < 0 || e > this.i - 1) {
      throw new RangeError();
    }
    const i = this.ir(e);
    this.U(i);
    return this.i;
  };
  TreeContainer.prototype.eraseElementByKey = function (e) {
    if (this.i === 0) return false;
    const i = this.ar(this.rr, e);
    if (i === this.u) return false;
    this.U(i);
    return true;
  };
  TreeContainer.prototype.eraseElementByIterator = function (e) {
    const i = e.o;
    if (i === this.u) {
      throwIteratorAccessError();
    }
    const r = i.Z === undefined;
    const t = e.iteratorType === 0;
    if (t) {
      if (r) e.next();
    } else if (!r || i.Y === undefined) e.next();
    this.U(i);
    return e;
  };
  TreeContainer.prototype.getHeight = function () {
    if (this.i === 0) return 0;
    function traversal(e) {
      if (!e) return 0;
      return Math.max(traversal(e.Y), traversal(e.Z)) + 1;
    }
    return traversal(this.rr);
  };
  return TreeContainer;
})(Container);

const __extends$1 =
  (undefined && undefined.t) ||
  (function () {
    var extendStatics = function (t, r) {
      extendStatics =
        Object.setPrototypeOf ||
        ({
          __proto__: []
        } instanceof Array &&
          function (t, r) {
            t.__proto__ = r;
          }) ||
        function (t, r) {
          for (const e in r) if (Object.prototype.hasOwnProperty.call(r, e)) t[e] = r[e];
        };
      return extendStatics(t, r);
    };
    return function (t, r) {
      if (typeof r !== "function" && r !== null) throw new TypeError(`Class extends value ${String(r)} is not a constructor or null`);
      extendStatics(t, r);
      function __() {
        this.constructor = t;
      }
      t.prototype = r === null ? Object.create(r) : ((__.prototype = r.prototype), new __());
    };
  })();

const TreeIterator = (function (t) {
  __extends$1(TreeIterator, t);
  function TreeIterator(r, e, i) {
    const n = t.call(this, i) || this;
    n.o = r;
    n.u = e;
    if (n.iteratorType === 0) {
      n.pre = function () {
        if (this.o === this.u.Y) {
          throwIteratorAccessError();
        }
        this.o = this.o.L();
        return this;
      };
      n.next = function () {
        if (this.o === this.u) {
          throwIteratorAccessError();
        }
        this.o = this.o.m();
        return this;
      };
    } else {
      n.pre = function () {
        if (this.o === this.u.Z) {
          throwIteratorAccessError();
        }
        this.o = this.o.m();
        return this;
      };
      n.next = function () {
        if (this.o === this.u) {
          throwIteratorAccessError();
        }
        this.o = this.o.L();
        return this;
      };
    }
    return n;
  }
  Object.defineProperty(TreeIterator.prototype, "index", {
    get() {
      let t = this.o;
      const r = this.u.tt;
      if (t === this.u) {
        if (r) {
          return r.rt - 1;
        }
        return 0;
      }
      let e = 0;
      if (t.Y) {
        e += t.Y.rt;
      }
      while (t !== r) {
        const i = t.tt;
        if (t === i.Z) {
          e += 1;
          if (i.Y) {
            e += i.Y.rt;
          }
        }
        t = i;
      }
      return e;
    },
    enumerable: false,
    configurable: true
  });
  TreeIterator.prototype.isAccessible = function () {
    return this.o !== this.u;
  };
  return TreeIterator;
})(ContainerIterator);

const __extends =
  (undefined && undefined.t) ||
  (function () {
    var extendStatics = function (r, t) {
      extendStatics =
        Object.setPrototypeOf ||
        ({
          __proto__: []
        } instanceof Array &&
          function (r, t) {
            r.__proto__ = t;
          }) ||
        function (r, t) {
          for (const e in t) if (Object.prototype.hasOwnProperty.call(t, e)) r[e] = t[e];
        };
      return extendStatics(r, t);
    };
    return function (r, t) {
      if (typeof t !== "function" && t !== null) throw new TypeError(`Class extends value ${String(t)} is not a constructor or null`);
      extendStatics(r, t);
      function __() {
        this.constructor = r;
      }
      r.prototype = t === null ? Object.create(t) : ((__.prototype = t.prototype), new __());
    };
  })();

const __generator =
  (undefined && undefined.h) ||
  function (r, t) {
    let e = {
      label: 0,
      sent() {
        if (a[0] & 1) throw a[1];
        return a[1];
      },
      trys: [],
      ops: []
    };
    let n;
    let i;
    let a;
    let o;
    return (
      (o = {
        next: verb(0),
        throw: verb(1),
        return: verb(2)
      }),
      typeof Symbol === "function" &&
        (o[Symbol.iterator] = function () {
          return this;
        }),
      o
    );
    function verb(r) {
      return function (t) {
        return step([r, t]);
      };
    }
    function step(o) {
      if (n) throw new TypeError("Generator is already executing.");
      while (e) {
        try {
          if (((n = 1), i && (a = o[0] & 2 ? i.return : o[0] ? i.throw || ((a = i.return) && a.call(i), 0) : i.next) && !(a = a.call(i, o[1])).done)) return a;
          if (((i = 0), a)) o = [o[0] & 2, a.value];
          switch (o[0]) {
            case 0:
            case 1:
              a = o;
              break;

            case 4:
              e.label++;
              return {
                value: o[1],
                done: false
              };

            case 5:
              e.label++;
              i = o[1];
              o = [0];
              continue;

            case 7:
              o = e.ops.pop();
              e.trys.pop();
              continue;

            default:
              if (!((a = e.trys), (a = a.length > 0 && a[a.length - 1])) && (o[0] === 6 || o[0] === 2)) {
                e = 0;
                continue;
              }
              if (o[0] === 3 && (!a || (o[1] > a[0] && o[1] < a[3]))) {
                e.label = o[1];
                break;
              }
              if (o[0] === 6 && e.label < a[1]) {
                e.label = a[1];
                a = o;
                break;
              }
              if (a && e.label < a[2]) {
                e.label = a[2];
                e.ops.push(o);
                break;
              }
              if (a[2]) e.ops.pop();
              e.trys.pop();
              continue;
          }
          o = t.call(r, e);
        } catch (r) {
          o = [6, r];
          i = 0;
        } finally {
          n = a = 0;
        }
      }
      if (o[0] & 5) throw o[1];
      return {
        value: o[0] ? o[1] : void 0,
        done: true
      };
    }
  };

const OrderedMapIterator = (function (r) {
  __extends(OrderedMapIterator, r);
  function OrderedMapIterator(t, e, n, i) {
    const a = r.call(this, t, e, i) || this;
    a.container = n;
    return a;
  }
  Object.defineProperty(OrderedMapIterator.prototype, "pointer", {
    get() {
      if (this.o === this.u) {
        throwIteratorAccessError();
      }
      const r = this;
      return new Proxy([], {
        get(t, e) {
          if (e === "0") return r.o.p;
          if (e === "1") return r.o.H;
          t[0] = r.o.p;
          t[1] = r.o.H;
          return t[e];
        },
        set(t, e, n) {
          if (e !== "1") {
            throw new TypeError("prop must be 1");
          }
          r.o.H = n;
          return true;
        }
      });
    },
    enumerable: false,
    configurable: true
  });
  OrderedMapIterator.prototype.copy = function () {
    return new OrderedMapIterator(this.o, this.u, this.container, this.iteratorType);
  };
  return OrderedMapIterator;
})(TreeIterator);

const OrderedMap = (function (r) {
  __extends(OrderedMap, r);
  function OrderedMap(t, e, n) {
    if (t === void 0) {
      t = [];
    }
    const i = r.call(this, e, n) || this;
    const a = i;
    t.forEach((r) => {
      a.setElement(r[0], r[1]);
    });
    return i;
  }
  OrderedMap.prototype.begin = function () {
    return new OrderedMapIterator(this.u.Y || this.u, this.u, this);
  };
  OrderedMap.prototype.end = function () {
    return new OrderedMapIterator(this.u, this.u, this);
  };
  OrderedMap.prototype.rBegin = function () {
    return new OrderedMapIterator(this.u.Z || this.u, this.u, this, 1);
  };
  OrderedMap.prototype.rEnd = function () {
    return new OrderedMapIterator(this.u, this.u, this, 1);
  };
  OrderedMap.prototype.front = function () {
    if (this.i === 0) return;
    const r = this.u.Y;
    return [r.p, r.H];
  };
  OrderedMap.prototype.back = function () {
    if (this.i === 0) return;
    const r = this.u.Z;
    return [r.p, r.H];
  };
  OrderedMap.prototype.lowerBound = function (r) {
    const t = this.$(this.rr, r);
    return new OrderedMapIterator(t, this.u, this);
  };
  OrderedMap.prototype.upperBound = function (r) {
    const t = this.tr(this.rr, r);
    return new OrderedMapIterator(t, this.u, this);
  };
  OrderedMap.prototype.reverseLowerBound = function (r) {
    const t = this.er(this.rr, r);
    return new OrderedMapIterator(t, this.u, this);
  };
  OrderedMap.prototype.reverseUpperBound = function (r) {
    const t = this.nr(this.rr, r);
    return new OrderedMapIterator(t, this.u, this);
  };
  OrderedMap.prototype.forEach = function (r) {
    this.ir((t, e, n) => {
      r([t.p, t.H], e, n);
    });
  };
  OrderedMap.prototype.setElement = function (r, t, e) {
    return this.v(r, t, e);
  };
  OrderedMap.prototype.getElementByPos = function (r) {
    if (r < 0 || r > this.i - 1) {
      throw new RangeError();
    }
    const t = this.ir(r);
    return [t.p, t.H];
  };
  OrderedMap.prototype.find = function (r) {
    const t = this.ar(this.rr, r);
    return new OrderedMapIterator(t, this.u, this);
  };
  OrderedMap.prototype.getElementByKey = function (r) {
    const t = this.ar(this.rr, r);
    return t.H;
  };
  OrderedMap.prototype.union = function (r) {
    const t = this;
    r.forEach((r) => {
      t.setElement(r[0], r[1]);
    });
    return this.i;
  };
  OrderedMap.prototype[Symbol.iterator] = function () {
    let r;
    let t;
    let e;
    let n;
    return __generator(this, function (i) {
      switch (i.label) {
        case 0:
          r = this.i;
          t = this.ir();
          e = 0;
          i.label = 1;

        case 1:
          if (!(e < r)) return [3, 4];
          n = t[e];
          return [4, [n.p, n.H]];

        case 2:
          i.sent();
          i.label = 3;

        case 3:
          ++e;
          return [3, 1];

        case 4:
          return [2];
      }
    });
  };
  return OrderedMap;
})(TreeContainer);

const MeshColorShader = {
  uniforms: {},
  vertexShader: /* glsl */ `
        attribute vec3 barycentric;
        /*attribute ivec3 vertex_id;
        attribute ivec3 edge_id;
        attribute int face_id;*/

        out vec3 FBarycentric;
        /*out ivec3 FVertex_ids;
        out ivec3 FEfge_ids;
        out int FFace_ids;*/
		void main()
		{
            FBarycentric = barycentric;
           /* FVertex_ids = vertex_id;
            FEfge_ids = edge_id;
            FFace_ids = face_id;*/

			gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0);
	    }`,
  fragmentShader: /* glsl */ `

    in vec3 FBarycentric;
    /*in ivec3 FVertex_ids;
    in ivec3 FEfge_ids;
    in int FFace_ids;*/

	void main() {
        gl_FragColor = vec4(FBarycentric,1.0);
	}`
};

class MeshColorsMaterial extends ShaderMaterial {
  constructor() {
    super({
      uniforms: UniformsUtils.clone(MeshColorShader.uniforms),
      vertexShader: MeshColorShader.vertexShader,
      fragmentShader: MeshColorShader.fragmentShader
    });
  }
}

class MColor extends Vector4 {
  setRGBA(r, g, b, a) {
    this.set(r, g, b, a);
  }

  setRGB(r, g, b) {
    this.set(r, g, b, this.a);
  }

  lerpColors(color_1, color_2, mix_factor) {
    this.lerpVectors(color_1, color_2, mix_factor);
  }

  alphaBlendColor(color_2) {
    if (color_2.a === 0.0) return;
    const alpha = color_2.a * (1.0 - this.a) + this.a;
    this.r = (this.r * this.a + color_2.r * color_2.a * (1.0 - this.a)) / alpha;
    this.g = (this.g * this.a + color_2.g * color_2.a * (1.0 - this.a)) / alpha;
    this.b = (this.b * this.a + color_2.b * color_2.a * (1.0 - this.a)) / alpha;
    this.a = alpha;
  }

  get r() {
    return this.x;
  }

  set r(val) {
    this.x = val;
  }

  get g() {
    return this.y;
  }

  set g(val) {
    this.y = val;
  }

  get b() {
    return this.z;
  }

  set b(val) {
    this.z = val;
  }

  get a() {
    return this.w;
  }

  set a(val) {
    this.w = val;
  }
}

// import { glbToGltf, gltfToGlb, processGltf } from "@dioxygen-software/gltf-pipeline-lite";
function resizeArray(arr, new_size, def_val_func) {
  let delta_size = arr.length - new_size;
  if (delta_size >= 0) {
    // arr.length = new_size;
    for (let i = 0; i < delta_size; ++i) arr.pop();
  } else {
    while (delta_size++ < 0) {
      arr.push(def_val_func());
    }
    // arr.length = new_size;
  }
}
function vertexPairing(a, b) {
  // Szudzik pairing function http://szudzik.com/ElegantPairing.pdf /!\ not symetric, that's why we start with the smaller
  return a >= b ? a * a + a + b : b * b + b + a;
  // Alternative Cantor pairing algo /!\ produce very large numbers
  // return a >= b ? (a + b) * (a + b + 1) / 2 + a : (a + b) * (a + b + 1) / 2 + b;
}
// https://bottosson.github.io/posts/oklab
const kCONEtoLMS = new Matrix3();
kCONEtoLMS.set(0.412165612, 0.211859107, 0.0883097947, 0.536275208, 0.6807189584, 0.2818474174, 0.0514575653, 0.107406579, 0.6302613616);
function RGBToOklab(rgb, oklab) {
  oklab.copy(rgb);
  oklab.applyMatrix3(kCONEtoLMS);
  // oklab.x = Math.pow(oklab.x,1.0/3.0);
  // oklab.y = Math.pow(oklab.y,1.0/3.0);
  // oklab.z = Math.pow(oklab.z,1.0/3.0);
  oklab.x = Math.cbrt(oklab.x);
  oklab.y = Math.cbrt(oklab.y);
  oklab.z = Math.cbrt(oklab.z);
}
// https://bottosson.github.io/posts/oklab
const kLMStoCONE = new Matrix3();
kLMStoCONE.set(4.0767245293, -1.2681437731, -0.0041119885, -3.3072168827, 2.6093323231, -0.7034763098, 0.2307590544, -0.341134429, 1.7068625689);
function OklabToRGB(oklab, rgb) {
  rgb.x = oklab.x * oklab.x * oklab.x;
  rgb.y = oklab.y * oklab.y * oklab.y;
  rgb.z = oklab.z * oklab.z * oklab.z;
  rgb.applyMatrix3(kLMStoCONE);
}
// Optimization variables to avoid excessive object allocation
new Vector3();
new Vector3();
function Uint8ArrayArrayToUint8Array(uArray) {
  // Get the total length of all arrays.
  let exportArrayLength = 0;
  uArray.forEach((item) => {
    exportArrayLength += item.length;
  });
  // Create a new array with total length and merge all source arrays.
  const exportArray = new Uint8Array(exportArrayLength);
  let offset = 0;
  uArray.forEach((item) => {
    exportArray.set(item, offset);
    offset += item.length;
  });
  return exportArray;
}
/* export async function dracoProcessGlb(inGlb: Uint8Array, compressionLevel = 0)
{
    const exportOptions = {
        dracoOptions: {
            compressionLevel: Math.min(10, Math.max(0, Math.round(compressionLevel)))
        }
    };
    const outputGLB = true;
    return glbToGltf(inGlb)
    .then((gltf) => {
        return processGltf(gltf.gltf, exportOptions)
        .then((results) => {
            if (!outputGLB) {
                return JSON.stringify(results.gltf);
            } else { // glb
                return gltfToGlb(results.gltf).then(function (results) {
                    return results.glb;
                }).catch(function (e) {
                    throw e;
                });
            }
        })});
} */

for (let i = 0; i < 256; i++) {
  (i < 16 ? "0" : "") + i.toString(16);
}

// https://en.wikipedia.org/wiki/Linear_interpolation
function lerp(x, y, t) {
  return (1 - t) * x + t * y;
}

const MeshColorTextureType = {
  Byte: 0,
  Short: 1,
  Float: 2
};
const MeshColorTextureFormat = {
  Red: 0,
  RGBA: 1
};
const ignoredAttributes = new Set(["skinWeight", "skinIndex", "uv"]);
class MeshColorInternal {
  vertexColors = [];

  edgeColors = [];

  faceColors = [];

  // Texture used to render mesh colors directly with a MeshColorsMaterial
  // TODO not used for now
  colorData = null;

  indexedGeometry = null;

  // TODO experimental Color sampling function
  colorSampler = (colorIndex, outColor) => {
    // outColor.copy(this.posSampleArray[colorIndex]);
    if (this.posSampleArray && colorIndex >= 0 && colorIndex < this.posSampleArray.length) {
      outColor.copy(this.posSampleArray[colorIndex]);
    }
  };

  initialized = false;

  textureMarginLength = 0;

  textureSize = 0;

  textureFaceMetaData = [];

  textureQuadFaceMetaData = new Map();

  edgeResolutionChanged = new Signal();

  faceResolutionChanged = new Signal();

  samplesResolutionChanged = new Signal();

  /**
   * Note/TODO: Right now just a point cloud as this geometry is not triangulated.
   * geometry that perfectly matches the mesh colors samples
   * It can be used to check intersections for coloring process
   * and update corresponding color samples
   * The geometry is stored in the following manner:
   * first original vertices, then edge color samples and finally face color samples
   * That way we can easilly extract information from original geometry (attributes)
   * and use it for procedural computation such as interpolating
   * from vertex sample color to edge and face sample color
   * */
  colorSampleGeometry = null;

  // Accessors
  // map index in sample geometry to related color samples
  posSampleArray = null;

  // map edge id to start id in position buffer of color_sample_geometry
  edgePosGeometryMap = null;

  // map face id to start id in position buffer of color_sample_geometry
  facePosGeometryMap = null;

  // map id in position buffer of color_sample_geometry to related EdgeColor
  edgePosColorMap = null;

  // map id in position buffer of color_sample_geometry to retated FaceColor
  facePosColorMap = null;

  // Contains related faces for each vertex
  vertexRelFaces = null;

  // Contains related faces for each edge (should be at most 2 faces)
  edgeRelFaces = null;

  // Variable used for optimisation purposes
  tmpColor1 = new MColor();

  tmpColor2 = new MColor();

  tmpColor3 = new MColor();

  // /!\ FOR SERIALIZATION ONLY ---------------------------
  fromWorkerData(indexed_geometry, color_sample_geometry, vertex_colors, edge_colors, face_colors, texture_margin_length, texture_size) {
    this.vertexColors = vertex_colors;
    this.edgeColors = edge_colors;
    this.faceColors = face_colors;
    this.colorSampleGeometry = color_sample_geometry;
    this.indexedGeometry = indexed_geometry;
    this.textureMarginLength = texture_margin_length;
    this.textureSize = texture_size;
    this.computeVertexEdgeRelFacesFromData();
    this.computeEdgeRelFacesFromData();
    this.computeSamplePosColorMapsFromData();
  }

  // Function to call right after calling toWorkerData to dispose data that will not be used anymore
  // This function is quite critical to call because meshColors may manipulates a lot of data
  disposeWorkered() {
    this.vertexColors = [];
    this.edgeColors = [];
    this.faceColors = [];
    this.posSampleArray = [];
    this.vertexRelFaces = [];
    this.edgeRelFaces = [];
    if (this.edgePosColorMap) this.edgePosColorMap.clear();
    if (this.facePosColorMap) this.facePosColorMap.clear();
  }

  _generateSampleGeometryAndComputedProperties() {
    // this.colorSampleGeometry = new OctreeBufferGeometry();
    for (const attribute of Object.keys(this.colorSampleGeometry.attributes)) {
      this.colorSampleGeometry?.deleteAttribute(attribute);
    }
    this.computeVertexEdgeRelFacesFromData();
    this.computeEdgeRelFacesFromData();
    this.computeSamplePosColorMapsFromData();
    this._updateColorSampleGeometry(this.indexedGeometry);
  }

  generateColorSamples(edgeColorStep, vertexMaxBudget) {
    if (!this.indexedGeometry) {
      throw "[Mesh Color] Generating color samples on non initialized mesh colors";
    }
    // Test to force resizing
    if (this.initialized) {
      const createColor = function () {
        return new MColor();
      };
      for (const edge of this.edgeColors) {
        edge.R = 1;
        // edge.colors.length = 0;
        resizeArray(edge.colors, 0, createColor);
      }
      for (const face of this.faceColors) {
        face.R = 1;
        // face.colors.length = 0;
        resizeArray(face.colors, 0, createColor);
      }
    }
    // subdivide at the end of the process to avoid color data override
    // console.time("Mesh Color Subdivisions");
    const resSortedFaces = this.faceColors.map((x) => x);
    const faceResArray = new Array(resSortedFaces.length);
    const point_1 = new Vector3();
    const point_2 = new Vector3();
    const point_3 = new Vector3();
    const triangle = new Triangle();
    const indexAttribute = this.indexedGeometry.index;
    const indexPositionAttribute = this.indexedGeometry.getAttribute("position");
    for (let i = 0; i < indexAttribute.count; i += 3) {
      point_1.fromBufferAttribute(indexPositionAttribute, indexAttribute.getX(i));
      point_2.fromBufferAttribute(indexPositionAttribute, indexAttribute.getX(i + 1));
      point_3.fromBufferAttribute(indexPositionAttribute, indexAttribute.getX(i + 2));
      triangle.set(point_1, point_2, point_3);
      const edge_1_dist = point_1.distanceTo(point_2);
      const edge_2_dist = point_2.distanceTo(point_3);
      const edge_3_dist = point_3.distanceTo(point_1);
      const max_edge_dist = Math.max(edge_1_dist, edge_2_dist, edge_3_dist);
      if (max_edge_dist > edgeColorStep) {
        faceResArray[i / 3] = Math.ceil(max_edge_dist / edgeColorStep);
      } else {
        faceResArray[i / 3] = 1;
      }
    }
    resSortedFaces.sort((a, b) => {
      const aR = faceResArray[a.id];
      const bR = faceResArray[b.id];
      if (aR == bR) return a.id - b.id;
      return bR - aR;
    });
    // We subdivide faces with respect to their newly computed resolution
    // We early stop if we used all the sample color budget
    let vertexBudget = vertexMaxBudget ?? Number.MAX_SAFE_INTEGER;
    vertexBudget -= this.vertexColors.length;
    // TODO change how budget policy handled.
    // If budget -> uniformly subdvide
    for (let i = 0; i < resSortedFaces.length; i++) {
      const new_res = faceResArray[resSortedFaces[i].id];
      if (new_res > 1) {
        this.subdivideFace(resSortedFaces[i].id, new_res - 1, false);
      }
      vertexBudget -= this.faceColors[resSortedFaces[i].id].colors.length;
      vertexBudget -= this.edgeColors[resSortedFaces[i].edge_ids[0]].colors.length;
      vertexBudget -= this.edgeColors[resSortedFaces[i].edge_ids[1]].colors.length;
      vertexBudget -= this.edgeColors[resSortedFaces[i].edge_ids[2]].colors.length;
      if (vertexBudget <= 0) {
        vertexBudget = 0;
        break;
      }
    }
    // TODO create vertex edge face attribute
    // Set vertex edge face attributes
    /* for (let i = 0; i < positionAttribute.count; i+=3) {
            point_1.fromBufferAttribute(positionAttribute, i);
            point_2.fromBufferAttribute(positionAttribute, i+1);
            point_3.fromBufferAttribute(positionAttribute, i+2);

            let pt_1 = vertex_bary_map.get(point_1.x.toFixed(4) + ',' + point_1.y.toFixed(4)+ ',' + point_1.z.toFixed(4));
            let pt_2 = vertex_bary_map.get(point_2.x.toFixed(4) + ',' + point_2.y.toFixed(4)+ ',' + point_2.z.toFixed(4));
            let pt_3 = vertex_bary_map.get(point_3.x.toFixed(4) + ',' + point_3.y.toFixed(4)+ ',' + point_3.z.toFixed(4));
        } */
    this.createInitialGeometrySubdivision();
    return vertexBudget;
  }

  // Compute from worker functions -------------------------
  computeVertexEdgeRelFacesFromData() {
    if (!this.vertexColors || this.vertexColors.length == 0 || !this.indexedGeometry) return;
    const indexAttribute = this.indexedGeometry.index;
    const indexPositionAttribute = this.indexedGeometry.getAttribute("position");
    this.vertexRelFaces = new Array(indexPositionAttribute.count);
    for (let i = 0; i < indexPositionAttribute.count; ++i) this.vertexRelFaces[i] = [];
    for (let i = 0; i < indexAttribute.count; i += 3) {
      const v_id_1 = indexAttribute.getX(i);
      const v_id_2 = indexAttribute.getX(i + 1);
      const v_id_3 = indexAttribute.getX(i + 2);
      if (v_id_1 === undefined || v_id_2 === undefined || v_id_3 === undefined) {
        console.error("MeshColor initailize: A triangle has been removed through merging geometry");
        return;
      }
      if (!this.vertexRelFaces[v_id_1]) this.vertexRelFaces[v_id_1] = [this.faceColors[i / 3]];
      else this.vertexRelFaces[v_id_1].push(this.faceColors[i / 3]);
      if (!this.vertexRelFaces[v_id_2]) this.vertexRelFaces[v_id_2] = [this.faceColors[i / 3]];
      else this.vertexRelFaces[v_id_2].push(this.faceColors[i / 3]);
      if (!this.vertexRelFaces[v_id_3]) this.vertexRelFaces[v_id_3] = [this.faceColors[i / 3]];
      else this.vertexRelFaces[v_id_3].push(this.faceColors[i / 3]);
    }
  }

  computeEdgeRelFacesFromData() {
    if (!(this.edgeColors && this.edgeColors.length > 0 && this.faceColors && this.faceColors.length > 0 && this.indexedGeometry)) return;
    this.edgeRelFaces = Array(this.edgeColors.length);
    const indexAttribute = this.indexedGeometry.index;
    for (let i = 0; i < indexAttribute.count; i += 3) {
      const v_id_1 = indexAttribute.getX(i);
      const v_id_2 = indexAttribute.getX(i + 1);
      const v_id_3 = indexAttribute.getX(i + 2);
      if (v_id_1 === undefined || v_id_2 === undefined || v_id_3 === undefined) {
        console.error("MeshColor initailize: A triangle has been removed through merging geometry");
        return;
      }
      const face = this.faceColors[i / 3];
      // Store related faces
      for (const e_id of face.edge_ids) {
        if (!this.edgeRelFaces[e_id]) this.edgeRelFaces[e_id] = [face];
        else this.edgeRelFaces[e_id].push(face);
      }
      //-----------------------------------------------------
    }
  }

  computeSamplePosColorMapsFromData() {
    if (!this.indexedGeometry || !this.colorSampleGeometry) return;
    this.edgePosGeometryMap = new Array(this.edgeColors.length);
    this.facePosGeometryMap = new Array(this.faceColors.length);
    this.edgePosColorMap = new Map();
    this.facePosColorMap = new Map();
    this.posSampleArray = new Array();
    let currId = this.vertexColors.length;
    for (const vertex of this.vertexColors) {
      this.posSampleArray.push(vertex.color);
    }
    for (const edge of this.edgeColors) {
      this.edgePosGeometryMap[edge.id] = currId;
      for (let i = 1; i < edge.R; ++i) {
        this.posSampleArray.push(edge.colors[i - 1]);
        this.edgePosColorMap.set(currId + (i - 1), edge);
      }
      currId += edge.colors.length;
    }
    for (const face of this.faceColors) {
      const faceR = face.R;
      this.facePosGeometryMap[face.id] = currId;
      for (let i = 0; i < Math.max(0, ((faceR - 2) * (faceR - 1)) / 2); ++i) {
        this.posSampleArray.push(face.colors[i]);
        this.facePosColorMap.set(currId + i, face);
      }
      currId += face.colors.length;
    }
  }

  //---------------------------------------------
  //---------------------------------------------
  getVertexRelativeFaces(v_id) {
    if (!this.vertexRelFaces) return null;
    if (v_id < this.vertexRelFaces.length) return this.vertexRelFaces[v_id];
    return null;
  }

  getEdgeRelativeFaces(edge_id) {
    if (!this.edgeRelFaces) return null;
    return this.edgeRelFaces[edge_id];
  }

  getColorSampleNb() {
    let edge_color_sample_nb = 0;
    for (const edge_color of this.edgeColors) {
      edge_color_sample_nb += edge_color.colors.length;
    }
    let face_color_sample_nb = 0;
    for (const face_color of this.faceColors) {
      face_color_sample_nb += face_color.colors.length;
    }
    return this.vertexColors.length + edge_color_sample_nb + face_color_sample_nb;
  }

  // edge color sample index position in geometry position buffer
  getEdgeColorSamplePosId(edgeId, colorId) {
    if (!this.edgePosGeometryMap) {
      throw "Trying to sample non initialized or empty meshColor";
    }
    return this.edgePosGeometryMap[edgeId] + colorId;
  }

  // face color sample index position in geometry position buffer
  getFaceColorSamplePosId(faceId, colorId) {
    if (!this.facePosGeometryMap) {
      throw "Trying to sample non initialized or empty meshColor";
    }
    return this.facePosGeometryMap[faceId] + colorId;
  }

  // TODO Experimental
  setColorSampler(sampler) {
    this.colorSampler = sampler;
  }

  computeSamplingDensity() {
    // True density (expensive)
    /* for(let v_col of this.vertexColors)
        {
            v_col.color.setRGB(0,0,0);
        }

        for(let edge of this.edgeColors)
        {
            for(let e_col of edge.colors)
                e_col.setRGB(0,0,0);
        } */
    if (!this.colorSampleGeometry || !this.facePosGeometryMap) {
      console.log("computeSamplingDensity: mesh color is not initialized, cannot compute density");
      return;
    }
    const positionAttribute = this.colorSampleGeometry.getAttribute("position");
    // Clear or create density attribute
    this.colorSampleGeometry.setAttribute("colorSamplingDensity", new BufferAttribute(new Float32Array(positionAttribute.count), 1));
    const densityAttribute = this.colorSampleGeometry.getAttribute("colorSamplingDensity");
    /* let start_pt = new Vector3();
        let end_pt = new Vector3();
        let min_dist = Number.MAX_VALUE; */
    const meanSampleDist = 0.0;
    // Density esitmation per face
    const point_1 = new Vector3();
    const point_2 = new Vector3();
    const point_3 = new Vector3();
    const triangle = new Triangle();
    for (const face of this.faceColors) {
      const face_pos_index = this.facePosGeometryMap[face.id];
      const edge_1 = this.edgeColors[face.edge_ids[0]];
      const edge_2 = this.edgeColors[face.edge_ids[1]];
      const edge_3 = this.edgeColors[face.edge_ids[2]];
      const v_1 = this.vertexColors[face.edge_orientation[0] ? edge_1.vertex_ids[0] : edge_1.vertex_ids[1]];
      const v_2 = this.vertexColors[face.edge_orientation[1] ? edge_2.vertex_ids[0] : edge_2.vertex_ids[1]];
      const v_3 = this.vertexColors[face.edge_orientation[2] ? edge_3.vertex_ids[0] : edge_3.vertex_ids[1]];
      point_1.fromBufferAttribute(positionAttribute, v_1.id);
      point_2.fromBufferAttribute(positionAttribute, v_2.id);
      point_3.fromBufferAttribute(positionAttribute, v_3.id);
      triangle.set(point_1, point_2, point_3);
      const triangle_area = triangle.getArea();
      // This is the ideal point number is the triangle was "uniform"(either isosceles rectangle or equilateral)
      const target_points = (triangle_area * 2.0) / (meanSampleDist * meanSampleDist);
      // Number of point of the face, 3 being the 3 vertices
      const face_sample_points = 3 + edge_1.colors.length + edge_2.colors.length + edge_3.colors.length + face.colors.length;
      for (let i = 0; i < face.colors.length; ++i) {
        densityAttribute.setX(face_pos_index + i, face_sample_points / target_points);
      }
    }
    // True density (expensive)
    // Get minimum distance to closest sample for each face color cample
    /*
        //Function updating min distance between face sample (start_pt) and its neighbours (end_pt)
        let compareDistance = (pos_index: number) => {
            end_pt.fromBufferAttribute(positionAttribute, pos_index);
            let dist = start_pt.distanceTo(end_pt);
            if(dist < min_dist)
            {
                min_dist = dist;
            }
        }
        for(let face of this.faceColors)
        {
            let face_pos_index = this.facePosGeometryMap.get(face.id)!;
            let edge_1 = this.edgeColors[face.edge_ids[0]];
            let edge_2 = this.edgeColors[face.edge_ids[1]];
            let edge_3 = this.edgeColors[face.edge_ids[2]];
            let edge_1_pos_index = this.edgePosGeometryMap.get(edge_1.id)!;
            let edge_2_pos_index = this.edgePosGeometryMap.get(edge_2.id)!;
            let edge_3_pos_index = this.edgePosGeometryMap.get(edge_3.id)!;
            let v_1 = this.vertexColors[face.edge_orientation[0] ? edge_1.vertex_ids[0] :  edge_1.vertex_ids[1]];
            let v_2 = this.vertexColors[face.edge_orientation[1] ? edge_2.vertex_ids[0] :  edge_2.vertex_ids[1]];
            let v_3 = this.vertexColors[face.edge_orientation[2] ? edge_3.vertex_ids[0] :  edge_3.vertex_ids[1]];

            // Get sample closest neighbour
            for(let i = 0 ; i < face.colors.length; ++i)
            {
                min_dist = Number.MAX_VALUE;
                start_pt.fromBufferAttribute(positionAttribute,face_pos_index + i);
                //Compare with other face samples
                for(let j = i ; j < face.colors.length; ++j)
                {
                    //Do not compare sample with itself
                    if(i == j)
                    {
                        continue;
                    }
                    compareDistance(face_pos_index + j);
                }

                //Compare with edge samples
                for(let j = 0 ; j < edge_1.colors.length; ++j)
                {
                    compareDistance(edge_1_pos_index + j);
                }

                for(let j = 0 ; j < edge_2.colors.length; ++j)
                {
                    compareDistance(edge_2_pos_index + j);
                }

                for(let j = 0 ; j < edge_3.colors.length; ++j)
                {
                    compareDistance(edge_3_pos_index + j);
                }

                //Compare with vertices
                compareDistance(v_1.id);
                compareDistance(v_2.id);
                compareDistance(v_3.id);

                let ratio = this.edgeColorStep/min_dist;
                densityAttribute!.setX(face_pos_index + i, ratio);

            }
        } */
  }

  /**
   * - internal triangles are computed independently from edges
   * - triangles between edges and face interior points are computed following this algorithm:
   *      - ordered points on edges around the triangle are stored in exteriorPoints
   *      - ordered points on boundary of the interior face are stored in interiorPoints
   *      - Triangulate between the two curves : interiorPoints and exteriorPoints. To do this we take into account the ratio faceR/edgeR
   */
  _updateColorTriangleGeometry() {
    if (!this.colorSampleGeometry || !this.edgePosGeometryMap) return;
    this.colorSampleGeometry.setIndex(null);
    const indices = new Array();
    let exteriorPoints = new Array();
    let interiorPoints = [];
    let interiorPointsInverseOrder = [];
    let posShift = this.vertexColors.length - 1;
    for (const edge of this.edgeColors) {
      posShift += edge.R - 1;
    }
    // Face interiors
    for (const face of this.faceColors) {
      let x_count = 1;
      const faceR = face.R;
      let shiftRNb = faceR - 2;
      // edges of the face
      const edge_1 = this.edgeColors[face.edge_ids[0]];
      const edge_2 = this.edgeColors[face.edge_ids[1]];
      const edge_3 = this.edgeColors[face.edge_ids[2]];
      // vertices of the face
      const v_0_id = face.edge_orientation[0] ? edge_1.vertex_ids[0] : edge_1.vertex_ids[1];
      const v_1_id = face.edge_orientation[1] ? edge_2.vertex_ids[0] : edge_2.vertex_ids[1];
      const v_2_id = face.edge_orientation[2] ? edge_3.vertex_ids[0] : edge_3.vertex_ids[1];
      // Init first 3 triangle for the angle of the face
      if (faceR >= 2) {
        indices.push(v_0_id);
        indices.push(face.edge_orientation[0] ? this.edgePosGeometryMap[edge_1.id] : this.edgePosGeometryMap[edge_1.id] + edge_1.R - 2);
        indices.push(face.edge_orientation[2] ? this.edgePosGeometryMap[edge_3.id] + edge_3.R - 2 : this.edgePosGeometryMap[edge_3.id]);
        indices.push(v_1_id);
        indices.push(face.edge_orientation[1] ? this.edgePosGeometryMap[edge_2.id] : this.edgePosGeometryMap[edge_2.id] + edge_2.R - 2);
        indices.push(face.edge_orientation[0] ? this.edgePosGeometryMap[edge_1.id] + edge_1.R - 2 : this.edgePosGeometryMap[edge_1.id]);
        indices.push(v_2_id);
        indices.push(face.edge_orientation[2] ? this.edgePosGeometryMap[edge_3.id] : this.edgePosGeometryMap[edge_3.id] + edge_3.R - 2);
        indices.push(face.edge_orientation[1] ? this.edgePosGeometryMap[edge_2.id] + edge_2.R - 2 : this.edgePosGeometryMap[edge_2.id]);
      }
      // special case for faceR == 2
      if (faceR === 2) {
        for (let i = 1; i < edge_1.R - 1; i++) {
          if (face.edge_orientation[0]) {
            indices.push(this.edgePosGeometryMap[edge_1.id] + i - 1);
            indices.push(this.edgePosGeometryMap[edge_1.id] + i);
            if (face.edge_orientation[1]) {
              indices.push(this.edgePosGeometryMap[edge_2.id]);
            } else {
              indices.push(this.edgePosGeometryMap[edge_2.id] + edge_2.R - 2);
            }
          } else {
            indices.push(this.edgePosGeometryMap[edge_1.id] + i);
            indices.push(this.edgePosGeometryMap[edge_1.id] + i - 1);
            if (face.edge_orientation[2]) {
              indices.push(this.edgePosGeometryMap[edge_3.id] + edge_3.R - 2);
            } else {
              indices.push(this.edgePosGeometryMap[edge_3.id]);
            }
          }
        }
        for (let i = 1; i < edge_2.R - 1; i++) {
          indices.push(this.edgePosGeometryMap[edge_2.id] + i);
          if (face.edge_orientation[1]) {
            indices.push(this.edgePosGeometryMap[edge_2.id] + i - 1);
            indices.push(this.edgePosGeometryMap[edge_1.id]);
          } else {
            indices.push(this.edgePosGeometryMap[edge_1.id]);
            indices.push(this.edgePosGeometryMap[edge_2.id] + i - 1);
          }
        }
        for (let i = 1; i < edge_3.R - 1; i++) {
          indices.push(this.edgePosGeometryMap[edge_3.id] + i);
          if (face.edge_orientation[2]) {
            indices.push(this.edgePosGeometryMap[edge_1.id]);
            indices.push(this.edgePosGeometryMap[edge_3.id] + i - 1);
          } else {
            indices.push(this.edgePosGeometryMap[edge_3.id] + i - 1);
            indices.push(this.edgePosGeometryMap[edge_1.id]);
          }
        }
        // middle triangle
        indices.push(this.edgePosGeometryMap[edge_1.id]);
        if (face.edge_orientation[1]) {
          indices.push(this.edgePosGeometryMap[edge_2.id] + edge_2.R - 2);
        } else {
          indices.push(this.edgePosGeometryMap[edge_2.id]);
        }
        if (face.edge_orientation[2]) {
          indices.push(this.edgePosGeometryMap[edge_3.id]);
        } else {
          indices.push(this.edgePosGeometryMap[edge_3.id] + edge_3.R - 2);
        }
      }
      // special case for faceR == 1
      if (faceR === 1) {
        indices.push(v_0_id);
        indices.push(v_1_id);
        indices.push(v_2_id);
      }
      // Exterior points
      exteriorPoints = [];
      for (let i = 2; i >= 0; --i) {
        const edge = this.edgeColors[face.edge_ids[i]];
        if (!face.edge_orientation[i]) {
          for (let x = 0; x < edge.R - 1; x++) {
            exteriorPoints.push(this.edgePosGeometryMap[edge.id] + x);
          }
        } else {
          for (let x = 0; x < edge.R - 1; x++) {
            exteriorPoints.push(this.edgePosGeometryMap[edge.id] + edge.R - 2 - x);
          }
        }
      }
      // Interior points initialization
      interiorPoints = [];
      interiorPointsInverseOrder = [];
      // searching for boundaries of interior points
      for (let i = 0; i < Math.max(0, ((faceR - 2) * (faceR - 1)) / 2); ++i) {
        posShift++;
        // interior points
        if (shiftRNb === faceR - 2) {
          interiorPoints.push(posShift);
        } else {
          indices.push(posShift);
          indices.push(posShift - shiftRNb);
          indices.push(posShift - shiftRNb - 1);
          if (x_count === shiftRNb) {
            interiorPoints.push(posShift);
          } else {
            indices.push(posShift);
            indices.push(posShift + 1);
            indices.push(posShift - shiftRNb);
            if (x_count === 1) {
              interiorPointsInverseOrder.push(posShift);
            }
          }
        }
        if (x_count >= shiftRNb) {
          shiftRNb--;
          x_count = 1;
        } else {
          x_count++;
        }
      }
      // add reverse order of interior points
      for (let i = 0; i < interiorPointsInverseOrder.length; i++) {
        interiorPoints.push(interiorPointsInverseOrder[interiorPointsInverseOrder.length - 1 - i]);
      }
      // triangulation between two lines of interior points and exterior points
      let offsetExterior = 0;
      if (interiorPoints.length >= 2) {
        let indexInterior = 0;
        for (let i = 0; i <= 2; i++) {
          const edge = this.edgeColors[face.edge_ids[2 - i]];
          for (let j = 0; j <= edge.R - 3; j++) {
            const indexInteriorPoint = (Math.floor((j * (faceR - 3)) / (edge.R - 3)) + i * (faceR - 3)) % interiorPoints.length;
            indices.push(interiorPoints[indexInteriorPoint]);
            indices.push(exteriorPoints[(j + 1 + offsetExterior) % exteriorPoints.length]);
            indices.push(exteriorPoints[j + offsetExterior]);
            if (j === edge.R - 3) {
              indices.push(interiorPoints[indexInteriorPoint]);
              indices.push(exteriorPoints[(j + 2 + offsetExterior) % exteriorPoints.length]);
              indices.push(exteriorPoints[j + 1 + offsetExterior]);
            }
            if (indexInterior !== indexInteriorPoint) {
              if (indexInteriorPoint === 0) {
                indices.push(interiorPoints[interiorPoints.length - 1]);
              } else {
                indices.push(interiorPoints[indexInteriorPoint - 1]);
              }
              indices.push(interiorPoints[indexInteriorPoint]);
              indices.push(exteriorPoints[j + offsetExterior]);
              indexInterior = indexInteriorPoint;
            }
          }
          offsetExterior += edge.R - 1;
        }
      } else if (interiorPoints.length === 1) {
        for (let i = 0; i < exteriorPoints.length; i++) {
          indices.push(interiorPoints[0]);
          indices.push(exteriorPoints[i]);
          indices.push(exteriorPoints[(i + 1) % exteriorPoints.length]);
        }
      }
    }
    this.colorSampleGeometry.setIndex(indices);
    this.colorSampleGeometry.index.needsUpdate = true;
  }

  _updateColorSampleGeometry(indexedGeometry) {
    if (!this.colorSampleGeometry || !this.edgePosGeometryMap) return;
    const attributes = [];
    for (const key of Object.keys(indexedGeometry.attributes)) {
      if (!ignoredAttributes.has(key)) {
        attributes.push(key);
      }
    }
    const positionAttribute = indexedGeometry.getAttribute("position");
    if (positionAttribute.count != this.vertexColors.length) {
      // TODO warning
      return;
    }
    const attributeArrays = new Map();
    for (let i = 0; i < attributes.length; i++) {
      const attribute = indexedGeometry.getAttribute(attributes[i]);
      const attributeArray = Array.from(attribute.array);
      attributeArrays.set(attributes[i], attributeArray);
    }
    const interpolatedAttribute = new Map();
    const attr1 = new Map();
    const attr2 = new Map();
    const attr3 = new Map();
    for (let i = 0; i < attributes.length; i++) {
      const attribute = indexedGeometry.getAttribute(attributes[i]);
      switch (attribute.itemSize) {
        case 1: {
          interpolatedAttribute.set(attributes[i], 0);
          attr1.set(attributes[i], 0);
          attr2.set(attributes[i], 0);
          attr3.set(attributes[i], 0);
          break;
        }
        case 2: {
          interpolatedAttribute.set(attributes[i], new Vector2());
          attr1.set(attributes[i], new Vector2());
          attr2.set(attributes[i], new Vector2());
          attr3.set(attributes[i], new Vector2());
          break;
        }
        case 3: {
          interpolatedAttribute.set(attributes[i], new Vector3());
          attr1.set(attributes[i], new Vector3());
          attr2.set(attributes[i], new Vector3());
          attr3.set(attributes[i], new Vector3());
          break;
        }
        case 4: {
          interpolatedAttribute.set(attributes[i], new Vector4());
          attr1.set(attributes[i], new Vector4());
          attr2.set(attributes[i], new Vector4());
          attr3.set(attributes[i], new Vector4());
          break;
        }
        default:
          throw "[MeshColor]: geometry attributes cannot be of size greater than 4";
      }
    }
    for (const edge of this.edgeColors) {
      const edgeR = edge.R;
      for (let i = 1; i < edge.R; ++i) {
        for (let j = 0; j < attributes.length; j++) {
          let interpolated = interpolatedAttribute.get(attributes[j]);
          const attribute = indexedGeometry.getAttribute(attributes[j]);
          if (!interpolated) continue;
          const attributeArray = attributeArrays.get(attributes[j]);
          if (typeof interpolated === "number") {
            interpolated = lerp(attribute.getX(edge.vertex_ids[0]), attribute.getX(edge.vertex_ids[1]), i / edgeR);
            attributeArray.push(interpolated);
          } else {
            const atr1 = attr1.get(attributes[j]);
            const atr2 = attr2.get(attributes[j]);
            interpolated
              .copy(atr1.fromBufferAttribute(attribute, edge.vertex_ids[0]))
              .multiplyScalar(1.0 - i / edgeR)
              .add(atr2.fromBufferAttribute(attribute, edge.vertex_ids[1]).multiplyScalar(i / edgeR));
            if (attributes[j] == "normal") interpolated.normalize();
            for (let k = 0; k < attribute.itemSize; k++) {
              attributeArray.push(interpolated.getComponent(k));
            }
          }
        }
      }
    }
    // Face interiors
    for (const face of this.faceColors) {
      let x_count = 1;
      let y_count = 1;
      const faceR = face.R;
      let shiftRNb = faceR - 2;
      // edges of the face
      const edge_1 = this.edgeColors[face.edge_ids[0]];
      const edge_2 = this.edgeColors[face.edge_ids[1]];
      const edge_3 = this.edgeColors[face.edge_ids[2]];
      // vertices of the face
      const v_0_id = face.edge_orientation[0] ? edge_1.vertex_ids[0] : edge_1.vertex_ids[1];
      const v_1_id = face.edge_orientation[1] ? edge_2.vertex_ids[0] : edge_2.vertex_ids[1];
      const v_2_id = face.edge_orientation[2] ? edge_3.vertex_ids[0] : edge_3.vertex_ids[1];
      for (let i = 0; i < Math.max(0, ((faceR - 2) * (faceR - 1)) / 2); ++i) {
        const x_coord = x_count;
        const y_coord = y_count;
        for (let j = 0; j < attributes.length; j++) {
          let interpolated = interpolatedAttribute.get(attributes[j]);
          const attribute = indexedGeometry.getAttribute(attributes[j]);
          if (!interpolated) continue;
          const attributeArray = attributeArrays.get(attributes[j]);
          if (typeof interpolated === "number") {
            interpolated = attribute.getX(v_0_id) * (1 - (x_coord + y_coord) / faceR) + attribute.getX(v_1_id) * (y_coord / faceR) + attribute.getX(v_2_id) * (x_coord / faceR);
            attributeArray.push(interpolated);
          } else {
            const atr1 = attr1.get(attributes[j]);
            const atr2 = attr2.get(attributes[j]);
            const atr3 = attr3.get(attributes[j]);
            interpolated
              .copy(atr1.fromBufferAttribute(attribute, v_0_id))
              .multiplyScalar(1 - (x_coord + y_coord) / faceR)
              .add(atr2.fromBufferAttribute(attribute, v_1_id).multiplyScalar(y_coord / faceR))
              .add(atr3.fromBufferAttribute(attribute, v_2_id).multiplyScalar(x_coord / faceR));
            if (attributes[j] == "normal") interpolated.normalize();
            for (let k = 0; k < attribute.itemSize; k++) {
              attributeArray.push(interpolated.getComponent(k));
            }
          }
        }
        if (x_count >= shiftRNb) {
          shiftRNb--;
          x_count = 1;
          y_count++;
        } else {
          x_count++;
        }
      }
    }
    // console.log("Mesh color point count: " + positionArray.length/3);
    // TODO refactor position and normal into this loop when triangularization has been refactored
    for (let i = 0; i < attributes.length; i++) {
      const attribute = indexedGeometry.getAttribute(attributes[i]);
      this.colorSampleGeometry.setAttribute(attributes[i], new Float32BufferAttribute(attributeArrays.get(attributes[i]), attribute.itemSize));
    }
    this._updateColorTriangleGeometry();
    this.colorSampleGeometry.initializeOctree();
  }

  createInitialGeometrySubdivision() {
    if (!this.edgePosGeometryMap || !this.facePosGeometryMap) {
      console.error("_createInitialGeometrySubdivision: Mesh Color is not initialized, should not happen, internal error");
      return;
    }
    this.posSampleArray = new Array();
    this.edgePosColorMap = new Map();
    this.facePosColorMap = new Map();
    for (const vertex of this.vertexColors) {
      this.posSampleArray.push(vertex.color);
    }
    let posShift = 0;
    for (const edge of this.edgeColors) {
      const edgePosId = this.vertexColors.length + posShift;
      this.edgePosGeometryMap[edge.id] = edgePosId;
      for (let i = 1; i < edge.R; ++i) {
        const edgeSamplePosId = this.vertexColors.length + posShift;
        this.posSampleArray.push(edge.colors[i - 1]);
        this.edgePosColorMap.set(edgeSamplePosId, edge);
        posShift++;
      }
    }
    // Face interiors
    for (const face of this.faceColors) {
      const faceR = face.R;
      // We store face colors by increasing x coordinates
      this.facePosGeometryMap[face.id] = this.vertexColors.length + posShift;
      for (let i = 0; i < Math.max(0, ((faceR - 2) * (faceR - 1)) / 2); ++i) {
        const faceSamplePosId = this.vertexColors.length + posShift;
        this.posSampleArray.push(face.colors[i]);
        this.facePosColorMap.set(faceSamplePosId, face);
        posShift++;
      }
    }
    // TODO check null
    this._updateColorSampleGeometry(this.indexedGeometry);
  }

  resetColorSamples(initColor) {
    const fillColor = initColor ?? new MColor(0, 0, 0);
    for (const vertexColor of this.vertexColors) {
      vertexColor.color.copy(fillColor);
    }
    for (const edgeColor of this.edgeColors) {
      for (const color of edgeColor.colors) color.copy(fillColor);
    }
    for (const faceColor of this.faceColors) {
      for (const color of faceColor.colors) color.copy(fillColor);
    }
  }

  /**
   *
   * TODO remove geometry and use colorSampleGeometry directly ?
   * TODO use filter like meshColorsToAttribute function
   * @param geometry
   * @param attribute_name
   */
  setColorsFromGeometryAttribute(attributeName, vertexGeometryOnly, setRedChannel = true, setGreenChannel = true, setBlueChannel = true, setAlphaChannel = true) {
    const geometry = vertexGeometryOnly ? this.indexedGeometry : this.colorSampleGeometry;
    if (!geometry || !this.posSampleArray) {
      console.log("setColorsFromGeometryAttribute: Mesh Color not initialized");
      return;
    }
    const colorAttribute = geometry.getAttribute(attributeName);
    const positionAttribute = geometry.getAttribute("position");
    if (colorAttribute.count != positionAttribute.count) {
      console.log("Cannot set Colors from geometry attribute position - color attribute length missmatch");
      return;
    }
    // If color attribute item size is 1 we copy info into other channels
    const colorAttributeDim = colorAttribute.itemSize;
    const color = new Vector4();
    for (let i = 0; i < positionAttribute.count; ++i) {
      color.fromBufferAttribute(colorAttribute, i);
      if (i < this.posSampleArray.length) {
        const sample_color = this.posSampleArray[i];
        if (setRedChannel) sample_color.r = color.x;
        if (setGreenChannel) sample_color.g = colorAttributeDim < 2 ? color.x : color.y;
        if (setBlueChannel) sample_color.b = colorAttributeDim < 3 ? color.x : color.z;
        if (setAlphaChannel) sample_color.a = colorAttributeDim < 4 ? color.x : color.w;
      }
    }
  }

  saveColorsToAttribute(attribute_name) {
    if (!this.colorSampleGeometry) {
      console.log("saveColorsToAttribute: Mesh Color not initialized cannot set attibutes");
      return;
    }
    const positionAttribute = this.colorSampleGeometry.getAttribute("position");
    let colorAttribute = this.colorSampleGeometry.getAttribute(attribute_name);
    if (!colorAttribute) {
      this.colorSampleGeometry.setAttribute(attribute_name, new BufferAttribute(new Float32Array(positionAttribute.count * 4), 4));
      colorAttribute = this.colorSampleGeometry.getAttribute(attribute_name);
    }
    this.meshColorsToAttribute(attribute_name);
  }

  meshColorsToAttribute(attribute_name, channelFilter = { r: "r", g: "g", b: "b", a: "a" }) {
    if (!this.colorSampleGeometry || !this.posSampleArray) {
      console.log("meshColorsToAttribute: Mesh Color not initialized cannot set attibutes");
      return;
    }
    const positionAttribute = this.colorSampleGeometry.getAttribute("position");
    const colorAttribute = this.colorSampleGeometry.getAttribute(attribute_name);
    let colorAttributeDim = Object.entries(channelFilter).length;
    if (colorAttribute) colorAttributeDim = colorAttribute.itemSize;
    const colorArray = new Float32Array(positionAttribute.count * colorAttributeDim);
    let color;
    for (let i = 0; i < positionAttribute.count; ++i) {
      color = this.posSampleArray[i];
      colorArray[colorAttributeDim * i] = color[channelFilter.r];
      if (colorAttributeDim >= 2 && channelFilter.g) colorArray[colorAttributeDim * i + 1] = color[channelFilter.g];
      if (colorAttributeDim >= 3 && channelFilter.b) colorArray[colorAttributeDim * i + 2] = color[channelFilter.b];
      if (colorAttributeDim == 4 && channelFilter.a) colorArray[colorAttributeDim * i + 3] = color[channelFilter.a];
    }
    this.colorSampleGeometry.setAttribute(attribute_name, new BufferAttribute(colorArray, colorAttributeDim));
  }

  getVertexColor(index) {
    if (index < this.vertexColors.length) return this.vertexColors[index];
    return null;
  }

  getEdgeColor(index) {
    if (!this.edgePosColorMap) return null;
    const edge_color = this.edgePosColorMap.get(index);
    if (edge_color) return edge_color;
    return null;
  }

  getFaceColor(index) {
    if (!this.facePosColorMap) return null;
    const face_color = this.facePosColorMap.get(index);
    if (face_color) return face_color;
    return null;
  }

  getSampleColor(index) {
    if (!this.posSampleArray || index >= this.posSampleArray.length) return null;
    return this.posSampleArray[index];
  }

  setSampleColor(index, color) {
    if (this.posSampleArray && index < this.posSampleArray.length) {
      const sample_color = this.posSampleArray[index];
      sample_color.copy(color);
    }
  }

  /**
   * Interpolates edge and face color sample from vertex color samples
   * @param set_red_channel
   * @param set_green_channel
   * @param set_blue_channel
   */
  interpolateSamplesFromVertexSamples(set_red_channel = true, set_green_channel = true, set_blue_channel = true, set_alpha_channel = true) {
    for (const edge_color of this.edgeColors) {
      const v_1 = this.vertexColors[edge_color.vertex_ids[0]];
      const v_2 = this.vertexColors[edge_color.vertex_ids[1]];
      for (let i = 0; i < edge_color.colors.length; ++i) {
        const factor = (i + 1) / (edge_color.colors.length + 1);
        if (set_red_channel) edge_color.colors[i].r = v_1.color.r * (1.0 - factor) + v_2.color.r * factor;
        if (set_green_channel) edge_color.colors[i].g = v_1.color.g * (1.0 - factor) + v_2.color.g * factor;
        if (set_blue_channel) edge_color.colors[i].b = v_1.color.b * (1.0 - factor) + v_2.color.b * factor;
        if (set_alpha_channel) edge_color.colors[i].a = v_1.color.a * (1.0 - factor) + v_2.color.a * factor;
      }
    }
    for (const face_color of this.faceColors) {
      // Face interior
      let x_count = 1;
      let y_count = 1;
      let shiftRNb = face_color.R - 2;
      const barycentric = new Vector3();
      const edge_1 = this.edgeColors[face_color.edge_ids[0]];
      const edge_2 = this.edgeColors[face_color.edge_ids[1]];
      const edge_3 = this.edgeColors[face_color.edge_ids[2]];
      const v_1 = this.vertexColors[face_color.edge_orientation[0] ? edge_1.vertex_ids[0] : edge_1.vertex_ids[1]];
      const v_2 = this.vertexColors[face_color.edge_orientation[1] ? edge_2.vertex_ids[0] : edge_2.vertex_ids[1]];
      const v_3 = this.vertexColors[face_color.edge_orientation[2] ? edge_3.vertex_ids[0] : edge_3.vertex_ids[1]];
      // We store face colors by increasing x coordinates
      for (let i = 0; i < face_color.colors.length; ++i) {
        const x_coord = x_count;
        const y_coord = y_count;
        barycentric.setX(x_coord / face_color.R); // v_2
        barycentric.setY(y_coord / face_color.R); // v_1
        barycentric.setZ(1.0 - (x_coord + y_coord) / face_color.R); // v_0
        if (set_red_channel) face_color.colors[i].r = barycentric.z * v_1.color.r + barycentric.y * v_2.color.r + barycentric.x * v_3.color.r;
        if (set_green_channel) face_color.colors[i].g = barycentric.z * v_1.color.g + barycentric.y * v_2.color.g + barycentric.x * v_3.color.g;
        if (set_blue_channel) face_color.colors[i].b = barycentric.z * v_1.color.b + barycentric.y * v_2.color.b + barycentric.x * v_3.color.b;
        if (set_alpha_channel) face_color.colors[i].a = barycentric.z * v_1.color.a + barycentric.y * v_2.color.a + barycentric.x * v_3.color.a;
        if (x_count >= shiftRNb) {
          shiftRNb--;
          x_count = 1;
          y_count++;
        } else {
          x_count++;
        }
      }
    }
  }

  flattenAttributeData(attributeData) {
    if (attributeData.length === 0) {
      return [];
    }
    if (typeof attributeData[0] === "number") {
      return attributeData;
    }
    // to make typescript understand that attributeData contains only vectors at this stage
    const attributeDataSample = attributeData;
    const elemSize = attributeData[0] instanceof Vector4 ? 4 : attributeData[0] instanceof Vector3 ? 3 : 2;
    const flattenedArray = new Array(attributeData.length * elemSize);
    for (let i = 0; i < attributeDataSample.length; ++i) {
      for (let j = 0; j < elemSize; j++) {
        flattenedArray[i * elemSize + j] = attributeDataSample[i].getComponent(j);
      }
    }
    return flattenedArray;
  }

  setEdgeColorResolution(edgeId, newRes, subdivideOtherData = true) {
    if (newRes === this.edgeColors[edgeId].R) return;
    // Compute new colors
    const new_edge_colors = this.edgeColors[edgeId].colors;
    const createColor = function () {
      return new MColor();
    };
    resizeArray(new_edge_colors, newRes - 1, createColor);
    for (let i = 1; i < newRes; ++i) {
      this.sampleEdge(edgeId, i / newRes, new_edge_colors[i - 1], false);
    }
    // Compute new attributes if possible
    if (this.colorSampleGeometry && this.edgePosGeometryMap && this.initialized && subdivideOtherData) {
      const attributes = Object.keys(this.colorSampleGeometry.attributes);
      for (let i = 0; i < attributes.length; i++) {
        // get sampled edge attributes inside an array of number or vector
        const newAttributeData = this.sampleEdgeAttributesData(edgeId, newRes, attributes[i]);
        if (!newAttributeData) continue;
        const attribute = this.colorSampleGeometry.getAttribute(attributes[i]);
        const attributeArray = Array.from(attribute.array);
        const flattenedNewAttributeData = this.flattenAttributeData(newAttributeData);
        attributeArray.splice(this.edgePosGeometryMap[edgeId] * attribute.itemSize, this.edgeColors[edgeId].colors.length * attribute.itemSize, ...flattenedNewAttributeData);
        // TODO might optimize set
        this.colorSampleGeometry.setAttribute(attributes[i], new Float32BufferAttribute(attributeArray, attribute.itemSize));
      }
      for (let i = edgeId + 1; i < this.edgeColors.length; i++) {
        this.edgePosGeometryMap[i] += newRes - this.edgeColors[edgeId].R;
      }
    }
    this.edgeColors[edgeId].R = newRes;
  }

  sampleEdgeAttributesData(edgeId, newRes, attributeName) {
    if (!this.colorSampleGeometry) {
      return;
    }
    const attributeData = this.colorSampleGeometry.getAttribute(attributeName);
    const attributeSize = attributeData.itemSize;
    const newEdgeAttributes = new Array();
    let outAttribute;
    if (attributeSize === 1) {
      outAttribute = 0;
    } else if (attributeSize === 2) {
      outAttribute = new Vector2();
    } else if (attributeSize === 3) {
      outAttribute = new Vector3();
    } else {
      outAttribute = new Vector4();
    }
    for (let i = 1; i < newRes; ++i) {
      this.sampleEdgeAttribute(edgeId, i / newRes, attributeName, outAttribute);
      newEdgeAttributes.push(outAttribute);
    }
    return newEdgeAttributes;
  }

  sampleEdgeAttribute(edgeId, edgeWeight, attributeName, outAttribute) {
    if (!this.colorSampleGeometry || !Object.keys(this.colorSampleGeometry.attributes).includes(attributeName) || !this.edgePosGeometryMap) return;
    const edge = this.edgeColors[edgeId];
    const attributeData = this.colorSampleGeometry.getAttribute(attributeName);
    if (attributeData instanceof InterleavedBufferAttribute) return;
    if (edgeWeight <= 1e-6) {
      if (typeof outAttribute === "number") outAttribute = attributeData.getX(edge.vertex_ids[0]);
      else {
        outAttribute.x = attributeData.getX(edge.vertex_ids[0]);
        outAttribute.y = attributeData.getY(edge.vertex_ids[0]);
        if (outAttribute instanceof Vector3 || outAttribute instanceof Vector4) {
          outAttribute.z = attributeData.getZ(edge.vertex_ids[0]);
        }
        if (outAttribute instanceof Vector4) {
          outAttribute.w = attributeData.getW(edge.vertex_ids[0]);
        }
      }
    } else if (edgeWeight >= 1.0) {
      if (typeof outAttribute === "number") outAttribute = attributeData.getX(edge.vertex_ids[1]);
      else {
        outAttribute.x = attributeData.getX(edge.vertex_ids[1]);
        outAttribute.y = attributeData.getY(edge.vertex_ids[1]);
        if (outAttribute instanceof Vector3 || outAttribute instanceof Vector4) {
          outAttribute.z = attributeData.getZ(edge.vertex_ids[1]);
        }
        if (outAttribute instanceof Vector4) {
          outAttribute.w = attributeData.getW(edge.vertex_ids[1]);
        }
      }
    } else {
      let sample0;
      let sample1;
      const startId = Math.floor(edgeWeight * edge.R);
      const endId = Math.ceil(edgeWeight * edge.R);
      if (startId === 0.0) {
        if (typeof outAttribute === "number") sample0 = attributeData.getX(edge.vertex_ids[0]);
        else {
          sample0 = outAttribute.clone();
          sample0.fromBufferAttribute(attributeData, edge.vertex_ids[0]);
        }
      } else if (typeof outAttribute === "number") sample0 = attributeData.getX(this.edgePosGeometryMap[edge.id] + startId - 1);
      else {
        sample0 = outAttribute.clone();
        sample0.fromBufferAttribute(attributeData, this.edgePosGeometryMap[edge.id] + startId - 1);
      }
      if (endId === edge.R) {
        if (typeof outAttribute === "number") sample1 = attributeData.getX(edge.vertex_ids[1]);
        else {
          sample1 = outAttribute.clone();
          sample1.fromBufferAttribute(attributeData, edge.vertex_ids[1]);
        }
      } else if (typeof outAttribute === "number") sample1 = attributeData.getX(this.edgePosGeometryMap[edge.id] + endId - 1);
      else {
        sample1 = outAttribute.clone();
        sample1.fromBufferAttribute(attributeData, this.edgePosGeometryMap[edge.id] + endId - 1);
      }
      const factor = edgeWeight * edge.R - startId;
      if (typeof outAttribute === "number" && typeof sample0 === "number" && typeof sample1 === "number") {
        outAttribute = sample0 * factor + sample1 * (1 - factor);
      } else {
        outAttribute.copy(sample0).lerp(sample1, factor);
      }
    }
  }

  /**
   *
   * @param facedgeIdeId
   * @param edge_number
   * @param new_res
   * @returns
   */
  getEdgeColorAtRes(edgeId, new_res, edge_color_buffer) {
    const new_edge_colors = edge_color_buffer ?? new Array(new_res - 1);
    if (edge_color_buffer && edge_color_buffer.length != new_res - 1) {
      const createColor = function () {
        return new MColor();
      };
      resizeArray(new_edge_colors, new_res - 1, createColor);
    } else if (!edge_color_buffer) {
      for (let i = 0; i < new_edge_colors.length; ++i) if (!new_edge_colors[i]) new_edge_colors[i] = new MColor();
    }
    for (let i = 1; i < new_res; ++i) {
      this.sampleEdge(edgeId, i / new_res, new_edge_colors[i - 1], true);
    }
    return new_edge_colors;
  }

  setFaceColorResolution(faceId, newRes, subdivideOtherData = true) {
    if (newRes === this.faceColors[faceId].R) return;
    // Compute new colors
    const newFaceColors = this.faceColors[faceId].colors;
    const createColor = function () {
      return new MColor();
    };
    const newFaceSampleCount = ((newRes - 1) * (newRes - 2)) / 2;
    resizeArray(newFaceColors, newFaceSampleCount, createColor);
    // Face interior
    let x_count = 1;
    let y_count = 1;
    let shiftRNb = newRes - 2;
    const barycentric = new Vector3();
    // We store face colors by increasing x coordinates
    for (let i = 0; i < Math.max(0, newFaceSampleCount); ++i) {
      const x_coord = x_count;
      const y_coord = y_count;
      barycentric.setX(x_coord / newRes);
      barycentric.setY(y_coord / newRes);
      barycentric.setZ(1 - (x_coord + y_coord) / newRes);
      this.sampleFace(faceId, barycentric, newFaceColors[i], false);
      if (x_count >= shiftRNb) {
        shiftRNb--;
        x_count = 1;
        y_count++;
      } else {
        x_count++;
      }
    }
    // Compute new attributes if possible
    if (this.colorSampleGeometry && this.facePosGeometryMap && this.initialized && subdivideOtherData) {
      const attributes = Object.keys(this.colorSampleGeometry.attributes);
      for (let i = 0; i < attributes.length; i++) {
        // get sampled edge attributes inside an array of number or vector
        const newAttributeData = this.sampleFaceAttributesData(faceId, newRes, attributes[i]);
        if (!newAttributeData) continue;
        const attribute = this.colorSampleGeometry.getAttribute(attributes[i]);
        const attributeArray = Array.from(attribute.array);
        const flattenedNewAttributeData = this.flattenAttributeData(newAttributeData);
        attributeArray.splice(this.facePosGeometryMap[faceId] * attribute.itemSize, this.faceColors[faceId].colors.length * attribute.itemSize, ...flattenedNewAttributeData);
        // TODO might optimize set
        this.colorSampleGeometry.setAttribute(attributes[i], new Float32BufferAttribute(attributeArray, attribute.itemSize));
      }
      const posShift = ((newRes - 1) * (newRes - 2)) / 2 - ((this.faceColors[faceId].R - 1) * (this.faceColors[faceId].R - 2)) / 2;
      for (let i = faceId + 1; i < this.faceColors.length; i++) {
        this.facePosGeometryMap[i] += posShift;
      }
    }
    this.faceColors[faceId].R = newRes;
  }

  sampleFaceAttributesData(faceId, newRes, attributeName) {
    const barycentric = new Vector3();
    if (!this.colorSampleGeometry) {
      return;
    }
    const attributeData = this.colorSampleGeometry.getAttribute(attributeName);
    const attributeSize = attributeData.itemSize;
    const newFaceAttributes = new Array();
    let outAttribute;
    if (attributeSize === 1) {
      outAttribute = 0;
    } else if (attributeSize === 2) {
      outAttribute = new Vector2();
    } else if (attributeSize === 3) {
      outAttribute = new Vector3();
    } else {
      outAttribute = new Vector4();
    }
    // Face interior
    let x_count = 1;
    let y_count = 1;
    let shiftRNb = newRes - 2;
    // We store face colors by increasing x coordinates
    for (let i = 0; i < Math.max(0, ((newRes - 1) * (newRes - 2)) / 2); ++i) {
      const x_coord = x_count;
      const y_coord = y_count;
      barycentric.setX(x_coord / newRes);
      barycentric.setY(y_coord / newRes);
      barycentric.setZ(1 - (x_coord + y_coord) / newRes);
      this.sampleFaceAttribute(faceId, barycentric, attributeName, outAttribute);
      newFaceAttributes.push(outAttribute);
      if (x_count >= shiftRNb) {
        shiftRNb--;
        x_count = 1;
        y_count++;
      } else {
        x_count++;
      }
    }
    return newFaceAttributes;
  }

  sampleFaceAttribute(faceId, barycentric, attributeName, outAttribute) {
    if (!this.colorSampleGeometry || !Object.keys(this.colorSampleGeometry.attributes).includes(attributeName) || !this.edgePosGeometryMap || !this.facePosGeometryMap) return;
    const face = this.faceColors[faceId];
    const attributeData = this.colorSampleGeometry.getAttribute(attributeName);
    if (attributeData instanceof InterleavedBufferAttribute) return;
    const faceR = this.faceColors[faceId].R;
    const barycentricIds = new Vector3(Math.floor(barycentric.x * faceR), Math.floor(barycentric.y * faceR), Math.floor(barycentric.z * faceR));
    const weights = new Vector3().copy(barycentric).multiplyScalar(faceR).sub(barycentricIds);
    const weightsSum = weights.x + weights.y + weights.z;
    const getFaceAttributeSample = (i, j, outAttribute) => {
      let attributeId;
      let edgeSampleIndex = null;
      if (i == 0) {
        // First Edge
        const edge_1 = this.edgeColors[face.edge_ids[0]];
        if (j == 0) {
          if (face.edge_orientation[0]) attributeId = edge_1.vertex_ids[0];
          else attributeId = edge_1.vertex_ids[1];
        } else if (j == face.R) {
          if (face.edge_orientation[0]) attributeId = edge_1.vertex_ids[1];
          else attributeId = edge_1.vertex_ids[0];
        } else {
          let sampleId;
          if (face.edge_orientation[0]) sampleId = j - 1;
          else sampleId = face.R - 1 - 1 - (j - 1);
          // If the edge resolution is higher than face resolution
          // We take the closest edge color to (i,j)
          edgeSampleIndex = Math.min(edge_1.colors.length - 1, Math.ceil((sampleId * edge_1.R) / face.R));
          attributeId = this.edgePosGeometryMap[edge_1.id] + edgeSampleIndex;
        }
      } else if (j == 0) {
        // Third Edge
        const edge_3 = this.edgeColors[face.edge_ids[2]];
        // Case where i == 0 already managed above
        if (i == face.R) {
          if (face.edge_orientation[2]) attributeId = edge_3.vertex_ids[0];
          else attributeId = edge_3.vertex_ids[1];
        } else {
          let sampleId;
          // Be careful third edge is oriented from v3 to v1 when
          // face.edge_orientation[2] is true
          if (face.edge_orientation[2]) sampleId = face.R - 1 - 1 - (i - 1);
          else sampleId = i - 1;
          // If the edge resolution is higher than face resolution
          // We take the closest edge color to (i,j)
          edgeSampleIndex = Math.min(edge_3.colors.length - 1, Math.ceil((sampleId * edge_3.R) / face.R));
          attributeId = this.edgePosGeometryMap[edge_3.id] + edgeSampleIndex;
        }
      } else if (i + j == face.R) {
        // Second Edge
        const edge_2 = this.edgeColors[face.edge_ids[1]];
        // Case where i == 0 or j == 0 already managed above
        let sampleId;
        if (face.edge_orientation[1]) sampleId = i - 1;
        else sampleId = face.R - 1 - 1 - (i - 1);
        // If the edge resolution is higher than face resolution
        // We take the closest edge color to (i,j)
        edgeSampleIndex = Math.min(edge_2.colors.length - 1, Math.ceil((sampleId * edge_2.R) / face.R));
        attributeId = this.edgePosGeometryMap[edge_2.id] + edgeSampleIndex;
      } // Face interior
      else {
        let currR = face.R - 2;
        let faceSampleIndex = 0;
        for (let k = 1; k < j; ++k) {
          faceSampleIndex += currR;
          currR--;
        }
        faceSampleIndex += i - 1;
        attributeId = this.facePosGeometryMap[face.id] + faceSampleIndex;
      }
      if (typeof outAttribute === "number") outAttribute = attributeData.getX(attributeId);
      else {
        outAttribute.fromBufferAttribute(attributeData, attributeId);
      }
    };
    if (weightsSum < 0.001) {
      getFaceAttributeSample(barycentricIds.x, barycentricIds.y, outAttribute);
    } else {
      const c1 = typeof outAttribute === "number" ? 0 : outAttribute.clone();
      const c2 = typeof outAttribute === "number" ? 0 : outAttribute.clone();
      const c3 = typeof outAttribute === "number" ? 0 : outAttribute.clone();
      if (weightsSum < 1.001) {
        getFaceAttributeSample(barycentricIds.x + 1, barycentricIds.y, c1);
        getFaceAttributeSample(barycentricIds.x, barycentricIds.y + 1, c2);
        getFaceAttributeSample(barycentricIds.x, barycentricIds.y, c3);
        if (typeof outAttribute === "number") outAttribute = c1 * weights.x + c2 * weights.y + c3 * weights.z;
        else {
          for (let i = 0; i < attributeData.itemSize; ++i) {
            outAttribute.setComponent(i, c1.getComponent(i) * weights.x + c2.getComponent(i) * weights.y + c3.getComponent(i) * weights.z);
          }
        }
      } else {
        getFaceAttributeSample(barycentricIds.x, barycentricIds.y + 1, c1);
        getFaceAttributeSample(barycentricIds.x + 1, barycentricIds.y, c2);
        getFaceAttributeSample(barycentricIds.x + 1, barycentricIds.y + 1, c3);
        if (typeof outAttribute === "number") outAttribute = c1 * (1 - weights.x) + c2 * (1 - weights.y) + c3 * (1 - weights.z);
        else {
          for (let i = 0; i < attributeData.itemSize; ++i) {
            outAttribute.setComponent(i, c1.getComponent(i) * (1 - weights.x) + c2.getComponent(i) * (1 - weights.y) + c3.getComponent(i) * (1 - weights.z));
          }
        }
      }
    }
  }

  getFaceColorAtRes(faceId, new_res, face_color_buffer) {
    const new_face_colors = face_color_buffer ?? new Array(Math.max(0, ((new_res - 2) * (new_res - 1)) / 2));
    if (face_color_buffer && new_face_colors.length < Math.max(0, ((new_res - 2) * (new_res - 1)) / 2)) {
      const createColor = function () {
        return new MColor();
      };
      resizeArray(new_face_colors, Math.max(0, ((new_res - 2) * (new_res - 1)) / 2), createColor);
    } else if (!face_color_buffer) {
      for (let i = 0; i < new_face_colors.length; ++i) if (!new_face_colors[i]) new_face_colors[i] = new MColor();
    }
    // Face interior
    let x_count = 1;
    let y_count = 1;
    let shiftRNb = new_res - 2;
    const barycentric = new Vector3();
    // We store face colors by increasing x coordinates
    for (let i = 0; i < Math.max(0, ((new_res - 2) * (new_res - 1)) / 2); ++i) {
      const x_coord = x_count;
      const y_coord = y_count;
      barycentric.setX(x_coord / new_res);
      barycentric.setY(y_coord / new_res);
      barycentric.setZ(1 - (x_coord + y_coord) / new_res);
      this.sampleFace(faceId, barycentric, new_face_colors[i], true);
      if (x_count >= shiftRNb) {
        shiftRNb--;
        x_count = 1;
        y_count++;
      } else {
        x_count++;
      }
    }
    return new_face_colors;
  }

  subdivideFace(faceId, level = 1, subdivideOtherData = true) {
    const oldR = this.faceColors[faceId].R;
    const newR = oldR + level;
    new Vector3();
    const face = this.faceColors[faceId];
    const edge_1 = this.edgeColors[face.edge_ids[0]];
    const edge_2 = this.edgeColors[face.edge_ids[1]];
    const edge_3 = this.edgeColors[face.edge_ids[2]];
    edge_1.R;
    edge_2.R;
    edge_3.R;
    // Sample new color samples
    // First edge
    if (edge_1.R < newR) {
      this.setEdgeColorResolution(edge_1.id, newR, subdivideOtherData);
    }
    // Second edge
    if (edge_2.R < newR) {
      this.setEdgeColorResolution(edge_2.id, newR, subdivideOtherData);
    }
    // Third edge
    if (edge_3.R < newR) {
      this.setEdgeColorResolution(edge_3.id, newR, subdivideOtherData);
    }
    // Face interior
    this.setFaceColorResolution(faceId, newR, subdivideOtherData);
  }

  /**
   *
   * @param faceId
   * @param i sample x coordinate in [[1, R-2]]
   * @param j sample y coordinate in [[1, R-2]]
   */
  getFaceColorSample(faceId, i, j, outColor, useSampler) {
    let currR = this.faceColors[faceId].R - 2;
    let currId = 0;
    for (let k = 1; k < j; ++k) {
      currId += currR;
      currR--;
    }
    currId += i - 1;
    if (useSampler) {
      if (!this.facePosGeometryMap) {
        console.error("Trying to sample non initialized or empty meshColor");
        return;
      }
      this.colorSampler(this.facePosGeometryMap[faceId] + currId, outColor);
    } else {
      outColor.copy(this.faceColors[faceId].colors[currId]);
    }
  }

  getFaceColorSampleBarycentric(faceId, id) {
    const faceR = this.faceColors[faceId].R;
    let shiftRNb = faceR - 2;
    const barycentric = new Vector3();
    let x_count = 1;
    let y_count = 1;
    for (let i = 0; i < id; ++i) {
      if (x_count > shiftRNb) {
        shiftRNb--;
        x_count = 1;
        y_count++;
      } else {
        x_count++;
      }
    }
    barycentric.setX(x_count / faceR);
    barycentric.setY(y_count / faceR);
    barycentric.setZ(1 - (x_count + y_count) / faceR);
    return barycentric;
  }

  /**
   *
   * @param faceId
   * @param i
   * @param j
   * @returns
   */
  getMeshColorSample(faceId, i, j, outColor, useSampler) {
    const face = this.faceColors[faceId];
    let sample = null;
    let edgeSampleIndex = null;
    if (i == 0) {
      // First Edge
      const edge_1 = this.edgeColors[face.edge_ids[0]];
      if (j == 0) {
        if (face.edge_orientation[0]) sample = this.vertexColors[edge_1.vertex_ids[0]];
        else sample = this.vertexColors[edge_1.vertex_ids[1]];
      } else if (j == face.R) {
        if (face.edge_orientation[0]) sample = this.vertexColors[edge_1.vertex_ids[1]];
        else sample = this.vertexColors[edge_1.vertex_ids[0]];
      } else {
        let sample_id;
        if (face.edge_orientation[0]) sample_id = j - 1;
        else sample_id = face.R - 1 - 1 - (j - 1);
        // If the edge resolution is higher than face resolution
        // We take the closest edge color to (i,j)
        sample = edge_1;
        edgeSampleIndex = Math.min(edge_1.colors.length - 1, Math.ceil((sample_id * edge_1.R) / face.R));
      }
    } else if (j == 0) {
      // Third Edge
      const edge_3 = this.edgeColors[face.edge_ids[2]];
      // Case where i == 0 already managed above
      if (i == face.R) {
        if (face.edge_orientation[2]) sample = this.vertexColors[edge_3.vertex_ids[0]];
        else sample = this.vertexColors[edge_3.vertex_ids[1]];
      } else {
        let sample_id;
        // Be careful third edge is oriented from v3 to v1 when
        // face.edge_orientation[2] is true
        if (face.edge_orientation[2]) sample_id = face.R - 1 - 1 - (i - 1);
        else sample_id = i - 1;
        // If the edge resolution is higher than face resolution
        // We take the closest edge color to (i,j)
        sample = edge_3;
        edgeSampleIndex = Math.min(edge_3.colors.length - 1, Math.ceil((sample_id * edge_3.R) / face.R));
      }
    } else if (i + j == face.R) {
      // Second Edge
      const edge_2 = this.edgeColors[face.edge_ids[1]];
      // Case where i == 0 or j == 0 already managed above
      let sample_id;
      if (face.edge_orientation[1]) sample_id = i - 1;
      else sample_id = face.R - 1 - 1 - (i - 1);
      // If the edge resolution is higher than face resolution
      // We take the closest edge color to (i,j)
      sample = edge_2;
      edgeSampleIndex = Math.min(edge_2.colors.length - 1, Math.ceil((sample_id * edge_2.R) / face.R));
    } // Face interior
    else {
      this.getFaceColorSample(faceId, i, j, outColor, useSampler);
      return;
    }
    if (useSampler) {
      if (edgeSampleIndex != null) {
        if (!this.edgePosGeometryMap) {
          console.error("Trying to sample non initialized or empty meshColor");
          return;
        }
        this.colorSampler(this.edgePosGeometryMap[sample.id] + edgeSampleIndex, outColor);
      } else {
        this.colorSampler(sample.id, outColor);
      }
    } else if (edgeSampleIndex != null) {
      outColor.copy(sample.colors[edgeSampleIndex]);
    } else {
      outColor.copy(sample.color);
    }
  }

  sampleEdge(edgeId, edgeWeight, outColor, useSampler) {
    const edge = this.edgeColors[edgeId];
    if (edgeWeight <= 1e-6) {
      if (useSampler) {
        if (!this.edgePosGeometryMap) {
          console.error("Trying to sample non initialized or empty meshColor");
          return;
        }
        this.colorSampler(edge.vertex_ids[0], outColor);
      } else {
        outColor.copy(this.vertexColors[edge.vertex_ids[0]].color);
      }
    } else if (edgeWeight >= 1.0) {
      if (useSampler) {
        if (!this.edgePosGeometryMap) {
          console.error("Trying to sample non initialized or empty meshColor");
          return;
        }
        this.colorSampler(edge.vertex_ids[1], outColor);
      } else {
        outColor.copy(this.vertexColors[edge.vertex_ids[1]].color);
      }
    } else {
      const sample0 = this.tmpColor1;
      const sample1 = this.tmpColor2;
      const startId = Math.floor(edgeWeight * edge.R);
      const endId = Math.ceil(edgeWeight * edge.R);
      if (useSampler) {
        if (!this.edgePosGeometryMap) {
          console.error("Trying to sample non initialized or empty meshColor");
          return;
        }
        if (startId === 0.0) {
          this.colorSampler(edge.vertex_ids[0], sample0);
        } else {
          this.colorSampler(this.edgePosGeometryMap[edge.id] + startId - 1, sample0);
        }
        if (endId === edge.R) {
          this.colorSampler(edge.vertex_ids[1], sample1);
        } else {
          this.colorSampler(this.edgePosGeometryMap[edge.id] + endId - 1, sample1);
        }
      } else {
        if (startId === 0.0) {
          sample0.copy(this.vertexColors[edge.vertex_ids[0]].color);
        } else {
          sample0.copy(this.edgeColors[edgeId].colors[startId - 1]);
        }
        if (endId === edge.R) {
          sample1.copy(this.vertexColors[edge.vertex_ids[1]].color);
        } else {
          sample1.copy(this.edgeColors[edgeId].colors[endId - 1]);
        }
      }
      outColor.lerpColors(sample0, sample1, edgeWeight * edge.R - startId);
    }
  }

  /**
   *
   * @param faceId
   * @param barycentric
   * @returns
   */
  sampleFace(faceId, barycentric, outColor, useSampler) {
    const faceR = this.faceColors[faceId].R;
    const barycentricIds = new Vector3(Math.floor(barycentric.x * faceR), Math.floor(barycentric.y * faceR), Math.floor(barycentric.z * faceR));
    const weights = new Vector3().copy(barycentric).multiplyScalar(faceR).sub(barycentricIds);
    const weightsSum = weights.x + weights.y + weights.z;
    if (weightsSum < 0.001) {
      this.getMeshColorSample(faceId, barycentricIds.x, barycentricIds.y, outColor, useSampler);
    } else {
      const c1 = this.tmpColor1;
      const c2 = this.tmpColor2;
      const c3 = this.tmpColor3;
      if (weightsSum < 1.001) {
        this.getMeshColorSample(faceId, barycentricIds.x + 1, barycentricIds.y, c1, useSampler);
        this.getMeshColorSample(faceId, barycentricIds.x, barycentricIds.y + 1, c2, useSampler);
        this.getMeshColorSample(faceId, barycentricIds.x, barycentricIds.y, c3, useSampler);
        outColor.setRGBA(c1.r * weights.x + c2.r * weights.y + c3.r * weights.z, c1.g * weights.x + c2.g * weights.y + c3.g * weights.z, c1.b * weights.x + c2.b * weights.y + c3.b * weights.z, c1.a * weights.x + c2.a * weights.y + c3.a * weights.z);
      } else {
        this.getMeshColorSample(faceId, barycentricIds.x, barycentricIds.y + 1, c1, useSampler);
        this.getMeshColorSample(faceId, barycentricIds.x + 1, barycentricIds.y, c2, useSampler);
        this.getMeshColorSample(faceId, barycentricIds.x + 1, barycentricIds.y + 1, c3, useSampler);
        outColor.setRGBA(c1.r * (1 - weights.x) + c2.r * (1 - weights.y) + c3.r * (1 - weights.z), c1.g * (1 - weights.x) + c2.g * (1 - weights.y) + c3.g * (1 - weights.z), c1.b * (1 - weights.x) + c2.b * (1 - weights.y) + c3.b * (1 - weights.z), c1.a * (1 - weights.x) + c2.a * (1 - weights.y) + c3.a * (1 - weights.z));
      }
    }
  }
}
function mixinMeshColor(base) {
  return class extends base {
    _internal;

    constructor(...args) {
      super(...args);
      this._internal = new MeshColorInternal();
    }

    // TODO return budget  value
    initialize(edgeColorStep = Number.MAX_VALUE, vertex_max_budget, initColor) {
      // Indexed geometry was not set at constructor
      if (!this.internal.indexedGeometry) {
        // Note that merge vertices is done on all attributes
        // We might remove other attributes than position and normals
        this.internal.indexedGeometry = new BufferGeometry().copy(BufferGeometryUtils$1.mergeVertices(this.geometry));
      }
      // console.time("Mesh Color init");
      const positionAttribute = this.geometry.getAttribute("position");
      const point_1 = new Vector3();
      const point_2 = new Vector3();
      const point_3 = new Vector3();
      const triangle = new Triangle();
      let mean_area = 0.0;
      // Initialize color_sample_geometry
      this.internal.colorSampleGeometry = new OctreeBufferGeometry(); // .copy(this.internal.indexedGeometry);
      // Set barycentric data
      // console.time("Mesh Color Barycentric");
      let barycentricAttribute = this.geometry.getAttribute("barycentric");
      if (!barycentricAttribute) {
        this.geometry.setAttribute("barycentric", new BufferAttribute(new Float32Array(positionAttribute.count * 3), 3));
      }
      barycentricAttribute = this.geometry.getAttribute("barycentric");
      for (let i = 0; i < positionAttribute.count; i += 3) {
        point_1.fromBufferAttribute(positionAttribute, i);
        point_2.fromBufferAttribute(positionAttribute, i + 1);
        point_3.fromBufferAttribute(positionAttribute, i + 2);
        triangle.set(point_1, point_2, point_3);
        mean_area += triangle.getArea();
        barycentricAttribute.setXYZ(i, 1, 0, 0);
        barycentricAttribute.setXYZ(i + 1, 0, 1, 0);
        barycentricAttribute.setXYZ(i + 2, 0, 0, 1);
      }
      mean_area /= positionAttribute.count / 3;
      // console.timeEnd("Mesh Color Barycentric");
      // console.time("Mesh Color Topological setup");
      const indexAttribute = this.internal.indexedGeometry.index;
      const indexPositionAttribute = this.internal.indexedGeometry.getAttribute("position");
      let v_id_1;
      let v_id_2;
      let v_id_3;
      let e_id_1;
      let e_id_2;
      let e_id_3;
      // console.time("Mesh Color Topological setup allocate");
      this.internal.vertexColors = Array(indexPositionAttribute.count);
      this.internal.vertexRelFaces = Array(indexPositionAttribute.count);
      for (let i = 0; i < indexPositionAttribute.count; ++i) this.internal.vertexRelFaces[i] = [];
      this.internal.faceColors = Array(indexAttribute.count / 3);
      // console.timeEnd("Mesh Color Topological setup allocate");
      // We don't know the true number of edges in advance
      // we compute in the first iteration then allocate data
      let edge_count = 0;
      // This set is used to remap edge index to [[0:nb_edges-1]]
      const edge_corresp = new Map();
      // This set is used to make a correspondance between non index geometry vertices (vertex duplicated for each face)
      // and unique vertices of the merged geometry and thus of the vertex colors
      // let vertex_map: Map<string, number> = new Map<string, number>();
      // console.time("Mesh Color Topological setup vertexmap");
      // iterate through indexed geometry vertex and strore
      for (let i = 0; i < indexAttribute.count; i += 3) {
        v_id_1 = indexAttribute.getX(i);
        v_id_2 = indexAttribute.getX(i + 1);
        v_id_3 = indexAttribute.getX(i + 2);
        point_1.fromBufferAttribute(indexPositionAttribute, v_id_1);
        point_2.fromBufferAttribute(indexPositionAttribute, v_id_2);
        point_3.fromBufferAttribute(indexPositionAttribute, v_id_3);
        const fact = 1;
        this.internal.vertexColors[v_id_1] = {
          id: v_id_1,
          color: initColor ?? new MColor(Math.abs(Math.sin(point_1.x / fact)), Math.abs(Math.sin(point_1.y / fact)), Math.abs(Math.sin(point_1.z / fact)))
        };
        this.internal.vertexColors[v_id_2] = {
          id: v_id_2,
          color: initColor ?? new MColor(Math.abs(Math.sin(point_2.x / fact)), Math.abs(Math.sin(point_2.y / fact)), Math.abs(Math.sin(point_2.z / fact)))
        };
        this.internal.vertexColors[v_id_3] = {
          id: v_id_3,
          color: initColor ?? new MColor(Math.abs(Math.sin(point_3.x / fact)), Math.abs(Math.sin(point_3.y / fact)), Math.abs(Math.sin(point_3.z / fact)))
        };
      }
      // console.timeEnd("Mesh Color Topological setup vertexmap");
      // console.time("Mesh Color Topological setup face topo");
      // Face Assignment is done using non indexed geometry so that face ids match input geometry and face colors
      for (let i = 0; i < indexAttribute.count; i += 3) {
        const v_id_1 = indexAttribute.getX(i);
        const v_id_2 = indexAttribute.getX(i + 1);
        const v_id_3 = indexAttribute.getX(i + 2);
        if (v_id_1 === undefined || v_id_2 === undefined || v_id_3 === undefined) {
          console.error("MeshColor initailize: A triangle has been removed through merging geometry");
          return 0;
        }
        e_id_1 = vertexPairing(v_id_1, v_id_2);
        e_id_2 = vertexPairing(v_id_2, v_id_3);
        e_id_3 = vertexPairing(v_id_3, v_id_1);
        if (!edge_corresp.has(e_id_1)) {
          edge_corresp.set(e_id_1, edge_count++);
        }
        if (!edge_corresp.has(e_id_2)) {
          edge_corresp.set(e_id_2, edge_count++);
        }
        if (!edge_corresp.has(e_id_3)) {
          edge_corresp.set(e_id_3, edge_count++);
        }
        this.internal.faceColors[i / 3] = {
          id: i / 3,
          edge_ids: [edge_corresp.get(e_id_1), edge_corresp.get(e_id_2), edge_corresp.get(e_id_3)],
          edge_orientation: [true, true, true],
          colors: [],
          R: 1
        };
        if (!this.internal.vertexRelFaces[v_id_1]) this.internal.vertexRelFaces[v_id_1] = [this.internal.faceColors[i / 3]];
        else this.internal.vertexRelFaces[v_id_1].push(this.internal.faceColors[i / 3]);
        if (!this.internal.vertexRelFaces[v_id_2]) this.internal.vertexRelFaces[v_id_2] = [this.internal.faceColors[i / 3]];
        else this.internal.vertexRelFaces[v_id_2].push(this.internal.faceColors[i / 3]);
        if (!this.internal.vertexRelFaces[v_id_3]) this.internal.vertexRelFaces[v_id_3] = [this.internal.faceColors[i / 3]];
        else this.internal.vertexRelFaces[v_id_3].push(this.internal.faceColors[i / 3]);
      }
      // console.timeEnd("Mesh Color Topological setup face topo");
      // console.time("Mesh Color Topological setup edge topo");
      this.internal.edgeColors = Array(edge_count);
      this.internal.edgeRelFaces = Array(edge_count);
      // Edge assignment is consequently also done using non indexed geometry triangle ordering
      for (let i = 0; i < indexAttribute.count; i += 3) {
        const v_id_1 = indexAttribute.getX(i);
        const v_id_2 = indexAttribute.getX(i + 1);
        const v_id_3 = indexAttribute.getX(i + 2);
        if (v_id_1 === undefined || v_id_2 === undefined || v_id_3 === undefined) {
          console.error("MeshColor initailize: A triangle has been removed through merging geometry");
          return 0;
        }
        // Set edge color attributes-----------------------------
        const vp_1 = vertexPairing(v_id_1, v_id_2);
        const vp_2 = vertexPairing(v_id_2, v_id_3);
        const vp_3 = vertexPairing(v_id_3, v_id_1);
        e_id_1 = edge_corresp.get(vp_1);
        e_id_2 = edge_corresp.get(vp_2);
        e_id_3 = edge_corresp.get(vp_3);
        const face = this.internal.faceColors[i / 3];
        // We orient edges because we need a convention
        // Edges that already contain data must be read in the opposite direction
        // inside this current face
        if (this.internal.edgeColors[e_id_1] && this.internal.edgeColors[e_id_1].vertex_ids.length > 0) {
          face.edge_orientation[0] = false;
        } else {
          this.internal.edgeColors[e_id_1] = { id: e_id_1, vertex_ids: [v_id_1, v_id_2], colors: [], R: 1 };
        }
        // Store related faces
        if (!this.internal.edgeRelFaces[e_id_1]) this.internal.edgeRelFaces[e_id_1] = [face];
        else this.internal.edgeRelFaces[e_id_1].push(face);
        if (this.internal.edgeColors[e_id_2] && this.internal.edgeColors[e_id_2].vertex_ids.length > 0) {
          face.edge_orientation[1] = false;
        } else {
          this.internal.edgeColors[e_id_2] = { id: e_id_2, vertex_ids: [v_id_2, v_id_3], colors: [], R: 1 };
        }
        // Store related faces
        if (!this.internal.edgeRelFaces[e_id_2]) this.internal.edgeRelFaces[e_id_2] = [face];
        else this.internal.edgeRelFaces[e_id_2].push(face);
        if (this.internal.edgeColors[e_id_3] && this.internal.edgeColors[e_id_3].vertex_ids.length > 0) {
          face.edge_orientation[2] = false;
        } else {
          this.internal.edgeColors[e_id_3] = { id: e_id_3, vertex_ids: [v_id_3, v_id_1], colors: [], R: 1 };
        }
        // Store related faces
        if (!this.internal.edgeRelFaces[e_id_3]) this.internal.edgeRelFaces[e_id_3] = [face];
        else this.internal.edgeRelFaces[e_id_3].push(face);
        //-----------------------------------------------------
      }
      this.internal.facePosGeometryMap = new Array(this.faceColors.length);
      this.internal.edgePosGeometryMap = new Array(this.edgeColors.length);
      // console.timeEnd("Mesh Color Subdivisions");
      const vertex_budget = this.generateColorSamples(edgeColorStep, vertex_max_budget);
      this.internal.initialized = true;
      // console.timeEnd("Mesh Color init");
      return vertex_budget;
    }

    generateColorSamples(edgeColorStep, vertexMaxBudget) {
      const vertexBudget = this._internal.generateColorSamples(edgeColorStep, vertexMaxBudget);
      if (this._internal.textureFaceMetaData.length > 0) {
        this.computeTextureMetaData();
      }
      this.samplesResolutionChanged.emit();
      return vertexBudget;
    }

    // /!\ FOR SERIALIZATION ONLY ----------------
    _fromWorkerData(indexed_geometry, color_sample_geometry, vertex_colors, edge_colors, face_colors, texture_margin_length, texture_size) {
      this._internal.fromWorkerData(indexed_geometry, color_sample_geometry, vertex_colors, edge_colors, face_colors, texture_margin_length, texture_size);
      if (this._internal.textureSize > 0) {
        this.computeTextureMetaData();
      }
      this._internal.initialized = true;
    }

    // Function to call right after calling toWorkerData to dispose data that will not be used anymore
    // This function is quite critical to call because meshColors may manipulates a lot of data
    _disposeWorkered() {
      this._internal.disposeWorkered();
    }

    _generateSampleGeometryAndComputedProperties() {
      this._internal._generateSampleGeometryAndComputedProperties();
      this.computeTextureMetaData();
      this.samplesResolutionChanged.emit();
    }

    get vertexColors() {
      return this._internal.vertexColors;
    }

    get edgeColors() {
      return this._internal.edgeColors;
    }

    get faceColors() {
      return this._internal.faceColors;
    }

    get facePosGeometryMap() {
      return this._internal.facePosGeometryMap;
    }

    get posSampleArray() {
      return this._internal.posSampleArray;
    }

    get edgeRelFaces() {
      return this._internal.edgeRelFaces;
    }

    get edgePosGeometryMap() {
      return this._internal.edgePosGeometryMap;
    }

    get edgePosColorMap() {
      return this._internal.edgePosColorMap;
    }

    get facePosColorMap() {
      return this._internal.facePosColorMap;
    }

    get vertexRelFaces() {
      return this._internal.vertexRelFaces;
    }

    get initialized() {
      return this._internal.initialized;
    }

    // -------------------------------
    // Getters ------------------------------
    // Note this is done to avoid typescript error
    // interface do not support protected members....
    get internal() {
      return this._internal;
    }

    get edgeResolutionChanged() {
      return this._internal.edgeResolutionChanged;
    }

    get faceResolutionChanged() {
      return this._internal.faceResolutionChanged;
    }

    get samplesResolutionChanged() {
      return this._internal.samplesResolutionChanged;
    }

    getVertexColors() {
      return this._internal.vertexColors;
    }

    getEdgeColors() {
      return this._internal.edgeColors;
    }

    getFaceColors() {
      return this._internal.faceColors;
    }

    getIndexedGeometry() {
      return this._internal.indexedGeometry;
    }

    getColorSampleGeometry() {
      return this._internal.colorSampleGeometry;
    }

    getVertexRelativeFaces(v_id) {
      return this._internal.getVertexRelativeFaces(v_id);
    }

    getEdgeRelativeFaces(edge_id) {
      return this._internal.getEdgeRelativeFaces(edge_id);
    }

    getColorSampleNb() {
      return this._internal.getColorSampleNb();
    }

    hasTextureMetadata() {
      return this._internal.textureFaceMetaData.length > 0;
    }

    getTextureMetadata() {
      return this._internal.textureFaceMetaData;
    }

    getTextureQuadMetadata() {
      return this._internal.textureQuadFaceMetaData;
    }

    getTextureSize() {
      return this._internal.textureSize;
    }

    getTextureMarginLength() {
      return this._internal.textureMarginLength;
    }

    getVertexColor(index) {
      return this._internal.getVertexColor(index);
    }

    getEdgeColor(index) {
      return this._internal.getEdgeColor(index);
    }

    getFaceColor(index) {
      return this._internal.getFaceColor(index);
    }

    getSampleColor(index) {
      return this._internal.getSampleColor(index);
    }

    /**
     *
     * @param faceId
     * @param i sample x coordinate in [[1, R-2]]
     * @param j sample y coordinate in [[1, R-2]]
     */
    getFaceColorSample(faceId, i, j, outColor, useSampler) {
      this._internal.getFaceColorSample(faceId, i, j, outColor, useSampler);
    }

    getFaceColorSampleBarycentric(faceId, id) {
      return this._internal.getFaceColorSampleBarycentric(faceId, id);
    }

    /**
     *
     * @param faceId
     * @param i
     * @param j
     * @returns
     */
    getMeshColorSample(faceId, i, j, outColor, useSampler) {
      return this._internal.getMeshColorSample(faceId, i, j, outColor, useSampler);
    }

    getEdgeColorSamplePosId(edgeId, colorId) {
      return this._internal.getEdgeColorSamplePosId(edgeId, colorId);
    }

    getFaceColorSamplePosId(faceId, colorId) {
      return this._internal.getFaceColorSamplePosId(faceId, colorId);
    }

    // TODO Experimental
    getColorSampler() {
      return this._internal.colorSampler;
    }

    // --------------------------------------------
    // Setters ----------------------------------
    // Note this is done to avoid typescript error
    // interface do not support protected members....
    set internal(inInternal) {
      this._internal = inInternal;
    }

    setTextureMarginLength(texture_margin_length) {
      this._internal.textureMarginLength = texture_margin_length;
    }

    setColorsFromGeometryAttribute(attribute_name, vertex_geometry_only, set_red_channel = true, set_green_channel = true, set_blue_channel = true, set_alpha_channel = true) {
      this._internal.setColorsFromGeometryAttribute(attribute_name, vertex_geometry_only, set_red_channel, set_green_channel, set_blue_channel, set_alpha_channel);
    }

    setSampleColor(index, color) {
      this._internal.setSampleColor(index, color);
    }

    // TODO Experimental
    setColorSampler(sampler) {
      this._internal.setColorSampler(sampler);
    }

    // --------------------------------
    computeSamplingDensity() {
      this._internal.computeSamplingDensity();
    }

    _computeFaceTextureMetadata(face) {
      let maxEdgeR = 0;
      const edge_1 = this._internal.edgeColors[face.edge_ids[0]];
      const edge_2 = this._internal.edgeColors[face.edge_ids[1]];
      const edge_3 = this._internal.edgeColors[face.edge_ids[2]];
      maxEdgeR = Math.max(edge_1.R, edge_2.R, edge_3.R);
      if (!this._internal.textureFaceMetaData[face.id]) {
        this._internal.textureFaceMetaData[face.id] = {
          R: maxEdgeR,
          uv_start_pos: new Vector2(0, 0)
        };
      } else {
        this._internal.textureFaceMetaData[face.id].R = maxEdgeR;
        this._internal.textureFaceMetaData[face.id].uv_start_pos.set(0, 0);
      }
    }

    // This function computes UVs and minimal texture size to
    // Render the Mesh Colors with texture properly
    computeTextureMetaData() {
      // First implementation where we suppose that each triangle is not linked to another
      // Later on we can quandrangulize the mesh before creating the texture
      // First we sort faces by decreasing resolution
      // this line makes a copy of faceColors
      const resSortedFaces = this._internal.faceColors.map((x) => x);
      // Init texture face color samples that will contain true export color samples
      this._internal.textureFaceMetaData = new Array(resSortedFaces.length);
      this._internal.textureQuadFaceMetaData = new Map();
      let nb_texel = 0;
      for (const face of resSortedFaces) {
        const edge1 = this._internal.edgeColors[face.edge_ids[0]];
        const edge2 = this._internal.edgeColors[face.edge_ids[1]];
        const edge3 = this._internal.edgeColors[face.edge_ids[2]];
        // nb_texel += (3 + Math.max(0,face.R - 1) + Math.max(0,face.R - 1)*Math.max(0,face.R - 2)/2);
        // Non quadrangulized version
        const maxR = Math.max(edge1.R, edge2.R, edge3.R);
        nb_texel += ((maxR + 1) * (maxR + 1)) / 2 + maxR + 1;
        // For now we do not add pixels on the diagonal
        for (let m = 1; m <= this._internal.textureMarginLength; ++m) nb_texel += 2 * (maxR - 1) * m + 3 * (m * 2 + 1);
      }
      let texture_size = 4;
      const texture_max_size = 8192; // this._max_texture_size;
      // TODO do better
      const nb_texel_side = Math.ceil(Math.sqrt(nb_texel));
      while (texture_size < nb_texel_side) {
        texture_size *= 2;
      }
      // Before creating data texture
      // we compute true exported sample colors and check that texture size is adapated
      // In some edge cases we will loose too much texture space from triangles that would
      // have be written over the texture width if not shifted to new row.
      const current_uv = new Vector2(0, 0);
      // First faces must be resampled if one of the edge resolution is bigger than the face resolution
      console.time("Mesh Color texture face metadata");
      let maxFaceR = 0;
      for (let i = 0; i < resSortedFaces.length; ++i) {
        const face = resSortedFaces[i];
        this._computeFaceTextureMetadata(face);
      }
      console.timeEnd("Mesh Color texture face metadata");
      resSortedFaces.sort((a, b) => {
        const aR = this._internal.textureFaceMetaData[a.id].R;
        const bR = this._internal.textureFaceMetaData[b.id].R;
        if (aR == bR) return a.id - b.id;
        return bR - aR;
      });
      // TEST QuadFaces
      console.time("Mesh Color texture face Quad metadata");
      let dealtFaces = new Set();
      for (let i = 0; i < resSortedFaces.length; ++i) {
        const face = resSortedFaces[i];
        if (dealtFaces.has(face.id)) continue;
        // We look for the most subdivided relative face that is not
        // already dealt with
        const edge1 = this._internal.edgeColors[face.edge_ids[0]];
        const edge2 = this._internal.edgeColors[face.edge_ids[1]];
        const edge3 = this._internal.edgeColors[face.edge_ids[2]];
        const faces1 = this.getEdgeRelativeFaces(edge1.id);
        const faces2 = this.getEdgeRelativeFaces(edge2.id);
        const faces3 = this.getEdgeRelativeFaces(edge3.id);
        const relFaces = [];
        if (faces1 && faces1.length == 2) {
          faces1[0].id == face.id ? relFaces.push({ face: faces1[1], edge: 1 }) : relFaces.push({ face: faces1[0], edge: 1 });
        }
        if (faces2 && faces2.length == 2) {
          faces2[0].id == face.id ? relFaces.push({ face: faces2[1], edge: 2 }) : relFaces.push({ face: faces2[0], edge: 2 });
        }
        if (faces3 && faces3.length == 2) {
          faces3[0].id == face.id ? relFaces.push({ face: faces3[1], edge: 3 }) : relFaces.push({ face: faces3[0], edge: 3 });
        }
        let maxR = 1;
        let maxFace = null;
        let maxFaceCommonEdge = null;
        for (const relFace of relFaces) {
          if (!dealtFaces.has(relFace.face.id) && this._internal.textureFaceMetaData[relFace.face.id].R >= maxR) {
            maxR = this._internal.textureFaceMetaData[relFace.face.id].R;
            maxFace = relFace.face;
            maxFaceCommonEdge = relFace.edge;
          }
        }
        if (maxFace && maxFaceCommonEdge) {
          dealtFaces.add(maxFace.id);
          dealtFaces.add(face.id);
          const metaData = {
            // face R is greater because we treat faces by decreasing R order
            R: this._internal.textureFaceMetaData[face.id].R,
            uv_start_pos: new Vector2(0, 0),
            startFaceId: face.id,
            endFaceId: maxFace.id,
            commonEdge: maxFaceCommonEdge
          };
          this._internal.textureQuadFaceMetaData.set(maxFace.id, metaData);
          this._internal.textureQuadFaceMetaData.set(face.id, metaData);
        }
      }
      console.timeEnd("Mesh Color texture face Quad metadata");
      console.time("Mesh Color texture compute texture size");
      // Simulate how uvs will be written and if uv.y > texture size
      // we must enlarge texture
      let nextFreeUVStart = [];
      dealtFaces = new Set();
      // stores next available uv start by increasing order of X
      let XFreeUVStart = new OrderedMap();
      // stores next available uv start by increasing order of Y
      let YFreeUVStart = new OrderedMap();
      // Version ALT of packing algorithm, uses another heuristic that
      // seems to be worse than the first one but could be used in another context -> to move
      // becareful that condition currentMinY <  faceY should be currentMinY > faceY for ALT version
      // let YFreeUVStart = new OrderedMap<number,Vector2[]>();
      let XLimit = texture_size;
      let currentMinY = null;
      for (let i = 0; i < resSortedFaces.length; ++i) {
        const face = resSortedFaces[i];
        if (dealtFaces.has(face.id)) {
          continue;
        }
        const quadMetaData = this._internal.textureQuadFaceMetaData.get(face.id);
        maxFaceR = quadMetaData ? quadMetaData.R : this._internal.textureFaceMetaData[face.id].R;
        if (quadMetaData) {
          dealtFaces.add(quadMetaData.startFaceId);
          dealtFaces.add(quadMetaData.endFaceId);
        }
        let startFaceX = current_uv.x;
        // 2 (pixel of vertex 1 and 3) + nb of pixels in one edge
        const faceDelta = 2 + Math.max(maxFaceR - 1, 0) + 2 * this._internal.textureMarginLength;
        // EndFaceX and Y
        let faceX = current_uv.x + faceDelta;
        let faceY = current_uv.y + faceDelta;
        const mustShiftY = faceX >= XLimit;
        // if we reach the limit
        while (current_uv.x + faceDelta >= XLimit && XFreeUVStart.length > 0) {
          const lastFreeUVStart = YFreeUVStart.front()[1];
          YFreeUVStart.eraseElementByPos(0);
          // Version ALT
          /* let lastFreeUVStarts = YFreeUVStart.front()![1];
                    let lastFreeUVStart = lastFreeUVStarts[0];
                    lastFreeUVStarts.splice(0,1)
                    if(lastFreeUVStarts.length == 0)
                        YFreeUVStart.eraseElementByPos(0); */
          let lastUVXPos = 0;
          for (const it = XFreeUVStart.begin(); !it.equals(XFreeUVStart.end()); it.next()) {
            if (it.pointer[1].x == lastFreeUVStart.x) break;
            lastUVXPos++;
          }
          if (lastUVXPos + 1 < XFreeUVStart.length) {
            XLimit = XFreeUVStart.getElementByPos(lastUVXPos + 1)[1].x;
          } else {
            XLimit = texture_size;
          }
          XFreeUVStart.eraseElementByKey(lastFreeUVStart.x);
          current_uv.x = lastFreeUVStart.x;
          current_uv.y = lastFreeUVStart.y;
        }
        // Update current uv triangle origin
        if (!mustShiftY) {
          current_uv.x = faceX;
          if (!currentMinY) {
            currentMinY = faceY;
          }
        } else {
          startFaceX = current_uv.x;
          faceX = current_uv.x + faceDelta;
          faceY = current_uv.y + faceDelta;
          current_uv.x = faceX;
          currentMinY = null;
        }
        if ((!mustShiftY && (XFreeUVStart.length == 0 || (currentMinY && currentMinY < faceY))) || mustShiftY) {
          const newStart = new Vector2(startFaceX, faceY);
          nextFreeUVStart.push(newStart);
          currentMinY = faceY;
          // In the case where there is already a free uv at the same Y
          // we keep the one whose x coordinate is the smallest
          const storedYFreeUV = YFreeUVStart.getElementByKey(newStart.y);
          if (!storedYFreeUV || (storedYFreeUV && storedYFreeUV.x > newStart.x)) {
            XFreeUVStart.setElement(newStart.x, newStart);
            YFreeUVStart.setElement(newStart.y, newStart);
            if (storedYFreeUV && storedYFreeUV.x != newStart.x) XFreeUVStart.eraseElementByKey(storedYFreeUV.x);
          }
          // Version ALT
          /* XFreeUVStart.setElement(newStart.x, newStart);
                    if(!storedYFreeUV)
                        YFreeUVStart.setElement(newStart.y, [newStart]);
                    else
                        storedYFreeUV.push(newStart); */
        }
        // if last face we need to keep last maxR value to test if we need to extend the texture size
        if (i == resSortedFaces.length - 1) {
          current_uv.x = faceX;
          current_uv.y = faceY;
          // current_uv.y += Math.max(max_R - 1,0) + 2*this._internal.textureMarginLength;
        }
      }
      // texture size was not big enough
      if (current_uv.y >= texture_size) {
        texture_size *= 2;
      }
      console.log(`Texture Size: ${texture_size}`);
      if (texture_size > texture_max_size) {
        console.error(`Mesh Color texture size greater than maximum ${texture_max_size}x${texture_max_size}`);
        return;
      }
      console.timeEnd("Mesh Color texture compute texture size");
      this._internal.textureSize = texture_size;
      const uvs = new Float32Array(this.geometry.getAttribute("position").count * 2);
      current_uv.x = this._internal.textureMarginLength;
      current_uv.y = this._internal.textureMarginLength;
      // Write colors texels and uvs
      // console.time("Mesh Color texture write data");
      console.time("Mesh Color texture compute uvs");
      nextFreeUVStart = [];
      dealtFaces = new Set();
      XFreeUVStart = new OrderedMap();
      YFreeUVStart = new OrderedMap();
      // Version ALT
      // YFreeUVStart = new OrderedMap<number,Vector2[]>();
      XLimit = texture_size;
      currentMinY = null;
      for (let i = 0; i < resSortedFaces.length; ++i) {
        const face = resSortedFaces[i];
        if (dealtFaces.has(face.id)) {
          continue;
        }
        const quadMetaData = this._internal.textureQuadFaceMetaData.get(face.id);
        maxFaceR = quadMetaData ? quadMetaData.R : this._internal.textureFaceMetaData[face.id].R;
        if (quadMetaData) {
          dealtFaces.add(quadMetaData.startFaceId);
          dealtFaces.add(quadMetaData.endFaceId);
        }
        let startFaceX = current_uv.x;
        const faceDelta = 2 + Math.max(maxFaceR - 1, 0) + 2 * this._internal.textureMarginLength;
        let faceX = current_uv.x + faceDelta;
        let faceY = current_uv.y + faceDelta;
        const mustShiftY = faceX >= XLimit;
        while (current_uv.x + faceDelta >= XLimit && XFreeUVStart.length > 0) {
          const lastFreeUVStart = YFreeUVStart.front()[1];
          YFreeUVStart.eraseElementByPos(0);
          // Version ALT
          /* let lastFreeUVStarts = YFreeUVStart.front()![1];
                    let lastFreeUVStart = lastFreeUVStarts[0];
                    lastFreeUVStarts.splice(0,1)
                    if(lastFreeUVStarts.length == 0)
                        YFreeUVStart.eraseElementByPos(0); */
          let lastUVXPos = 0;
          for (const it = XFreeUVStart.begin(); !it.equals(XFreeUVStart.end()); it.next()) {
            if (it.pointer[1].x == lastFreeUVStart.x) break;
            lastUVXPos++;
          }
          if (lastUVXPos + 1 < XFreeUVStart.length) {
            XLimit = XFreeUVStart.getElementByPos(lastUVXPos + 1)[1].x;
          } else {
            XLimit = texture_size;
          }
          XFreeUVStart.eraseElementByKey(lastFreeUVStart.x);
          current_uv.x = lastFreeUVStart.x;
          current_uv.y = lastFreeUVStart.y;
        }
        const face_uv_x = current_uv.x + this._internal.textureMarginLength;
        const face_uv_y = current_uv.y + this._internal.textureMarginLength;
        this._internal.textureFaceMetaData[face.id].uv_start_pos.x = current_uv.x + this._internal.textureMarginLength;
        this._internal.textureFaceMetaData[face.id].uv_start_pos.y = current_uv.y + this._internal.textureMarginLength;
        if (quadMetaData) {
          quadMetaData.uv_start_pos.x = current_uv.x + this._internal.textureMarginLength;
          quadMetaData.uv_start_pos.y = current_uv.y + this._internal.textureMarginLength;
        }
        // Update current uv triangle origin
        if (!mustShiftY) {
          current_uv.x = faceX;
          if (!currentMinY) currentMinY = faceY;
        } else {
          startFaceX = current_uv.x;
          faceX = current_uv.x + faceDelta;
          faceY = current_uv.y + faceDelta;
          current_uv.x = faceX;
          currentMinY = null;
        }
        // TODO can do a little bit better should be currentMinY > faceY but XLimit might be wrong
        if ((!mustShiftY && (XFreeUVStart.length == 0 || (currentMinY && currentMinY < faceY))) || mustShiftY) {
          const newStart = new Vector2(startFaceX, faceY);
          nextFreeUVStart.push(newStart);
          currentMinY = faceY;
          // In the case where there is already a free uv at the same Y
          // we keep the one whose x coordinate is the smallest
          const storedYFreeUV = YFreeUVStart.getElementByKey(newStart.y);
          if (!storedYFreeUV || (storedYFreeUV && storedYFreeUV.x > newStart.x)) {
            XFreeUVStart.setElement(newStart.x, newStart);
            YFreeUVStart.setElement(newStart.y, newStart);
            if (storedYFreeUV && storedYFreeUV.x != newStart.x) XFreeUVStart.eraseElementByKey(storedYFreeUV.x);
          }
          // Version ALT
          /* let storedYFreeUV = YFreeUVStart.getElementByKey(newStart.y);
                    XFreeUVStart.setElement(newStart.x, newStart);
                    if(!storedYFreeUV)
                        YFreeUVStart.setElement(newStart.y, [newStart]);
                    else
                        storedYFreeUV.push(newStart); */
        }
        // Fill uv data
        // TODO Explain how it works
        /**
         *
         *  *
         *             x_ x _ x _ x
         *             |  \        |
         *     /|      x x x  x  x x  \
         *      |      |start\  end|   \
         *      |      x x  x x  x x    \|
         *             |        \  |
         *             x _ x_ x _  x  vertex 3
         *
         *             /__ __ __
         *
         *
         */
        const UV1X = (face_uv_x + 0.5) / texture_size;
        const UV1Y = (face_uv_y + 0.5) / texture_size;
        const UV2X = (face_uv_x + 0.5) / texture_size;
        const UV2Y = (face_uv_y + maxFaceR + 0.5) / texture_size;
        const UV3X = (face_uv_x + maxFaceR + 0.5) / texture_size;
        const UV3Y = (face_uv_y + 0.5) / texture_size;
        const UV4X = (face_uv_x + maxFaceR + 0.5) / texture_size;
        const UV4Y = (face_uv_y + maxFaceR + 0.5) / texture_size;
        let stVertex1UVX = UV1X;
        let stVertex1UVY = UV1Y;
        let stVertex2UVX = UV2X;
        let stVertex2UVY = UV2Y;
        let stVertex3UVX = UV3X;
        let stVertex3UVY = UV3Y;
        if (quadMetaData) {
          switch (quadMetaData.commonEdge) {
            case 1: {
              stVertex1UVX = UV2X;
              stVertex1UVY = UV2Y;
              stVertex2UVX = UV3X;
              stVertex2UVY = UV3Y;
              stVertex3UVX = UV1X;
              stVertex3UVY = UV1Y;
              break;
            }
            case 2: {
              break;
            }
            case 3: {
              stVertex1UVX = UV3X;
              stVertex1UVY = UV3Y;
              stVertex2UVX = UV1X;
              stVertex2UVY = UV1Y;
              stVertex3UVX = UV2X;
              stVertex3UVY = UV2Y;
              break;
            }
          }
        }
        uvs[face.id * 6] = stVertex1UVX;
        uvs[face.id * 6 + 1] = stVertex1UVY;
        uvs[face.id * 6 + 2] = stVertex2UVX;
        uvs[face.id * 6 + 3] = stVertex2UVY;
        uvs[face.id * 6 + 4] = stVertex3UVX;
        uvs[face.id * 6 + 5] = stVertex3UVY;
        if (quadMetaData) {
          const commonEdgeId = face.edge_ids[quadMetaData.commonEdge - 1];
          const endFace = this.faceColors[quadMetaData.endFaceId];
          const endFaceCommonEdge = commonEdgeId == endFace.edge_ids[0] ? 1 : commonEdgeId == endFace.edge_ids[1] ? 2 : 3;
          let edVertex1UVX = UV2X;
          let edVertex1UVY = UV2Y;
          let edVertex2UVX = UV4X;
          let edVertex2UVY = UV4Y;
          let edVertex3UVX = UV3X;
          let edVertex3UVY = UV3Y;
          switch (endFaceCommonEdge) {
            case 1: {
              edVertex1UVX = UV3X;
              edVertex1UVY = UV3Y;
              edVertex2UVX = UV2X;
              edVertex2UVY = UV2Y;
              edVertex3UVX = UV4X;
              edVertex3UVY = UV4Y;
              break;
            }
            case 2: {
              edVertex1UVX = UV4X;
              edVertex1UVY = UV4Y;
              edVertex2UVX = UV3X;
              edVertex2UVY = UV3Y;
              edVertex3UVX = UV2X;
              edVertex3UVY = UV2Y;
              break;
            }
            case 3: {
              edVertex1UVX = UV2X;
              edVertex1UVY = UV2Y;
              edVertex2UVX = UV4X;
              edVertex2UVY = UV4Y;
              edVertex3UVX = UV3X;
              edVertex3UVY = UV3Y;
              break;
            }
          }
          uvs[endFace.id * 6] = edVertex1UVX;
          uvs[endFace.id * 6 + 1] = edVertex1UVY;
          uvs[endFace.id * 6 + 2] = edVertex2UVX;
          uvs[endFace.id * 6 + 3] = edVertex2UVY;
          uvs[endFace.id * 6 + 4] = edVertex3UVX;
          uvs[endFace.id * 6 + 5] = edVertex3UVY;
        }
      }
      console.timeEnd("Mesh Color texture compute uvs");
      this.geometry.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
    }

    _createInitialGeometrySubdivision() {
      this._internal.createInitialGeometrySubdivision();
    }

    resetColorSamples(init_color) {
      this._internal.resetColorSamples(init_color);
    }

    saveColorsToAttribute(attribute_name) {
      this._internal.saveColorsToAttribute(attribute_name);
    }

    meshColorsToAttribute(attribute_name, channelFilter = { r: "r", g: "g", b: "b", a: "a" }) {
      this._internal.meshColorsToAttribute(attribute_name, channelFilter);
    }

    /**
     * Interpolates edge and face color sample from vertex color samples
     * @param set_red_channel
     * @param set_green_channel
     * @param set_blue_channel
     */
    interpolateSamplesFromVertexSamples(set_red_channel = true, set_green_channel = true, set_blue_channel = true, set_alpha_channel = true) {
      this._internal.interpolateSamplesFromVertexSamples(set_red_channel, set_green_channel, set_blue_channel, set_alpha_channel);
    }

    /**
     *
     * @param edgeId
     * @param newRes
     * @param edgeColorBuffer
     * @returns
     */
    getEdgeColorAtRes(edgeId, newRes, edgeColorBuffer) {
      return this._internal.getEdgeColorAtRes(edgeId, newRes, edgeColorBuffer);
    }

    getFaceColorAtRes(faceId, newRes, faceColorBuffer) {
      return this._internal.getFaceColorAtRes(faceId, newRes, faceColorBuffer);
    }

    subdivideFace(faceId, level = 1, subdivideOtherData = true) {
      this._internal.subdivideFace(faceId, level, subdivideOtherData);
    }

    /**
     *
     * @param faceId
     * @param barycentric
     * @returns
     */
    sampleFace(faceId, barycentric, outColor, useSampler) {
      this._internal.sampleFace(faceId, barycentric, outColor, useSampler);
    }
  };
}

class MeshColor extends mixinMeshColor(OctreeMesh) {
  //---------------------------------------
  // TODO geometry must be indexed
  /**
   *
   * @param geometry
   * @param initColor
   */
  constructor(geometry) {
    // console.time("Mesh Color Preconstruct");
    // Mesh color geometry should be non indexed
    // We need each face to have its own vertices
    let non_id_geom = new BufferGeometry();
    if (geometry.index) {
      non_id_geom = geometry.toNonIndexed();
      // geometry.toNonIndexed to compute does not copy userData
      // we might write our own toNonIndexed to include it
      non_id_geom.userData = geometry.userData;
    } else {
      console.warn("Mesh Color: input geometry must be indexed, coherence cannot be guarented upon saving and loading otherwise");
      non_id_geom = geometry;
    }
    // Init mesh
    super(new OctreeBufferGeometry().copy(non_id_geom), new MeshColorsMaterial());
    this.geometry.initializeOctree();
    this.internal = new MeshColorInternal();
    // We also need to store the related indexed geometry
    // Having an indexed geometry is necessary to have common edges
    // if geometry is not indexed we compute indexed geometry in intialize function
    if (geometry.index) {
      // TODO do we need to make a copy ?
      // this.internal.indexedGeometry = geometry;
      this.internal.indexedGeometry = new BufferGeometry().copy(geometry);
    }
    // We initialize color data
    this.internal.initialized = false;
    // console.timeEnd("Mesh Color Preconstruct");
  }
}
class MeshColorSkinned extends mixinMeshColor(OctreeMeshSkinned) {
  constructor(geometry) {
    // console.time("Mesh Color Preconstruct");
    // Mesh color geometry should be non indexed
    // We need each face to have its own vertices
    let non_id_geom = new BufferGeometry();
    if (geometry.index) {
      non_id_geom = geometry.toNonIndexed();
      // geometry.toNonIndexed to compute does not copy userData
      // we might write our own toNonIndexed to include it
      non_id_geom.userData = geometry.userData;
    } else {
      console.warn("Mesh Color: input geometry must be indexed, coherence cannot be guarented upon saving and loading otherwise");
      non_id_geom = geometry;
    }
    // Init mesh
    super(new OctreeBufferGeometry().copy(non_id_geom), new MeshColorsMaterial());
    this.geometry.initializeOctree();
    this.internal = new MeshColorInternal();
    // We also need to store the related indexed geometry
    // Having an indexed geometry is necessary to have common edges
    // if geometry is not indexed we compute indexed geometry in intialize function
    if (geometry.index) {
      // TODO do we need to make a copy ?
      // this.internal.indexedGeometry = geometry;
      this.internal.indexedGeometry = new OctreeBufferGeometry().copy(geometry);
    }
    // We initialize color data
    this.internal.initialized = false;
    // console.timeEnd("Mesh Color Preconstruct");
  }

  updateSkinnedGeometryPose() {
    super.updateSkinnedGeometryPose();
    // We need to copy indexed geometry because we need to compute a new pose from rest pose
    // indexed geometry should be left at rest pose
    let indexedGeometryCopy = null;
    if (this.internal.indexedGeometry) {
      indexedGeometryCopy = this.internal.indexedGeometry.clone();
    }
    // TODO we may evaluate what is faster, compute posing for 2 additionnal geometries,
    // or build 2 new geometries (indexed and sample) from non-indexed geometry
    // for now we go with the former
    if (this.morphTargetInfluences) {
      if (indexedGeometryCopy) {
        const indexedMorphedAttributes = BufferGeometryUtils.getMorphedAttributes(indexedGeometryCopy, this.morphTargetInfluences);
        BufferGeometryUtils.setBufferAttributeArray(indexedGeometryCopy, "position", indexedMorphedAttributes.positions);
        BufferGeometryUtils.setBufferAttributeArray(indexedGeometryCopy, "normal", indexedMorphedAttributes.normals);
        // sampleGeometry does not have morph attributes
        // but its position and normals can be computed from update indexedGeometry
        if (this.internal.colorSampleGeometry) {
          this.internal._updateColorSampleGeometry(indexedGeometryCopy);
        }
      }
    }
    if (this.skeleton && this.bindMatrix && this.bindMatrixInverse) {
      if (indexedGeometryCopy) {
        const skinnedAttributes = BufferGeometryUtils.getPosedAttributes(indexedGeometryCopy, this.skeleton, this.bindMatrix, this.bindMatrixInverse);
        BufferGeometryUtils.setBufferAttributeArray(indexedGeometryCopy, "position", skinnedAttributes.positions);
        BufferGeometryUtils.setBufferAttributeArray(indexedGeometryCopy, "normal", skinnedAttributes.normals);
        // sampleGeometry does not have skin attributes
        // but its position and normals can be computed from update indexedGeometry
        if (this.internal.colorSampleGeometry) {
          this.internal._updateColorSampleGeometry(indexedGeometryCopy);
        }
      }
    }
  }
}

// DEFLATE is a complex format; to read this code, you should probably check the RFC first:
// https://tools.ietf.org/html/rfc1951
// You may also wish to take a look at the guide I made about this program:
// https://gist.github.com/101arrowz/253f31eb5abc3d9275ab943003ffecad
// Some of the following code is similar to that of UZIP.js:
// https://github.com/photopea/UZIP.js
// However, the vast majority of the codebase has diverged from UZIP.js to increase performance and reduce bundle size.
// Sometimes 0 will appear where -1 would be more appropriate. This is because using a uint
// is better for memory in most engines (I *think*).

// aliases for shorter compressed code (most minifers don't do this)
const u8 = Uint8Array;
const u16 = Uint16Array;
const i32 = Int32Array;
// fixed length extra bits
const fleb = new u8([0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 0, /* unused */ 0, 0, /* impossible */ 0]);
// fixed distance extra bits
const fdeb = new u8([0, 0, 0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12, 12, 13, 13, /* unused */ 0, 0]);
// code length index map
const clim = new u8([16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15]);
// get base, reverse index map from extra bits
const freb = function (eb, start) {
  const b = new u16(31);
  for (var i = 0; i < 31; ++i) {
    b[i] = start += 1 << eb[i - 1];
  }
  // numbers here are at max 18 bits
  const r = new i32(b[30]);
  for (var i = 1; i < 30; ++i) {
    for (let j = b[i]; j < b[i + 1]; ++j) {
      r[j] = ((j - b[i]) << 5) | i;
    }
  }
  return { b, r };
};
const _a = freb(fleb, 2);
const fl = _a.b;
const revfl = _a.r;
// we can ignore the fact that the other numbers are wrong; they never happen anyway
(fl[28] = 258), (revfl[258] = 28);
const _b = freb(fdeb, 0);
const fd = _b.b;
const revfd = _b.r;
// map of value to reverse (assuming 16 bits)
const rev = new u16(32768);
for (var i = 0; i < 32768; ++i) {
  // reverse table algorithm from SO
  let x = ((i & 0xaaaa) >> 1) | ((i & 0x5555) << 1);
  x = ((x & 0xcccc) >> 2) | ((x & 0x3333) << 2);
  x = ((x & 0xf0f0) >> 4) | ((x & 0x0f0f) << 4);
  rev[i] = (((x & 0xff00) >> 8) | ((x & 0x00ff) << 8)) >> 1;
}
// create huffman tree from u8 "map": index -> code length for code index
// mb (max bits) must be at most 15
// TODO: optimize/split up?
const hMap = function (cd, mb, r) {
  const s = cd.length;
  // index
  let i = 0;
  // u16 "map": index -> # of codes with bit length = index
  const l = new u16(mb);
  // length of cd must be 288 (total # of codes)
  for (; i < s; ++i) {
    if (cd[i]) ++l[cd[i] - 1];
  }
  // u16 "map": index -> minimum code for bit length = index
  const le = new u16(mb);
  for (i = 1; i < mb; ++i) {
    le[i] = (le[i - 1] + l[i - 1]) << 1;
  }
  let co;
  if (r) {
    // u16 "map": index -> number of actual bits, symbol for code
    co = new u16(1 << mb);
    // bits to remove for reverser
    const rvb = 15 - mb;
    for (i = 0; i < s; ++i) {
      // ignore 0 lengths
      if (cd[i]) {
        // num encoding both symbol and bits read
        const sv = (i << 4) | cd[i];
        // free bits
        const r_1 = mb - cd[i];
        // start value
        let v = le[cd[i] - 1]++ << r_1;
        // m is end value
        for (let m = v | ((1 << r_1) - 1); v <= m; ++v) {
          // every 16 bit value starting with the code yields the same result
          co[rev[v] >> rvb] = sv;
        }
      }
    }
  } else {
    co = new u16(s);
    for (i = 0; i < s; ++i) {
      if (cd[i]) {
        co[i] = rev[le[cd[i] - 1]++] >> (15 - cd[i]);
      }
    }
  }
  return co;
};
// fixed length tree
const flt = new u8(288);
for (var i = 0; i < 144; ++i) flt[i] = 8;
for (var i = 144; i < 256; ++i) flt[i] = 9;
for (var i = 256; i < 280; ++i) flt[i] = 7;
for (var i = 280; i < 288; ++i) flt[i] = 8;
// fixed distance tree
const fdt = new u8(32);
for (var i = 0; i < 32; ++i) fdt[i] = 5;
// fixed length map
const flm = /* #__PURE__ */ hMap(flt, 9, 0);
const flrm = /* #__PURE__ */ hMap(flt, 9, 1);
// fixed distance map
const fdm = /* #__PURE__ */ hMap(fdt, 5, 0);
const fdrm = /* #__PURE__ */ hMap(fdt, 5, 1);
// find max of array
const max = function (a) {
  let m = a[0];
  for (let i = 1; i < a.length; ++i) {
    if (a[i] > m) m = a[i];
  }
  return m;
};
// read d, starting at bit p and mask with m
const bits = function (d, p, m) {
  const o = (p / 8) | 0;
  return ((d[o] | (d[o + 1] << 8)) >> (p & 7)) & m;
};
// read d, starting at bit p continuing for at least 16 bits
const bits16 = function (d, p) {
  const o = (p / 8) | 0;
  return (d[o] | (d[o + 1] << 8) | (d[o + 2] << 16)) >> (p & 7);
};
// get end of byte
const shft = function (p) {
  return ((p + 7) / 8) | 0;
};
// typed array slice - allows garbage collector to free original reference,
// while being more compatible than .slice
const slc = function (v, s, e) {
  if (s == null || s < 0) s = 0;
  if (e == null || e > v.length) e = v.length;
  // can't use .constructor in case user-supplied
  return new u8(v.subarray(s, e));
};
// error codes
const ec = [
  "unexpected EOF",
  "invalid block type",
  "invalid length/literal",
  "invalid distance",
  "stream finished",
  "no stream handler",
  ,
  "no callback",
  "invalid UTF-8 data",
  "extra field too long",
  "date not in range 1980-2099",
  "filename too long",
  "stream finishing",
  "invalid zip data"
  // determined by unknown compression method
];
var err = function (ind, msg, nt) {
  const e = new Error(msg || ec[ind]);
  e.code = ind;
  if (Error.captureStackTrace) Error.captureStackTrace(e, err);
  if (!nt) throw e;
  return e;
};
// expands raw DEFLATE data
const inflt = function (dat, st, buf, dict) {
  // source length       dict length
  const sl = dat.length;
  const dl = 0;
  if (!sl || (st.f && !st.l)) return buf || new u8(0);
  const noBuf = !buf;
  // have to estimate size
  const resize = noBuf || st.i != 2;
  // no state
  const noSt = st.i;
  // Assumes roughly 33% compression ratio average
  if (noBuf) buf = new u8(sl * 3);
  // ensure buffer can fit at least l elements
  const cbuf = function (l) {
    const bl = buf.length;
    // need to increase size to fit
    if (l > bl) {
      // Double or set to necessary, whichever is greater
      const nbuf = new u8(Math.max(bl * 2, l));
      nbuf.set(buf);
      buf = nbuf;
    }
  };
  //  last chunk         bitpos           bytes
  let final = st.f || 0;
  let pos = st.p || 0;
  let bt = st.b || 0;
  let lm = st.l;
  let dm = st.d;
  let lbt = st.m;
  let dbt = st.n;
  // total bits
  const tbts = sl * 8;
  do {
    if (!lm) {
      // BFINAL - this is only 1 when last chunk is next
      final = bits(dat, pos, 1);
      // type: 0 = no compression, 1 = fixed huffman, 2 = dynamic huffman
      const type = bits(dat, pos + 1, 3);
      pos += 3;
      if (!type) {
        // go to end of byte boundary
        var s = shft(pos) + 4;
        const l = dat[s - 4] | (dat[s - 3] << 8);
        const t = s + l;
        if (t > sl) {
          if (noSt) err(0);
          break;
        }
        // ensure size
        if (resize) cbuf(bt + l);
        // Copy over uncompressed data
        buf.set(dat.subarray(s, t), bt);
        // Get new bitpos, update byte count
        (st.b = bt += l), (st.p = pos = t * 8), (st.f = final);
        continue;
      } else if (type == 1) (lm = flrm), (dm = fdrm), (lbt = 9), (dbt = 5);
      else if (type == 2) {
        //  literal                            lengths
        const hLit = bits(dat, pos, 31) + 257;
        const hcLen = bits(dat, pos + 10, 15) + 4;
        const tl = hLit + bits(dat, pos + 5, 31) + 1;
        pos += 14;
        // length+distance tree
        const ldt = new u8(tl);
        // code length tree
        const clt = new u8(19);
        for (var i = 0; i < hcLen; ++i) {
          // use index map to get real code
          clt[clim[i]] = bits(dat, pos + i * 3, 7);
        }
        pos += hcLen * 3;
        // code lengths bits
        const clb = max(clt);
        const clbmsk = (1 << clb) - 1;
        // code lengths map
        const clm = hMap(clt, clb, 1);
        for (var i = 0; i < tl; ) {
          const r = clm[bits(dat, pos, clbmsk)];
          // bits read
          pos += r & 15;
          // symbol
          var s = r >> 4;
          // code length to copy
          if (s < 16) {
            ldt[i++] = s;
          } else {
            //  copy   count
            var c = 0;
            let n = 0;
            if (s == 16) (n = 3 + bits(dat, pos, 3)), (pos += 2), (c = ldt[i - 1]);
            else if (s == 17) (n = 3 + bits(dat, pos, 7)), (pos += 3);
            else if (s == 18) (n = 11 + bits(dat, pos, 127)), (pos += 7);
            while (n--) ldt[i++] = c;
          }
        }
        //    length tree                 distance tree
        const lt = ldt.subarray(0, hLit);
        var dt = ldt.subarray(hLit);
        // max length bits
        lbt = max(lt);
        // max dist bits
        dbt = max(dt);
        lm = hMap(lt, lbt, 1);
        dm = hMap(dt, dbt, 1);
      } else err(1);
      if (pos > tbts) {
        if (noSt) err(0);
        break;
      }
    }
    // Make sure the buffer can hold this + the largest possible addition
    // Maximum chunk size (practically, theoretically infinite) is 2^17
    if (resize) cbuf(bt + 131072);
    const lms = (1 << lbt) - 1;
    const dms = (1 << dbt) - 1;
    let lpos = pos;
    for (; ; lpos = pos) {
      // bits read, code
      var c = lm[bits16(dat, pos) & lms];
      const sym = c >> 4;
      pos += c & 15;
      if (pos > tbts) {
        if (noSt) err(0);
        break;
      }
      if (!c) err(2);
      if (sym < 256) buf[bt++] = sym;
      else if (sym == 256) {
        (lpos = pos), (lm = null);
        break;
      } else {
        let add = sym - 254;
        // no extra bits needed if less
        if (sym > 264) {
          // index
          var i = sym - 257;
          var b = fleb[i];
          add = bits(dat, pos, (1 << b) - 1) + fl[i];
          pos += b;
        }
        // dist
        const d = dm[bits16(dat, pos) & dms];
        const dsym = d >> 4;
        if (!d) err(3);
        pos += d & 15;
        var dt = fd[dsym];
        if (dsym > 3) {
          var b = fdeb[dsym];
          (dt += bits16(dat, pos) & ((1 << b) - 1)), (pos += b);
        }
        if (pos > tbts) {
          if (noSt) err(0);
          break;
        }
        if (resize) cbuf(bt + 131072);
        const end = bt + add;
        if (bt < dt) {
          const shift = dl - dt;
          const dend = Math.min(dt, end);
          if (shift + bt < 0) err(3);
          for (; bt < dend; ++bt) buf[bt] = dict[shift + bt];
        }
        for (; bt < end; ++bt) buf[bt] = buf[bt - dt];
      }
    }
    (st.l = lm), (st.p = lpos), (st.b = bt), (st.f = final);
    if (lm) (final = 1), (st.m = lbt), (st.d = dm), (st.n = dbt);
  } while (!final);
  // don't reallocate for streams or user buffers
  return bt != buf.length && noBuf ? slc(buf, 0, bt) : buf.subarray(0, bt);
};
// starting at p, write the minimum number of bits that can hold v to d
const wbits = function (d, p, v) {
  v <<= p & 7;
  const o = (p / 8) | 0;
  d[o] |= v;
  d[o + 1] |= v >> 8;
};
// starting at p, write the minimum number of bits (>8) that can hold v to d
const wbits16 = function (d, p, v) {
  v <<= p & 7;
  const o = (p / 8) | 0;
  d[o] |= v;
  d[o + 1] |= v >> 8;
  d[o + 2] |= v >> 16;
};
// creates code lengths from a frequency table
const hTree = function (d, mb) {
  // Need extra info to make a tree
  const t = [];
  for (var i = 0; i < d.length; ++i) {
    if (d[i]) t.push({ s: i, f: d[i] });
  }
  const s = t.length;
  const t2 = t.slice();
  if (!s) return { t: et, l: 0 };
  if (s == 1) {
    const v = new u8(t[0].s + 1);
    v[t[0].s] = 1;
    return { t: v, l: 1 };
  }
  t.sort((a, b) => a.f - b.f);
  // after i2 reaches last ind, will be stopped
  // freq must be greater than largest possible number of symbols
  t.push({ s: -1, f: 25001 });
  let l = t[0];
  let r = t[1];
  let i0 = 0;
  let i1 = 1;
  let i2 = 2;
  t[0] = { s: -1, f: l.f + r.f, l, r };
  // efficient algorithm from UZIP.js
  // i0 is lookbehind, i2 is lookahead - after processing two low-freq
  // symbols that combined have high freq, will start processing i2 (high-freq,
  // non-composite) symbols instead
  // see https://reddit.com/r/photopea/comments/ikekht/uzipjs_questions/
  while (i1 != s - 1) {
    l = t[t[i0].f < t[i2].f ? i0++ : i2++];
    r = t[i0 != i1 && t[i0].f < t[i2].f ? i0++ : i2++];
    t[i1++] = { s: -1, f: l.f + r.f, l, r };
  }
  let maxSym = t2[0].s;
  for (var i = 1; i < s; ++i) {
    if (t2[i].s > maxSym) maxSym = t2[i].s;
  }
  // code lengths
  const tr = new u16(maxSym + 1);
  // max bits in tree
  let mbt = ln(t[i1 - 1], tr, 0);
  if (mbt > mb) {
    // more algorithms from UZIP.js
    // TODO: find out how this code works (debt)
    //  ind    debt
    var i = 0;
    let dt = 0;
    //    left            cost
    const lft = mbt - mb;
    const cst = 1 << lft;
    t2.sort((a, b) => tr[b.s] - tr[a.s] || a.f - b.f);
    for (; i < s; ++i) {
      const i2_1 = t2[i].s;
      if (tr[i2_1] > mb) {
        dt += cst - (1 << (mbt - tr[i2_1]));
        tr[i2_1] = mb;
      } else break;
    }
    dt >>= lft;
    while (dt > 0) {
      const i2_2 = t2[i].s;
      if (tr[i2_2] < mb) dt -= 1 << (mb - tr[i2_2]++ - 1);
      else ++i;
    }
    for (; i >= 0 && dt; --i) {
      const i2_3 = t2[i].s;
      if (tr[i2_3] == mb) {
        --tr[i2_3];
        ++dt;
      }
    }
    mbt = mb;
  }
  return { t: new u8(tr), l: mbt };
};
// get the max length and assign length codes
var ln = function (n, l, d) {
  return n.s == -1 ? Math.max(ln(n.l, l, d + 1), ln(n.r, l, d + 1)) : (l[n.s] = d);
};
// length codes generation
const lc = function (c) {
  let s = c.length;
  // Note that the semicolon was intentional
  while (s && !c[--s]);
  const cl = new u16(++s);
  //  ind      num         streak
  let cli = 0;
  let cln = c[0];
  let cls = 1;
  const w = function (v) {
    cl[cli++] = v;
  };
  for (let i = 1; i <= s; ++i) {
    if (c[i] == cln && i != s) ++cls;
    else {
      if (!cln && cls > 2) {
        for (; cls > 138; cls -= 138) w(32754);
        if (cls > 2) {
          w(cls > 10 ? ((cls - 11) << 5) | 28690 : ((cls - 3) << 5) | 12305);
          cls = 0;
        }
      } else if (cls > 3) {
        w(cln), --cls;
        for (; cls > 6; cls -= 6) w(8304);
        if (cls > 2) w(((cls - 3) << 5) | 8208), (cls = 0);
      }
      while (cls--) w(cln);
      cls = 1;
      cln = c[i];
    }
  }
  return { c: cl.subarray(0, cli), n: s };
};
// calculate the length of output from tree, code lengths
const clen = function (cf, cl) {
  let l = 0;
  for (let i = 0; i < cl.length; ++i) l += cf[i] * cl[i];
  return l;
};
// writes a fixed block
// returns the new bit pos
const wfblk = function (out, pos, dat) {
  // no need to write 00 as type: TypedArray defaults to 0
  const s = dat.length;
  const o = shft(pos + 2);
  out[o] = s & 255;
  out[o + 1] = s >> 8;
  out[o + 2] = out[o] ^ 255;
  out[o + 3] = out[o + 1] ^ 255;
  for (let i = 0; i < s; ++i) out[o + i + 4] = dat[i];
  return (o + 4 + s) * 8;
};
// writes a block
const wblk = function (dat, out, final, syms, lf, df, eb, li, bs, bl, p) {
  wbits(out, p++, final);
  ++lf[256];
  const _a = hTree(lf, 15);
  const dlt = _a.t;
  const mlb = _a.l;
  const _b = hTree(df, 15);
  const ddt = _b.t;
  const mdb = _b.l;
  const _c = lc(dlt);
  const lclt = _c.c;
  const nlc = _c.n;
  const _d = lc(ddt);
  const lcdt = _d.c;
  const ndc = _d.n;
  const lcfreq = new u16(19);
  for (var i = 0; i < lclt.length; ++i) ++lcfreq[lclt[i] & 31];
  for (var i = 0; i < lcdt.length; ++i) ++lcfreq[lcdt[i] & 31];
  const _e = hTree(lcfreq, 7);
  const lct = _e.t;
  const mlcb = _e.l;
  let nlcc = 19;
  for (; nlcc > 4 && !lct[clim[nlcc - 1]]; --nlcc);
  const flen = (bl + 5) << 3;
  const ftlen = clen(lf, flt) + clen(df, fdt) + eb;
  const dtlen = clen(lf, dlt) + clen(df, ddt) + eb + 14 + 3 * nlcc + clen(lcfreq, lct) + 2 * lcfreq[16] + 3 * lcfreq[17] + 7 * lcfreq[18];
  if (bs >= 0 && flen <= ftlen && flen <= dtlen) return wfblk(out, p, dat.subarray(bs, bs + bl));
  let lm;
  let ll;
  let dm;
  let dl;
  wbits(out, p, 1 + (dtlen < ftlen)), (p += 2);
  if (dtlen < ftlen) {
    (lm = hMap(dlt, mlb, 0)), (ll = dlt), (dm = hMap(ddt, mdb, 0)), (dl = ddt);
    const llm = hMap(lct, mlcb, 0);
    wbits(out, p, nlc - 257);
    wbits(out, p + 5, ndc - 1);
    wbits(out, p + 10, nlcc - 4);
    p += 14;
    for (var i = 0; i < nlcc; ++i) wbits(out, p + 3 * i, lct[clim[i]]);
    p += 3 * nlcc;
    const lcts = [lclt, lcdt];
    for (let it = 0; it < 2; ++it) {
      const clct = lcts[it];
      for (var i = 0; i < clct.length; ++i) {
        var len = clct[i] & 31;
        wbits(out, p, llm[len]), (p += lct[len]);
        if (len > 15) wbits(out, p, (clct[i] >> 5) & 127), (p += clct[i] >> 12);
      }
    }
  } else {
    (lm = flm), (ll = flt), (dm = fdm), (dl = fdt);
  }
  for (var i = 0; i < li; ++i) {
    const sym = syms[i];
    if (sym > 255) {
      var len = (sym >> 18) & 31;
      wbits16(out, p, lm[len + 257]), (p += ll[len + 257]);
      if (len > 7) wbits(out, p, (sym >> 23) & 31), (p += fleb[len]);
      const dst = sym & 31;
      wbits16(out, p, dm[dst]), (p += dl[dst]);
      if (dst > 3) wbits16(out, p, (sym >> 5) & 8191), (p += fdeb[dst]);
    } else {
      wbits16(out, p, lm[sym]), (p += ll[sym]);
    }
  }
  wbits16(out, p, lm[256]);
  return p + ll[256];
};
// deflate options (nice << 13) | chain
const deo = /* #__PURE__ */ new i32([65540, 131080, 131088, 131104, 262176, 1048704, 1048832, 2114560, 2117632]);
// empty
var et = /* #__PURE__ */ new u8(0);
// compresses data into a raw DEFLATE buffer
const dflt = function (dat, lvl, plvl, pre, post, st) {
  const s = st.z || dat.length;
  const o = new u8(pre + s + 5 * (1 + Math.ceil(s / 7000)) + post);
  // writing to this writes to the output buffer
  const w = o.subarray(pre, o.length - post);
  const lst = st.l;
  let pos = (st.r || 0) & 7;
  if (lvl) {
    if (pos) w[0] = st.r >> 3;
    const opt = deo[lvl - 1];
    const n = opt >> 13;
    const c = opt & 8191;
    const msk_1 = (1 << plvl) - 1;
    //    prev 2-byte val map    curr 2-byte val map
    const prev = st.p || new u16(32768);
    const head = st.h || new u16(msk_1 + 1);
    const bs1_1 = Math.ceil(plvl / 3);
    const bs2_1 = 2 * bs1_1;
    const hsh = function (i) {
      return (dat[i] ^ (dat[i + 1] << bs1_1) ^ (dat[i + 2] << bs2_1)) & msk_1;
    };
    // 24576 is an arbitrary number of maximum symbols per block
    // 424 buffer for last block
    const syms = new i32(25000);
    // length/literal freq   distance freq
    const lf = new u16(288);
    const df = new u16(32);
    //  l/lcnt  exbits  index          l/lind  waitdx          blkpos
    let lc_1 = 0;
    let eb = 0;
    var i = st.i || 0;
    let li = 0;
    let wi = st.w || 0;
    let bs = 0;
    for (; i + 2 < s; ++i) {
      // hash value
      const hv = hsh(i);
      // index mod 32768    previous index mod
      let imod = i & 32767;
      let pimod = head[hv];
      prev[imod] = pimod;
      head[hv] = imod;
      // We always should modify head and prev, but only add symbols if
      // this data is not yet processed ("wait" for wait index)
      if (wi <= i) {
        // bytes remaining
        const rem = s - i;
        if ((lc_1 > 7000 || li > 24576) && (rem > 423 || !lst)) {
          pos = wblk(dat, w, 0, syms, lf, df, eb, li, bs, i - bs, pos);
          (li = lc_1 = eb = 0), (bs = i);
          for (var j = 0; j < 286; ++j) lf[j] = 0;
          for (var j = 0; j < 30; ++j) df[j] = 0;
        }
        //  len    dist   chain
        let l = 2;
        let d = 0;
        let ch_1 = c;
        let dif = (imod - pimod) & 32767;
        if (rem > 2 && hv == hsh(i - dif)) {
          const maxn = Math.min(n, rem) - 1;
          const maxd = Math.min(32767, i);
          // max possible length
          // not capped at dif because decompressors implement "rolling" index population
          const ml = Math.min(258, rem);
          while (dif <= maxd && --ch_1 && imod != pimod) {
            if (dat[i + l] == dat[i + l - dif]) {
              let nl = 0;
              for (; nl < ml && dat[i + nl] == dat[i + nl - dif]; ++nl);
              if (nl > l) {
                (l = nl), (d = dif);
                // break out early when we reach "nice" (we are satisfied enough)
                if (nl > maxn) break;
                // now, find the rarest 2-byte sequence within this
                // length of literals and search for that instead.
                // Much faster than just using the start
                const mmd = Math.min(dif, nl - 2);
                let md = 0;
                for (var j = 0; j < mmd; ++j) {
                  const ti = (i - dif + j) & 32767;
                  const pti = prev[ti];
                  const cd = (ti - pti) & 32767;
                  if (cd > md) (md = cd), (pimod = ti);
                }
              }
            }
            // check the previous match
            (imod = pimod), (pimod = prev[imod]);
            dif += (imod - pimod) & 32767;
          }
        }
        // d will be nonzero only when a match was found
        if (d) {
          // store both dist and len data in one int32
          // Make sure this is recognized as a len/dist with 28th bit (2^28)
          syms[li++] = 268435456 | (revfl[l] << 18) | revfd[d];
          const lin = revfl[l] & 31;
          const din = revfd[d] & 31;
          eb += fleb[lin] + fdeb[din];
          ++lf[257 + lin];
          ++df[din];
          wi = i + l;
          ++lc_1;
        } else {
          syms[li++] = dat[i];
          ++lf[dat[i]];
        }
      }
    }
    for (i = Math.max(i, wi); i < s; ++i) {
      syms[li++] = dat[i];
      ++lf[dat[i]];
    }
    pos = wblk(dat, w, lst, syms, lf, df, eb, li, bs, i - bs, pos);
    if (!lst) {
      st.r = (pos & 7) | (w[(pos / 8) | 0] << 3);
      // shft(pos) now 1 less if pos & 7 != 0
      pos -= 7;
      (st.h = head), (st.p = prev), (st.i = i), (st.w = wi);
    }
  } else {
    for (var i = st.w || 0; i < s + lst; i += 65535) {
      // end
      let e = i + 65535;
      if (e >= s) {
        // write final block
        w[(pos / 8) | 0] = lst;
        e = s;
      }
      pos = wfblk(w, pos + 1, dat.subarray(i, e));
    }
    st.i = s;
  }
  return slc(o, 0, pre + shft(pos) + post);
};
// CRC32 table
const crct = /* #__PURE__ */ (function () {
  const t = new Int32Array(256);
  for (let i = 0; i < 256; ++i) {
    let c = i;
    let k = 9;
    while (--k) c = (c & 1 && -306674912) ^ (c >>> 1);
    t[i] = c;
  }
  return t;
})();
// CRC32
const crc = function () {
  let c = -1;
  return {
    p(d) {
      // closures have awful performance
      let cr = c;
      for (let i = 0; i < d.length; ++i) cr = crct[(cr & 255) ^ d[i]] ^ (cr >>> 8);
      c = cr;
    },
    d() {
      return ~c;
    }
  };
};
// deflate with opts
const dopt = function (dat, opt, pre, post, st) {
  if (!st) {
    st = { l: 1 };
    if (opt.dictionary) {
      const dict = opt.dictionary.subarray(-32768);
      const newDat = new u8(dict.length + dat.length);
      newDat.set(dict);
      newDat.set(dat, dict.length);
      dat = newDat;
      st.w = dict.length;
    }
  }
  return dflt(dat, opt.level == null ? 6 : opt.level, opt.mem == null ? (st.l ? Math.ceil(Math.max(8, Math.min(13, Math.log(dat.length))) * 1.5) : 20) : 12 + opt.mem, pre, post, st);
};
// Walmart object spread
const mrg = function (a, b) {
  const o = {};
  for (var k in a) o[k] = a[k];
  for (var k in b) o[k] = b[k];
  return o;
};
// write bytes
const wbytes = function (d, b, v) {
  for (; v; ++b) (d[b] = v), (v >>>= 8);
};
// gzip header
const gzh = function (c, o) {
  const fn = o.filename;
  (c[0] = 31), (c[1] = 139), (c[2] = 8), (c[8] = o.level < 2 ? 4 : o.level == 9 ? 2 : 0), (c[9] = 3); // assume Unix
  if (o.mtime != 0) wbytes(c, 4, Math.floor(new Date(o.mtime || Date.now()) / 1000));
  if (fn) {
    c[3] = 8;
    for (let i = 0; i <= fn.length; ++i) c[i + 10] = fn.charCodeAt(i);
  }
};
// gzip footer: -8 to -4 = CRC, -4 to -0 is length
// gzip start
const gzs = function (d) {
  if (d[0] != 31 || d[1] != 139 || d[2] != 8) err(6, "invalid gzip data");
  const flg = d[3];
  let st = 10;
  if (flg & 4) st += (d[10] | (d[11] << 8)) + 2;
  for (let zs = ((flg >> 3) & 1) + ((flg >> 4) & 1); zs > 0; zs -= !d[st++]);
  return st + (flg & 2);
};
// gzip length
const gzl = function (d) {
  const l = d.length;
  return (d[l - 4] | (d[l - 3] << 8) | (d[l - 2] << 16) | (d[l - 1] << 24)) >>> 0;
};
// gzip header length
const gzhl = function (o) {
  return 10 + (o.filename ? o.filename.length + 1 : 0);
};
/**
 * Compresses data with DEFLATE without any wrapper
 * @param data The data to compress
 * @param opts The compression options
 * @returns The deflated version of the data
 */
function deflateSync(data, opts) {
  return dopt(data, opts || {}, 0, 0);
}
/**
 * Compresses data with GZIP
 * @param data The data to compress
 * @param opts The compression options
 * @returns The gzipped version of the data
 */
function gzipSync(data, opts) {
  if (!opts) opts = {};
  const c = crc();
  const l = data.length;
  c.p(data);
  const d = dopt(data, opts, gzhl(opts), 8);
  const s = d.length;
  return gzh(d, opts), wbytes(d, s - 8, c.d()), wbytes(d, s - 4, l), d;
}
/**
 * Expands GZIP data
 * @param data The data to decompress
 * @param opts The decompression options
 * @returns The decompressed version of the data
 */
function gunzipSync(data, opts) {
  const st = gzs(data);
  if (st + 8 > data.length) err(6, "invalid gzip data");
  return inflt(data.subarray(st, -8), { i: 2 }, new u8(gzl(data)), opts);
}
// flatten a directory structure
var fltn = function (d, p, t, o) {
  for (const k in d) {
    let val = d[k];
    let n = p + k;
    let op = o;
    if (Array.isArray(val)) (op = mrg(o, val[1])), (val = val[0]);
    if (val instanceof u8) t[n] = [val, op];
    else {
      t[(n += "/")] = [new u8(0), op];
      fltn(val, n, t, o);
    }
  }
};
// text encoder
const te = typeof TextEncoder !== "undefined" && /* #__PURE__ */ new TextEncoder();
// text decoder
const td = typeof TextDecoder !== "undefined" && /* #__PURE__ */ new TextDecoder();
// text decoder stream
let tds = 0;
try {
  td.decode(et, { stream: true });
  tds = 1;
} catch (e) {}
// decode UTF8
const dutf8 = function (d) {
  for (let r = "", i = 0; ; ) {
    let c = d[i++];
    const eb = (c > 127) + (c > 223) + (c > 239);
    if (i + eb > d.length) return { s: r, r: slc(d, i - 1) };
    if (!eb) r += String.fromCharCode(c);
    else if (eb == 3) {
      (c = (((c & 15) << 18) | ((d[i++] & 63) << 12) | ((d[i++] & 63) << 6) | (d[i++] & 63)) - 65536), (r += String.fromCharCode(55296 | (c >> 10), 56320 | (c & 1023)));
    } else if (eb & 1) r += String.fromCharCode(((c & 31) << 6) | (d[i++] & 63));
    else r += String.fromCharCode(((c & 15) << 12) | ((d[i++] & 63) << 6) | (d[i++] & 63));
  }
};
/**
 * Converts a string into a Uint8Array for use with compression/decompression methods
 * @param str The string to encode
 * @param latin1 Whether or not to interpret the data as Latin-1. This should
 *               not need to be true unless decoding a binary string.
 * @returns The string encoded in UTF-8/Latin-1 binary
 */
function strToU8(str, latin1) {
  var i;
  if (te) return te.encode(str);
  const l = str.length;
  let ar = new u8(str.length + (str.length >> 1));
  let ai = 0;
  const w = function (v) {
    ar[ai++] = v;
  };
  for (var i = 0; i < l; ++i) {
    if (ai + 5 > ar.length) {
      const n = new u8(ai + 8 + ((l - i) << 1));
      n.set(ar);
      ar = n;
    }
    let c = str.charCodeAt(i);
    if (c < 128 || latin1) w(c);
    else if (c < 2048) w(192 | (c >> 6)), w(128 | (c & 63));
    else if (c > 55295 && c < 57344) {
      (c = (65536 + (c & (1023 << 10))) | (str.charCodeAt(++i) & 1023)), w(240 | (c >> 18)), w(128 | ((c >> 12) & 63)), w(128 | ((c >> 6) & 63)), w(128 | (c & 63));
    } else w(224 | (c >> 12)), w(128 | ((c >> 6) & 63)), w(128 | (c & 63));
  }
  return slc(ar, 0, ai);
}
/**
 * Converts a Uint8Array to a string
 * @param dat The data to decode to string
 * @param latin1 Whether or not to interpret the data as Latin-1. This should
 *               not need to be true unless encoding to binary string.
 * @returns The original UTF-8/Latin-1 string
 */
function strFromU8(dat, latin1) {
  var r;
  if (td) {
    return td.decode(dat);
  }

  const _a = dutf8(dat);
  const { s } = _a;
  var { r } = _a;
  if (r.length) err(8);
  return s;
}
// extra field length
const exfl = function (ex) {
  let le = 0;
  if (ex) {
    for (const k in ex) {
      const l = ex[k].length;
      if (l > 65535) err(9);
      le += l + 4;
    }
  }
  return le;
};
// write zip header
const wzh = function (d, b, f, fn, u, c, ce, co) {
  const fl = fn.length;
  const ex = f.extra;
  const col = co && co.length;
  const exl = exfl(ex);
  wbytes(d, b, ce != null ? 0x2014b50 : 0x4034b50), (b += 4);
  if (ce != null) (d[b++] = 20), (d[b++] = f.os);
  (d[b] = 20), (b += 2); // spec compliance? what's that?
  (d[b++] = (f.flag << 1) | (c < 0 && 8)), (d[b++] = u && 8);
  (d[b++] = f.compression & 255), (d[b++] = f.compression >> 8);
  const dt = new Date(f.mtime == null ? Date.now() : f.mtime);
  const y = dt.getFullYear() - 1980;
  if (y < 0 || y > 119) err(10);
  wbytes(d, b, (y << 25) | ((dt.getMonth() + 1) << 21) | (dt.getDate() << 16) | (dt.getHours() << 11) | (dt.getMinutes() << 5) | (dt.getSeconds() >> 1)), (b += 4);
  if (c != -1) {
    wbytes(d, b, f.crc);
    wbytes(d, b + 4, c < 0 ? -c - 2 : c);
    wbytes(d, b + 8, f.size);
  }
  wbytes(d, b + 12, fl);
  wbytes(d, b + 14, exl), (b += 16);
  if (ce != null) {
    wbytes(d, b, col);
    wbytes(d, b + 6, f.attrs);
    wbytes(d, b + 10, ce), (b += 14);
  }
  d.set(fn, b);
  b += fl;
  if (exl) {
    for (const k in ex) {
      const exf = ex[k];
      const l = exf.length;
      wbytes(d, b, +k);
      wbytes(d, b + 2, l);
      d.set(exf, b + 4), (b += 4 + l);
    }
  }
  if (col) d.set(co, b), (b += col);
  return b;
};
// write zip footer (end of central directory)
const wzf = function (o, b, c, d, e) {
  wbytes(o, b, 0x6054b50); // skip disk
  wbytes(o, b + 8, c);
  wbytes(o, b + 10, c);
  wbytes(o, b + 12, d);
  wbytes(o, b + 16, e);
};
/**
 * Synchronously creates a ZIP file. Prefer using `zip` for better performance
 * with more than one file.
 * @param data The directory structure for the ZIP archive
 * @param opts The main options, merged with per-file options
 * @returns The generated ZIP archive
 */
function zipSync(data, opts) {
  if (!opts) opts = {};
  const r = {};
  const files = [];
  fltn(data, "", r, opts);
  let o = 0;
  let tot = 0;
  for (const fn in r) {
    const _a = r[fn];
    const file = _a[0];
    const p = _a[1];
    const compression = p.level == 0 ? 0 : 8;
    var f = strToU8(fn);
    const s = f.length;
    const com = p.comment;
    const m = com && strToU8(com);
    const ms = m && m.length;
    const exl = exfl(p.extra);
    if (s > 65535) err(11);
    const d = compression ? deflateSync(file, p) : file;
    const l = d.length;
    const c = crc();
    c.p(file);
    files.push(
      mrg(p, {
        size: file.length,
        crc: c.d(),
        c: d,
        f,
        m,
        u: s != fn.length || (m && com.length != ms),
        o,
        compression
      })
    );
    o += 30 + s + exl + l;
    tot += 76 + 2 * (s + exl) + (ms || 0) + l;
  }
  const out = new u8(tot + 22);
  const oe = o;
  const cdl = tot - o;
  for (let i = 0; i < files.length; ++i) {
    var f = files[i];
    wzh(out, f.o, f, f.f, f.u, f.c.length);
    const badd = 30 + f.f.length + exfl(f.extra);
    out.set(f.c, f.o + badd);
    wzh(out, o, f, f.f, f.u, f.c.length, f.o, f.m), (o += 16 + badd + (f.m ? f.m.length : 0));
  }
  wzf(out, o, files.length, cdl, oe);
  return out;
}

// Save/Load data structure constants
const eMetaDataSize = 4;
const fMetaDataSize = 8;
// Version constant
const serializerVersion = 12;
class MeshColorSerializer {
  /**
   * Serialize a BufferGeometry into a structure optimized for fast transfer between workers.
   */
  static toWorkerData(meshColor) {
    const colorSampleGeometryW = meshColor.getColorSampleGeometry();
    if (!colorSampleGeometryW || !meshColor.vertexRelFaces || !meshColor.edgeRelFaces || !meshColor.facePosGeometryMap || !meshColor.edgePosGeometryMap || !meshColor.edgePosColorMap || !meshColor.facePosColorMap) {
      throw "MeshColor toWorkerData: meshColor not initialized";
    }
    const geometry = OctreeBufferGeometrySerializer.toWorkerData(meshColor.geometry);
    const indexedGeometryData = BufferGeometrySerializer.toWorkerData(meshColor.getIndexedGeometry());
    const colorSampleGeometry = OctreeBufferGeometrySerializer.toWorkerData(colorSampleGeometryW);
    const vertexColorBuffer = new Float32Array(4 * meshColor.vertexColors.length);
    for (let i = 0; i < meshColor.vertexColors.length; ++i) {
      vertexColorBuffer[4 * i] = meshColor.vertexColors[i].color.r;
      vertexColorBuffer[4 * i + 1] = meshColor.vertexColors[i].color.g;
      vertexColorBuffer[4 * i + 2] = meshColor.vertexColors[i].color.b;
      vertexColorBuffer[4 * i + 3] = meshColor.vertexColors[i].color.a;
    }
    let edgeColorSampleNb = 0;
    for (let i = 0; i < meshColor.edgeColors.length; ++i) {
      edgeColorSampleNb += meshColor.edgeColors[i].colors.length;
    }
    const edgeColorBuffer = new Float32Array(4 * edgeColorSampleNb);
    const edgeColorMetadataBuffer = new Int32Array(eMetaDataSize * meshColor.edgeColors.length);
    let edgeSampleId = 0;
    for (let i = 0; i < meshColor.edgeColors.length; ++i) {
      edgeColorMetadataBuffer[eMetaDataSize * i] = meshColor.edgeColors[i].R;
      edgeColorMetadataBuffer[eMetaDataSize * i + 1] = meshColor.edgeColors[i].vertex_ids[0];
      edgeColorMetadataBuffer[eMetaDataSize * i + 2] = meshColor.edgeColors[i].vertex_ids[1];
      edgeColorMetadataBuffer[eMetaDataSize * i + 3] = meshColor.edgeColors[i].colors.length;
      for (let j = 0; j < meshColor.edgeColors[i].colors.length; ++j) {
        edgeColorBuffer[edgeSampleId] = meshColor.edgeColors[i].colors[j].r;
        edgeColorBuffer[edgeSampleId + 1] = meshColor.edgeColors[i].colors[j].g;
        edgeColorBuffer[edgeSampleId + 2] = meshColor.edgeColors[i].colors[j].b;
        edgeColorBuffer[edgeSampleId + 3] = meshColor.edgeColors[i].colors[j].a;
        edgeSampleId += 4;
      }
    }
    let faceColorSampleNb = 0;
    for (let i = 0; i < meshColor.faceColors.length; ++i) {
      faceColorSampleNb += meshColor.faceColors[i].colors.length;
    }
    const faceColorBuffer = new Float32Array(4 * faceColorSampleNb);
    const faceColorMetadataBuffer = new Int32Array(fMetaDataSize * meshColor.faceColors.length);
    let faceSampleId = 0;
    for (let i = 0; i < meshColor.faceColors.length; ++i) {
      faceColorMetadataBuffer[fMetaDataSize * i] = meshColor.faceColors[i].R;
      faceColorMetadataBuffer[fMetaDataSize * i + 1] = meshColor.faceColors[i].edge_ids[0];
      faceColorMetadataBuffer[fMetaDataSize * i + 2] = meshColor.faceColors[i].edge_ids[1];
      faceColorMetadataBuffer[fMetaDataSize * i + 3] = meshColor.faceColors[i].edge_ids[2];
      faceColorMetadataBuffer[fMetaDataSize * i + 4] = Number(meshColor.faceColors[i].edge_orientation[0]);
      faceColorMetadataBuffer[fMetaDataSize * i + 5] = Number(meshColor.faceColors[i].edge_orientation[1]);
      faceColorMetadataBuffer[fMetaDataSize * i + 6] = Number(meshColor.faceColors[i].edge_orientation[2]);
      faceColorMetadataBuffer[fMetaDataSize * i + 7] = meshColor.faceColors[i].colors.length;
      for (let j = 0; j < meshColor.faceColors[i].colors.length; ++j) {
        faceColorBuffer[faceSampleId] = meshColor.faceColors[i].colors[j].r;
        faceColorBuffer[faceSampleId + 1] = meshColor.faceColors[i].colors[j].g;
        faceColorBuffer[faceSampleId + 2] = meshColor.faceColors[i].colors[j].b;
        faceColorBuffer[faceSampleId + 3] = meshColor.faceColors[i].colors[j].a;
        faceSampleId += 4;
      }
    }
    const workerData = {
      metadata: {
        type: "MeshColorWorkerData",
        generator: "MeshColorSerializer.toWorkerData"
      },
      data: {
        geometry,
        indexedGeometry: indexedGeometryData,
        colorSampleGeometry,
        initialized: meshColor.initialized,
        marginLength: meshColor.getTextureMarginLength(),
        textureSize: meshColor.getTextureSize(),
        vertexColorBuffer,
        edgeColorBuffer,
        faceColorBuffer,
        edgeColorMetadataBuffer,
        faceColorMetadataBuffer,
        name: meshColor.name,
        isSkinned: meshColor instanceof MeshColorSkinned
      }
    };
    return workerData;
  }

  static vertexColorsFromVertexColorData(vertexColorData, vertexColorLength, extractColors) {
    const vertexColors = new Array(vertexColorLength);
    const colorNormalizationFactor = vertexColorData instanceof Float32Array ? 1.0 : vertexColorData instanceof Uint16Array ? 65536 : vertexColorData instanceof Uint8Array ? 255 : 1.0;
    for (let i = 0; i < vertexColors.length; ++i) {
      vertexColors[i] = { id: -1, color: new MColor() };
      vertexColors[i].id = i;
      if (extractColors) {
        vertexColors[i].color.setRGBA(vertexColorData[4 * i] / colorNormalizationFactor, vertexColorData[4 * i + 1] / colorNormalizationFactor, vertexColorData[4 * i + 2] / colorNormalizationFactor, vertexColorData[4 * i + 3] / colorNormalizationFactor);
      }
    }
    return vertexColors;
  }

  static edgeColorsFromEdgeColorMetaData(edgeColorMedadata) {
    const edgeColors = new Array(edgeColorMedadata.length / eMetaDataSize);
    for (let i = 0; i < edgeColors.length; ++i) {
      edgeColors[i] = { id: -1, R: -1, vertex_ids: [], colors: [] };
      edgeColors[i].id = i;
      edgeColors[i].R = edgeColorMedadata[eMetaDataSize * i];
      edgeColors[i].vertex_ids.push(edgeColorMedadata[eMetaDataSize * i + 1]);
      edgeColors[i].vertex_ids.push(edgeColorMedadata[eMetaDataSize * i + 2]);
      const sampleNb = edgeColorMedadata[eMetaDataSize * i + 3];
      edgeColors[i].colors = new Array(sampleNb);
    }
    return edgeColors;
  }

  static edgeColorsFromEdgeColorData(edgeColorData, edgeColorMedadata, extractColors) {
    const edgeColors = MeshColorSerializer.edgeColorsFromEdgeColorMetaData(edgeColorMedadata);
    const colorNormalizationFactor = edgeColorData instanceof Float32Array ? 1.0 : edgeColorData instanceof Uint16Array ? 65536 : edgeColorData instanceof Uint8Array ? 255 : 1.0;
    let eId = 0;
    for (let i = 0; i < edgeColors.length; ++i) {
      for (let j = 0; j < edgeColors[i].colors.length; j++) {
        edgeColors[i].colors[j] = new MColor();
        if (extractColors) {
          edgeColors[i].colors[j].r = edgeColorData[eId] / colorNormalizationFactor;
          edgeColors[i].colors[j].g = edgeColorData[eId + 1] / colorNormalizationFactor;
          edgeColors[i].colors[j].b = edgeColorData[eId + 2] / colorNormalizationFactor;
          edgeColors[i].colors[j].a = edgeColorData[eId + 3] / colorNormalizationFactor;
          eId += 4;
        }
      }
    }
    return edgeColors;
  }

  static faceColorsFromfaceColorMetaData(faceColorMedadata) {
    const faceColors = new Array(faceColorMedadata.length / fMetaDataSize);
    for (let i = 0; i < faceColors.length; ++i) {
      faceColors[i] = { id: -1, R: -1, edge_ids: [], edge_orientation: [], colors: [] };
      faceColors[i].id = i;
      faceColors[i].R = faceColorMedadata[fMetaDataSize * i];
      faceColors[i].edge_ids.push(faceColorMedadata[fMetaDataSize * i + 1]);
      faceColors[i].edge_ids.push(faceColorMedadata[fMetaDataSize * i + 2]);
      faceColors[i].edge_ids.push(faceColorMedadata[fMetaDataSize * i + 3]);
      faceColors[i].edge_orientation.push(Boolean(faceColorMedadata[fMetaDataSize * i + 4]));
      faceColors[i].edge_orientation.push(Boolean(faceColorMedadata[fMetaDataSize * i + 5]));
      faceColors[i].edge_orientation.push(Boolean(faceColorMedadata[fMetaDataSize * i + 6]));
      const sampleNb = faceColorMedadata[fMetaDataSize * i + 7];
      faceColors[i].colors = new Array(sampleNb);
    }
    return faceColors;
  }

  static faceColorsFromFaceColorData(faceColorData, faceColorMedadata, extractColors) {
    const faceColors = MeshColorSerializer.faceColorsFromfaceColorMetaData(faceColorMedadata);
    const colorNormalizationFactor = faceColorData instanceof Float32Array ? 1.0 : faceColorData instanceof Uint16Array ? 65536 : faceColorData instanceof Uint8Array ? 255 : 1.0;
    let fId = 0;
    for (let i = 0; i < faceColors.length; ++i) {
      for (let j = 0; j < faceColors[i].colors.length; j++) {
        faceColors[i].colors[j] = new MColor();
        if (extractColors) {
          faceColors[i].colors[j].r = faceColorData[fId] / colorNormalizationFactor;
          faceColors[i].colors[j].g = faceColorData[fId + 1] / colorNormalizationFactor;
          faceColors[i].colors[j].b = faceColorData[fId + 2] / colorNormalizationFactor;
          faceColors[i].colors[j].a = faceColorData[fId + 3] / colorNormalizationFactor;
          fId += 4;
        }
      }
    }
    return faceColors;
  }

  static updateVertexColorsFromVertexColorData(meshColor, vertexColorData) {
    const colorNormalizationFactor = vertexColorData instanceof Float32Array ? 1.0 : vertexColorData instanceof Uint16Array ? 65536 : vertexColorData instanceof Uint8Array ? 255 : 1.0;
    for (let i = 0; i < meshColor.vertexColors.length; ++i) {
      meshColor.vertexColors[i].color.setRGBA(vertexColorData[4 * i] / colorNormalizationFactor, vertexColorData[4 * i + 1] / colorNormalizationFactor, vertexColorData[4 * i + 2] / colorNormalizationFactor, vertexColorData[4 * i + 3] / colorNormalizationFactor);
    }
  }

  // called when we load colors for an already initialized meshColor
  static updateEdgeColorsFromEdgeColorData(meshColor, edgeColorData, edgeColorMetaData) {
    if (edgeColorMetaData && edgeColorMetaData.length / eMetaDataSize != meshColor.edgeColors.length) {
      throw "[Load Color Data] tyring to load color data whose edge count mismatches input MeshColor";
    }
    const colorNormalizationFactor = edgeColorData instanceof Float32Array ? 1.0 : edgeColorData instanceof Uint16Array ? 65536 : edgeColorData instanceof Uint8Array ? 255 : 1.0;
    let eId = 0;
    for (let i = 0; i < meshColor.edgeColors.length; ++i) {
      if (edgeColorMetaData && edgeColorMetaData[eMetaDataSize * i] != meshColor.edgeColors[i].R) {
        console.warn("Adapted Edge Color resolution to loaded color data");
        resizeArray(meshColor.edgeColors[i].colors, edgeColorMetaData[eMetaDataSize * i] - 1, () => new MColor());
        meshColor.edgeColors[i].R = edgeColorMetaData[eMetaDataSize * i];
      }
      const sampleNb = meshColor.edgeColors[i].colors.length;
      for (let j = 0; j < sampleNb; j++) {
        meshColor.edgeColors[i].colors[j].r = edgeColorData[eId] / colorNormalizationFactor;
        meshColor.edgeColors[i].colors[j].g = edgeColorData[eId + 1] / colorNormalizationFactor;
        meshColor.edgeColors[i].colors[j].b = edgeColorData[eId + 2] / colorNormalizationFactor;
        meshColor.edgeColors[i].colors[j].a = edgeColorData[eId + 3] / colorNormalizationFactor;
        eId += 4;
      }
    }
  }

  // called when we load colors for an already initialized meshColor
  static updateFaceColorsFromFaceColorData(meshColor, faceColorData, faceColorMetaData) {
    if (faceColorMetaData && faceColorMetaData.length / fMetaDataSize != meshColor.faceColors.length) {
      throw "[Load Color Data] tyring to load color data whose face count mismatches input MeshColor";
    }
    const colorNormalizationFactor = faceColorData instanceof Float32Array ? 1.0 : faceColorData instanceof Uint16Array ? 65536 : faceColorData instanceof Uint8Array ? 255 : 1.0;
    let fId = 0;
    for (let i = 0; i < meshColor.faceColors.length; ++i) {
      if (faceColorMetaData && faceColorMetaData[fMetaDataSize * i] != meshColor.faceColors[i].R) {
        const newFaceR = faceColorMetaData[fMetaDataSize * i];
        console.warn("Adapted Face Color resolution to loaded color data");
        resizeArray(meshColor.faceColors[i].colors, ((newFaceR - 1) * (newFaceR - 2)) / 2, () => new MColor());
        meshColor.faceColors[i].R = newFaceR;
      }
      const sampleNb = meshColor.faceColors[i].colors.length;
      for (let j = 0; j < sampleNb; j++) {
        meshColor.faceColors[i].colors[j].r = faceColorData[fId] / colorNormalizationFactor;
        meshColor.faceColors[i].colors[j].g = faceColorData[fId + 1] / colorNormalizationFactor;
        meshColor.faceColors[i].colors[j].b = faceColorData[fId + 2] / colorNormalizationFactor;
        meshColor.faceColors[i].colors[j].a = faceColorData[fId + 3] / colorNormalizationFactor;
        fId += 4;
      }
    }
  }

  /**
   * Deserialize a new MeshColor from workerData
   */
  static fromWorkerData(workerData) {
    if (workerData.metadata.type != "MeshColorWorkerData") throw "MeshColorSerializer: data is not of type MeshColor";
    const meshColor = workerData.data.isSkinned ? new MeshColorSkinned(OctreeBufferGeometrySerializer.fromWorkerData(workerData.data.geometry)) : new MeshColor(OctreeBufferGeometrySerializer.fromWorkerData(workerData.data.geometry));
    const vertexColorData = new Float32Array(workerData.data.vertexColorBuffer);
    const vertexColors = MeshColorSerializer.vertexColorsFromVertexColorData(vertexColorData, vertexColorData.length / 4, true);
    const edgeColorData = new Float32Array(workerData.data.edgeColorBuffer);
    const edgeColorMedadata = new Int32Array(workerData.data.edgeColorMetadataBuffer);
    const edgeColors = MeshColorSerializer.edgeColorsFromEdgeColorData(edgeColorData, edgeColorMedadata, true);
    const faceColorData = new Float32Array(workerData.data.faceColorBuffer);
    const faceColorMedadata = new Int32Array(workerData.data.faceColorMetadataBuffer);
    const faceColors = MeshColorSerializer.faceColorsFromFaceColorData(faceColorData, faceColorMedadata, true);
    meshColor._fromWorkerData(BufferGeometrySerializer.fromWorkerData(workerData.data.indexedGeometry), OctreeBufferGeometrySerializer.fromWorkerData(workerData.data.colorSampleGeometry), vertexColors, edgeColors, faceColors, workerData.data.marginLength, workerData.data.textureSize);
    meshColor.name = workerData.data.name;
    return meshColor;
  }

  /**
   * Returns all arraybuffers that can be transfered to a worker via shared memory
   */
  static getTransferables(workerData) {
    if (workerData.metadata.type != "MeshColorWorkerData") throw "MeshColorSerializer: data is not of type MeshColor";
    return [...OctreeBufferGeometrySerializer.getTransferables(workerData.data.geometry), ...BufferGeometrySerializer.getTransferables(workerData.data.indexedGeometry), ...OctreeBufferGeometrySerializer.getTransferables(workerData.data.colorSampleGeometry), workerData.data.vertexColorBuffer.buffer, workerData.data.edgeColorBuffer.buffer, workerData.data.faceColorBuffer.buffer, workerData.data.edgeColorMetadataBuffer.buffer, workerData.data.faceColorMetadataBuffer.buffer];
  }

  /**
   * Frees MeshColor data that will not be used anymore
   * Function to call after having workerized a MeshColor to free memory
   * @param meshColor
   */
  static disposeWorkeredMeshColor(meshColor) {
    meshColor._disposeWorkered();
  }

  /**
   * Frees MeshColorWorkerData that will not be used anymore
   * Function to call after having created a MeshColor from workerData to free memory
   * @param workerData
   */
  static disposeWorkerData(workerData) {}

  // Save -----------------------------------------
  static ToColorMetaData(meshColor) {
    const arraysToExport = new Array();
    // Vertex Meta data
    // number of vertex colors
    arraysToExport.push(new Uint8Array(new Uint32Array([meshColor.vertexColors.length]).buffer));
    // Edges Meta data
    // number of edge colors
    const edgeColorMetadataBuffer = new Int32Array(4 * meshColor.edgeColors.length);
    // Edge resolution, vertex ids and number of colors
    for (let i = 0; i < meshColor.edgeColors.length; ++i) {
      edgeColorMetadataBuffer[eMetaDataSize * i] = meshColor.edgeColors[i].R;
      edgeColorMetadataBuffer[eMetaDataSize * i + 1] = meshColor.edgeColors[i].vertex_ids[0];
      edgeColorMetadataBuffer[eMetaDataSize * i + 2] = meshColor.edgeColors[i].vertex_ids[1];
      edgeColorMetadataBuffer[eMetaDataSize * i + 3] = meshColor.edgeColors[i].colors.length;
    }
    arraysToExport.push(new Uint8Array(new Uint32Array([edgeColorMetadataBuffer.length]).buffer));
    arraysToExport.push(new Uint8Array(edgeColorMetadataBuffer.buffer));
    // Face Meta data
    const faceColorMetadataBuffer = new Int32Array(fMetaDataSize * meshColor.faceColors.length);
    // Face resolution, edge ids, edge orientation, number of colors
    for (let i = 0; i < meshColor.faceColors.length; ++i) {
      faceColorMetadataBuffer[fMetaDataSize * i] = meshColor.faceColors[i].R;
      faceColorMetadataBuffer[fMetaDataSize * i + 1] = meshColor.faceColors[i].edge_ids[0];
      faceColorMetadataBuffer[fMetaDataSize * i + 2] = meshColor.faceColors[i].edge_ids[1];
      faceColorMetadataBuffer[fMetaDataSize * i + 3] = meshColor.faceColors[i].edge_ids[2];
      faceColorMetadataBuffer[fMetaDataSize * i + 4] = Number(meshColor.faceColors[i].edge_orientation[0]);
      faceColorMetadataBuffer[fMetaDataSize * i + 5] = Number(meshColor.faceColors[i].edge_orientation[1]);
      faceColorMetadataBuffer[fMetaDataSize * i + 6] = Number(meshColor.faceColors[i].edge_orientation[2]);
      faceColorMetadataBuffer[fMetaDataSize * i + 7] = meshColor.faceColors[i].colors.length;
    }
    arraysToExport.push(new Uint8Array(new Uint32Array([faceColorMetadataBuffer.length]).buffer));
    arraysToExport.push(new Uint8Array(faceColorMetadataBuffer.buffer));
    return Uint8ArrayArrayToUint8Array(arraysToExport);
  }

  static ToColorData(meshColor, colorFormat = "float") {
    const arraysToExport = new Array();
    const colorNormalizationFactor = colorFormat == "float" ? 1.0 : colorFormat == "short" ? 65536 : colorFormat == "byte" ? 255 : 1.0;
    arraysToExport.push(new Uint8Array([colorFormat == "float" ? 2 : colorFormat == "short" ? 1 : colorFormat == "byte" ? 0 : 2]));
    arraysToExport.push(new Uint8Array(new Uint32Array([meshColor.vertexColors.length]).buffer));
    const vertexColorBuffer = colorFormat == "float" ? new Float32Array(4 * meshColor.vertexColors.length) : colorFormat == "short" ? new Uint16Array(4 * meshColor.vertexColors.length) : colorFormat == "byte" ? new Uint8Array(4 * meshColor.vertexColors.length) : new Float32Array(4 * meshColor.vertexColors.length);
    for (let i = 0; i < meshColor.vertexColors.length; ++i) {
      if (colorFormat != "float") {
        vertexColorBuffer[4 * i] = Math.max(0, Math.min(colorNormalizationFactor, meshColor.vertexColors[i].color.r * colorNormalizationFactor));
        vertexColorBuffer[4 * i + 1] = Math.max(0, Math.min(colorNormalizationFactor, meshColor.vertexColors[i].color.g * colorNormalizationFactor));
        vertexColorBuffer[4 * i + 2] = Math.max(0, Math.min(colorNormalizationFactor, meshColor.vertexColors[i].color.b * colorNormalizationFactor));
        vertexColorBuffer[4 * i + 3] = Math.max(0, Math.min(colorNormalizationFactor, meshColor.vertexColors[i].color.a * colorNormalizationFactor));
      } else {
        vertexColorBuffer[4 * i] = meshColor.vertexColors[i].color.r * colorNormalizationFactor;
        vertexColorBuffer[4 * i + 1] = meshColor.vertexColors[i].color.g * colorNormalizationFactor;
        vertexColorBuffer[4 * i + 2] = meshColor.vertexColors[i].color.b * colorNormalizationFactor;
        vertexColorBuffer[4 * i + 3] = meshColor.vertexColors[i].color.a * colorNormalizationFactor;
      }
    }
    arraysToExport.push(new Uint8Array(vertexColorBuffer.buffer));
    // Edges data
    let edgeColorSampleNb = 0;
    for (let i = 0; i < meshColor.edgeColors.length; ++i) {
      edgeColorSampleNb += meshColor.edgeColors[i].colors.length;
    }
    const edgeColorBuffer = colorFormat == "float" ? new Float32Array(4 * edgeColorSampleNb) : colorFormat == "short" ? new Uint16Array(4 * edgeColorSampleNb) : colorFormat == "byte" ? new Uint8Array(4 * edgeColorSampleNb) : new Float32Array(4 * edgeColorSampleNb);
    let edgeColorSampleId = 0;
    for (let i = 0; i < meshColor.edgeColors.length; ++i) {
      for (let j = 0; j < meshColor.edgeColors[i].colors.length; ++j) {
        if (colorFormat != "float") {
          edgeColorBuffer[edgeColorSampleId] = Math.max(0, Math.min(colorNormalizationFactor, meshColor.edgeColors[i].colors[j].r * colorNormalizationFactor));
          edgeColorBuffer[edgeColorSampleId + 1] = Math.max(0, Math.min(colorNormalizationFactor, meshColor.edgeColors[i].colors[j].g * colorNormalizationFactor));
          edgeColorBuffer[edgeColorSampleId + 2] = Math.max(0, Math.min(colorNormalizationFactor, meshColor.edgeColors[i].colors[j].b * colorNormalizationFactor));
          edgeColorBuffer[edgeColorSampleId + 3] = Math.max(0, Math.min(colorNormalizationFactor, meshColor.edgeColors[i].colors[j].a * colorNormalizationFactor));
        } else {
          edgeColorBuffer[edgeColorSampleId] = meshColor.edgeColors[i].colors[j].r * colorNormalizationFactor;
          edgeColorBuffer[edgeColorSampleId + 1] = meshColor.edgeColors[i].colors[j].g * colorNormalizationFactor;
          edgeColorBuffer[edgeColorSampleId + 2] = meshColor.edgeColors[i].colors[j].b * colorNormalizationFactor;
          edgeColorBuffer[edgeColorSampleId + 3] = meshColor.edgeColors[i].colors[j].a * colorNormalizationFactor;
        }
        edgeColorSampleId += 4;
      }
    }
    arraysToExport.push(new Uint8Array(new Uint32Array([edgeColorBuffer.length]).buffer));
    arraysToExport.push(new Uint8Array(edgeColorBuffer.buffer));
    // Face data
    let faceColorSampleNb = 0;
    for (let i = 0; i < meshColor.faceColors.length; ++i) {
      faceColorSampleNb += meshColor.faceColors[i].colors.length;
    }
    const faceColorBuffer = colorFormat == "float" ? new Float32Array(4 * faceColorSampleNb) : colorFormat == "short" ? new Uint16Array(4 * faceColorSampleNb) : colorFormat == "byte" ? new Uint8Array(4 * faceColorSampleNb) : new Float32Array(4 * faceColorSampleNb);
    let faceColorSampleId = 0;
    for (let i = 0; i < meshColor.faceColors.length; ++i) {
      for (let j = 0; j < meshColor.faceColors[i].colors.length; ++j) {
        if (colorFormat != "float") {
          faceColorBuffer[faceColorSampleId] = Math.max(0, Math.min(colorNormalizationFactor, meshColor.faceColors[i].colors[j].r * colorNormalizationFactor));
          faceColorBuffer[faceColorSampleId + 1] = Math.max(0, Math.min(colorNormalizationFactor, meshColor.faceColors[i].colors[j].g * colorNormalizationFactor));
          faceColorBuffer[faceColorSampleId + 2] = Math.max(0, Math.min(colorNormalizationFactor, meshColor.faceColors[i].colors[j].b * colorNormalizationFactor));
          faceColorBuffer[faceColorSampleId + 3] = Math.max(0, Math.min(colorNormalizationFactor, meshColor.faceColors[i].colors[j].a * colorNormalizationFactor));
        } else {
          faceColorBuffer[faceColorSampleId] = meshColor.faceColors[i].colors[j].r * colorNormalizationFactor;
          faceColorBuffer[faceColorSampleId + 1] = meshColor.faceColors[i].colors[j].g * colorNormalizationFactor;
          faceColorBuffer[faceColorSampleId + 2] = meshColor.faceColors[i].colors[j].b * colorNormalizationFactor;
          faceColorBuffer[faceColorSampleId + 3] = meshColor.faceColors[i].colors[j].a * colorNormalizationFactor;
        }
        faceColorSampleId += 4;
      }
    }
    arraysToExport.push(new Uint8Array(new Uint32Array([faceColorBuffer.length]).buffer));
    arraysToExport.push(new Uint8Array(faceColorBuffer.buffer));
    return Uint8ArrayArrayToUint8Array(arraysToExport);
  }

  // Saves Mesh Color internal data needed for building MeshColor relations (topological) structures
  static ToInternalData(meshColor, saveColorData = false, colorFormat = "float") {
    const arraysToExport = new Array();
    const version = new Uint8Array([serializerVersion]);
    arraysToExport.push(version);
    // let edgeColorStep = new Uint8Array((new Float32Array([meshColor.edge_color_step])).buffer);
    // arraysToExport.push(edgeColorStep);
    const initialized = new Uint8Array(1);
    initialized[0] = meshColor.initialized ? 1 : 0;
    arraysToExport.push(initialized);
    const textureMarginLength = new Uint8Array(new Uint32Array([meshColor.getTextureMarginLength()]).buffer);
    arraysToExport.push(textureMarginLength);
    const textureSize = new Uint8Array(new Uint32Array([meshColor.getTextureSize()]).buffer);
    arraysToExport.push(textureSize);
    const saveColorDataArray = new Uint8Array(1);
    saveColorDataArray[0] = saveColorData ? 1 : 0;
    arraysToExport.push(saveColorDataArray);
    arraysToExport.push(MeshColorSerializer.ToColorMetaData(meshColor));
    if (saveColorData) {
      const meshColorData = MeshColorSerializer.ToColorData(meshColor, colorFormat);
      arraysToExport.push(new Uint8Array(new Uint32Array([meshColorData.length]).buffer));
      arraysToExport.push(meshColorData);
    }
    const exportArray = Uint8ArrayArrayToUint8Array(arraysToExport);
    return exportArray;
  }

  static async ToSaveData(
    meshColor,
    saveOptions = {
      saveColorData: false,
      colorFormat: "float",
      prepareDracoCompression: false
    }
  ) {
    const exporter = new GLTFExporter();
    // Model exports
    const modelParse = (object) =>
      new Promise((resolve, reject) => {
        exporter.parse(
          object,
          (result) => {
            resolve(result);
          },
          { binary: true }
        );
      });
    const indexedGeometry = meshColor.getIndexedGeometry();
    const colorSampleGeometry = meshColor.getColorSampleGeometry();
    if (!indexedGeometry) throw "[MeshColor] : ToSaveData: missing indexedGeometry";
    if (!colorSampleGeometry) throw "[MeshColor] : ToSaveData: missing colorSampleGeometry";
    const exportIndexedGeometry = indexedGeometry.clone();
    const exportColorSampleGeometry = colorSampleGeometry.clone();
    const indexedGeometryArray = await modelParse(new Mesh(exportIndexedGeometry));
    const colorSampleGeometryArray = await modelParse(new Mesh(exportColorSampleGeometry));
    const zip = {
      indexedGeometryMesh: new Uint8Array(indexedGeometryArray),
      colorSampleGeometryMesh: new Uint8Array(colorSampleGeometryArray),
      internalData: gzipSync(MeshColorSerializer.ToInternalData(meshColor, saveOptions.saveColorData, saveOptions.colorFormat))
    };
    return zip;
  }

  //-------------------------------------------------------------
  // LOAD -------------------------------------------------------
  static _loadColorMetaData(colorMetaData, currentOffset = 0) {
    let currOffset = currentOffset;
    const view = new DataView(colorMetaData.buffer);
    const vertexColorBufferLength = view.getUint32(currOffset, true);
    currOffset += 4;
    const edgeColorMetadataBufferLength = view.getUint32(currOffset, true);
    currOffset += 4;
    const edgeColorMetadata = new Int32Array(colorMetaData.slice(currOffset, currOffset + 4 * edgeColorMetadataBufferLength).buffer);
    currOffset += 4 * edgeColorMetadataBufferLength;
    const faceColorMetadataBufferLength = view.getUint32(currOffset, true);
    currOffset += 4;
    const faceColorMetadata = new Int32Array(colorMetaData.slice(currOffset, currOffset + 4 * faceColorMetadataBufferLength).buffer);
    currOffset += 4 * faceColorMetadataBufferLength;
    return {
      currOffset,
      vertexColorBufferLength,
      edgeColorMetadata,
      faceColorMetadata
    };
  }

  static _loadColorData(colorData) {
    let currOffset = 0;
    const view = new DataView(colorData.buffer);
    const colorFormat = view.getUint8(currOffset); // 0 byte, 1 short, 2 float
    currOffset += 1;
    let colorFormatSize;
    switch (colorFormat) {
      case 0:
        colorFormatSize = 1;
        break;
      case 1:
        colorFormatSize = 2;
        break;
      case 2:
        colorFormatSize = 4;
        break;
      default:
        colorFormatSize = 4;
        break;
    }
    // number of vertex colors
    const vertexColorBufferLength = view.getUint32(currOffset, true);
    currOffset += 4;
    let vertexColorData;
    // vertex color buffer has no meta data thus the true size of color data is 4*vertex_color_buffer_length
    // we multiply by n depending on the number of bytes stored for each color (4 for float32 for instance)
    switch (colorFormat) {
      case 0:
        vertexColorData = new Uint8Array(colorData.slice(currOffset, currOffset + 4 * vertexColorBufferLength * colorFormatSize).buffer);
        break;
      case 1:
        vertexColorData = new Uint16Array(colorData.slice(currOffset, currOffset + 4 * vertexColorBufferLength * colorFormatSize).buffer);
        break;
      case 2:
        vertexColorData = new Float32Array(colorData.slice(currOffset, currOffset + 4 * vertexColorBufferLength * colorFormatSize).buffer);
        break;
      default:
        vertexColorData = new Float32Array(colorData.slice(currOffset, currOffset + 4 * vertexColorBufferLength * colorFormatSize).buffer);
        break;
    }
    currOffset += 4 * vertexColorBufferLength * colorFormatSize;
    const edgeColorBufferLength = view.getUint32(currOffset, true);
    currOffset += 4;
    let edgeColorData;
    switch (colorFormat) {
      case 0:
        edgeColorData = new Uint8Array(colorData.slice(currOffset, currOffset + edgeColorBufferLength * colorFormatSize).buffer);
        break;
      case 1:
        edgeColorData = new Uint16Array(colorData.slice(currOffset, currOffset + edgeColorBufferLength * colorFormatSize).buffer);
        break;
      case 2:
        edgeColorData = new Float32Array(colorData.slice(currOffset, currOffset + edgeColorBufferLength * colorFormatSize).buffer);
        break;
      default:
        edgeColorData = new Float32Array(colorData.slice(currOffset, currOffset + edgeColorBufferLength * colorFormatSize).buffer);
        break;
    }
    currOffset += edgeColorBufferLength * colorFormatSize;
    const faceColorBufferLength = view.getUint32(currOffset, true);
    currOffset += 4;
    let faceColorData;
    switch (colorFormat) {
      case 0:
        faceColorData = new Uint8Array(colorData.slice(currOffset, currOffset + faceColorBufferLength * colorFormatSize).buffer);
        break;
      case 1:
        faceColorData = new Uint16Array(colorData.slice(currOffset, currOffset + faceColorBufferLength * colorFormatSize).buffer);
        break;
      case 2:
        faceColorData = new Float32Array(colorData.slice(currOffset, currOffset + faceColorBufferLength * colorFormatSize).buffer);
        break;
      default:
        faceColorData = new Float32Array(colorData.slice(currOffset, currOffset + faceColorBufferLength * colorFormatSize).buffer);
        break;
    }
    return {
      vertexColorData,
      edgeColorData,
      faceColorData
    };
  }

  // loads color data only
  static loadColorData(meshColor, colorData, colorMetaData = undefined, onLoaded = () => {}) {
    const data = MeshColorSerializer._loadColorData(colorData);
    let metaData = null;
    if (colorMetaData) metaData = MeshColorSerializer._loadColorMetaData(colorMetaData);
    MeshColorSerializer.updateVertexColorsFromVertexColorData(meshColor, data.vertexColorData);
    MeshColorSerializer.updateEdgeColorsFromEdgeColorData(meshColor, data.edgeColorData, metaData?.edgeColorMetadata);
    MeshColorSerializer.updateFaceColorsFromFaceColorData(meshColor, data.faceColorData, metaData?.faceColorMetadata);
    // re generate meshColor geometry sample data
    if (colorMetaData) {
      meshColor._generateSampleGeometryAndComputedProperties();
    }
    onLoaded();
  }

  // This function needs a DRACOLoader in case MeshColor Geometries have been compressed with Draco
  // (see ToSaveData functions)
  // To instanciate a DRACOLOader you should :
  //    - create a new instance with const dracoLoader = new DRACOLoader();
  //    - set its decoderPath with the libraries provided by Three JS (mendatory to work with GLTF Loader)
  //          - dracoLoader.setDecoderPath(PATH_TO_LIB_FOLDER);
  //          - with PATH_TO_LIB_FOLDER = three/examples/js/libs/draco as of Three JS version 130
  static async FromSavedData(zipData, dracoLoader) {
    const unzippedFileData = zipData;
    const loader = new GLTFLoader();
    if (dracoLoader) {
      // We use draco compression
      // if decoderPath is equal to "./" it probably has not been set
      if (dracoLoader.decoderPath === "./") {
        console.warn("DracoLoader decoder path might be uncorrectly set");
      }
      loader.setDRACOLoader(dracoLoader);
    }
    let indexedGeometry = null;
    let colorSampleGeometry = null;
    const modelParse = (arraybuffer, setter) =>
      new Promise((resolve, reject) => {
        loader.parse(arraybuffer, "", (result) => {
          setter(result);
          resolve(result);
        });
      });
    await modelParse(unzippedFileData.indexedGeometryMesh.buffer, (result) => {
      indexedGeometry = new OctreeBufferGeometry().copy(result.scene.children[0].geometry);
    });
    if (!indexedGeometry) return null;
    await modelParse(unzippedFileData.colorSampleGeometryMesh.buffer, (result) => {
      colorSampleGeometry = new OctreeBufferGeometry().copy(result.scene.children[0].geometry);
    });
    if (!colorSampleGeometry) return null;
    // Remove unecessaray index if draco compressed
    if (dracoLoader) {
      colorSampleGeometry.setIndex(null);
    }
    colorSampleGeometry.initializeOctree();
    const internalData = gunzipSync(unzippedFileData.internalData);
    const view = new DataView(internalData.buffer);
    let currOffset = 0;
    const version = view.getUint8(currOffset);
    currOffset += 1;
    if (version != serializerVersion) {
      console.error(`Mesh Color data has been written with version ${version.toString()}Current version is ${serializerVersion.toString()}`);
    }
    view.getFloat32(currOffset, true);
    currOffset += 4;
    view.getUint8(currOffset);
    currOffset += 1;
    const textureMarginLength = view.getUint32(currOffset, true);
    currOffset += 4;
    const textureSize = view.getUint32(currOffset, true);
    currOffset += 4;
    const savedColorData = view.getUint8(currOffset);
    currOffset += 1;
    const metaData = MeshColorSerializer._loadColorMetaData(internalData, currOffset);
    currOffset = metaData.currOffset;
    const { vertexColorBufferLength } = metaData;
    const { edgeColorMetadata } = metaData;
    const { faceColorMetadata } = metaData;
    let vertexColorData;
    let edgeColorData;
    let faceColorData;
    if (savedColorData) {
      const color_data_buffer_length = view.getUint32(currOffset, true);
      currOffset += 4;
      const colorData = MeshColorSerializer._loadColorData(internalData.slice(currOffset, currOffset + color_data_buffer_length));
      currOffset += color_data_buffer_length;
      vertexColorData = colorData.vertexColorData;
      edgeColorData = colorData.edgeColorData;
      faceColorData = colorData.faceColorData;
    } else {
      vertexColorData = new Uint8Array();
      edgeColorData = new Uint8Array();
      faceColorData = new Uint8Array();
    }
    const vertexColors = MeshColorSerializer.vertexColorsFromVertexColorData(vertexColorData, vertexColorBufferLength, Boolean(savedColorData));
    const edgeColors = MeshColorSerializer.edgeColorsFromEdgeColorData(edgeColorData, edgeColorMetadata, Boolean(savedColorData));
    const faceColors = MeshColorSerializer.faceColorsFromFaceColorData(faceColorData, faceColorMetadata, Boolean(savedColorData));
    const meshColor = indexedGeometry.hasAttribute("skinWeight") ? new MeshColorSkinned(indexedGeometry) : new MeshColor(indexedGeometry);
    meshColor._fromWorkerData(indexedGeometry, colorSampleGeometry, vertexColors, edgeColors, faceColors, textureMarginLength, textureSize);
    return meshColor;
  }
}

// import { MeshColorSerializer } from "../meshes/MeshColor.util"
// import { dracoProcessGlb } from "./Utils";
// import { MeshColorTextureSerializer } from "../textures/MeshColorTexture.util"
// import { zipSync } from "fflate";
function getFaceColorSample(face_colors, resolution, i, j) {
  let currR = resolution - 2;
  let currId = 0;
  for (let k = 1; k < j; ++k) {
    currId += currR;
    currR--;
  }
  currId += i - 1;
  return face_colors[currId];
}
function mergeMeshColorAndTextures() {}
/**
 *
 * @param geometry The geometry we want to texture using Mesh Colors
 * @param edge_color_step The maximum distance between 2 points in the resulting Mesh Colors structure
 *                        (in world unit, ideally mm if the model is for 3D printing)
 * @returns
 */
function estimateMeshColorPointResolution(geometry, edge_color_step) {
  let non_id_geom = new BufferGeometry();
  if (geometry.index) {
    non_id_geom = geometry.toNonIndexed();
  } else {
    non_id_geom = geometry;
  }
  const positionAttribute = non_id_geom.getAttribute("position");
  const point_1 = new Vector3();
  const point_2 = new Vector3();
  const point_3 = new Vector3();
  const triangle = new Triangle();
  const points = {
    vertex_points: 0,
    edge_points: 0,
    face_points: 0
  };
  if (geometry.index) {
    const indexedPositionAttribute = non_id_geom.getAttribute("position");
    points.vertex_points += indexedPositionAttribute.count;
  } else {
    // Overestimation in case of non indexed geometry
    // We could merge vertices in a future version but that will add an additionnal cost
    points.vertex_points += positionAttribute.count;
  }
  for (let i = 0; i < positionAttribute.count; i += 3) {
    point_1.fromBufferAttribute(positionAttribute, i);
    point_2.fromBufferAttribute(positionAttribute, i + 1);
    point_3.fromBufferAttribute(positionAttribute, i + 2);
    triangle.set(point_1, point_2, point_3);
    const edge_1_dist = point_1.distanceTo(point_2);
    const edge_2_dist = point_2.distanceTo(point_3);
    const edge_3_dist = point_3.distanceTo(point_1);
    const max_edge_dist = Math.max(edge_1_dist, edge_2_dist, edge_3_dist);
    if (max_edge_dist > edge_color_step) {
      const resolution = Math.ceil(max_edge_dist / edge_color_step);
      points.edge_points += 3 * (resolution - 1);
      points.face_points += Math.max(0, ((resolution - 2) * (resolution - 1)) / 2);
    }
  }
  return points;
}
function estimateMeshColorTextureResolution(points, margin_length = 0) {
  let total_points = points.vertex_points + margin_length * 3 * points.vertex_points;
  total_points += points.edge_points + margin_length * points.edge_points;
  total_points += points.face_points;
  return Math.sqrt(total_points) * 2;
}
function getMeshColorFacesFromVertexSelection(meshColor, vertexSelection) {
  const selectionFaceRel = new Set();
  for (const pt of vertexSelection) {
    const vertexColor = meshColor.getVertexColor(pt.index);
    let edgeColor = null;
    let faceColor = null;
    if (vertexColor) {
      const vertexRelativeFaces = meshColor.getVertexRelativeFaces(pt.index);
      if (!vertexRelativeFaces) continue;
      for (const face of vertexRelativeFaces) {
        selectionFaceRel.add(face);
      }
    } else if ((edgeColor = meshColor.getEdgeColor(pt.index))) {
      const edgeRelativeFaces = meshColor.getEdgeRelativeFaces(edgeColor.id);
      if (!edgeRelativeFaces) continue;
      for (const face of edgeRelativeFaces) {
        selectionFaceRel.add(face);
      }
    } else if ((faceColor = meshColor.getFaceColor(pt.index))) {
      selectionFaceRel.add(faceColor);
    }
  }
  return Array.from(selectionFaceRel);
}
/* export async function zipExportMeshColors(meshesColor: MeshColor[],
    relatedMeshColorTextures: Map<MeshColor,Map<string,MeshColorTexture>>
)
{
    console.time("zip Mesh Colors");
    let zipDatas = []

    for(let i = 0 ; i < meshesColor.length ; ++i)
    {
        let meshColorArrayDatas = await MeshColorSerializer.saveMeshColorDataToZipData(meshesColor[i],true,true);
        meshColorArrayDatas.indexedGeometryMesh = await dracoProcessGlb(meshColorArrayDatas.indexedGeometryMesh);
        meshColorArrayDatas.colorSampleGeometryMesh = await dracoProcessGlb(meshColorArrayDatas.colorSampleGeometryMesh);

        let texturesToZip = {}

        if(relatedMeshColorTextures.has(meshesColor[i]))
        {
            let relatedTextures = relatedMeshColorTextures.get(meshesColor[i])!;
            for(let [key, texture] of relatedTextures)
            {
                texturesToZip[key] = await MeshColorTextureSerializer.saveMeshColorTextureDataToArrayData(texture);
            }
        }

        let meshColorTextureZip = {...{mesh:zipSync(meshColorArrayDatas)},
        ...{textures:zipSync(texturesToZip)}};

        zipDatas.push(zipSync(meshColorTextureZip));
    }

    console.timeEnd("zip Mesh Colors");
    let modelZip = {}
    for(let i = 0; i < zipDatas.length; ++i)
    {
        modelZip["vortex"+i.toString()+".zip"] = zipDatas[i];
    }

    return modelZip;
} */

class MeshColorTexture {
  #colorTexture;

  #meshColor;

  #data;

  #textureSize;

  #dataSize;

  #textureOptions;

  #texNormalizationValue;

  // This variable is used when mesh colors are updated
  // it contains faces whose texels should be recomputed
  #faceUpdateStack;

  // Variable used for optimisation purposes
  #tmpInterColor;

  #tmpDeltaInterColor;

  #tmpFaceColors;

  #tmpOtherFaceColors;

  #tmpSampledVertices;

  #tmpSampledEdges;

  #tmpCurrentSampler;

  #createColor = function () {
    return new MColor();
  };

  textureReallocated;

  constructor(
    meshColor,
    textureOptions = {
      maxTextureSize: 8192,
      marginLength: 0,
      type: MeshColorTextureType.Float,
      format: MeshColorTextureFormat.RGBA
    }
  ) {
    this.#tmpInterColor = new MColor();
    this.#tmpDeltaInterColor = new MColor();
    this.#tmpFaceColors = new Array();
    this.#tmpOtherFaceColors = new Array();
    this.#tmpSampledVertices = new Array();
    this.#tmpSampledEdges = new Array();
    this.#tmpCurrentSampler = meshColor.getColorSampler();
    this.#meshColor = meshColor;
    this.#faceUpdateStack = new Set();
    this.#textureOptions = textureOptions;
    this.#texNormalizationValue = 1.0; // 1.0 for float, 255 for U8 and 65536 for U16
    this.#textureSize = 0;
    this.#dataSize = this.#textureOptions.format === MeshColorTextureFormat.RGBA ? 4 : 1; // This is constant for now, it is very likely to stay constant
    this.#colorTexture = null;
    this.#data = null;
    this.textureReallocated = new Signal();
    this.#meshColor.samplesResolutionChanged.on(() => {
      this.allocateData();
      this.initTmpVariables();
      this.#colorTexture = new DataTexture(this.#data, this.#textureSize, this.#textureSize, this.#textureOptions.format === MeshColorTextureFormat.RGBA ? RGBAFormat : RedFormat, this.getTextureType());
      if (FloatTextureLinearFilterSupport()) {
        this.#colorTexture.magFilter = LinearFilter;
        this.#colorTexture.minFilter = LinearFilter;
      }
      this.textureReallocated.emit();
    });
  }

  allocateData() {
    this.#textureSize = this.#meshColor.getTextureSize();
    switch (this.#textureOptions.type) {
      case MeshColorTextureType.Float: {
        this.#data = new Float32Array(this.#dataSize * this.#textureSize * this.#textureSize);
        break;
      }
      case MeshColorTextureType.Short: {
        this.#data = new Uint16Array(this.#dataSize * this.#textureSize * this.#textureSize);
        break;
      }
      case MeshColorTextureType.Byte: {
        this.#data = new Uint8Array(this.#dataSize * this.#textureSize * this.#textureSize);
        break;
      }
      default: {
        this.#data = new Float32Array(this.#dataSize * this.#textureSize * this.#textureSize);
        break;
      }
    }
    this.#data.fill(0.0);
  }

  getTextureType() {
    return this.#textureOptions.type === MeshColorTextureType.Float ? FloatType : this.#textureOptions.type === MeshColorTextureType.Short ? UnsignedIntType : UnsignedByteType;
  }

  initialize() {
    // First implementation where we suppose that each triangle is not linked to another
    // Later on we can quandrangulize the mesh before creating the texture
    // First we sort faces by decreasing resolution
    // this line makes a copy of faceColors
    if (!this.#meshColor.hasTextureMetadata()) {
      this.meshColor.computeTextureMetaData();
    }
    const resSortedFaces = this.#meshColor.getFaceColors().map((x) => x);
    this.allocateData();
    this.initTmpVariables();
    // Write colors texels and uvs
    // console.time("Mesh Color texture write data");
    const dealtFaces = new Set();
    for (let i = 0; i < resSortedFaces.length; ++i) {
      const face = resSortedFaces[i];
      if (!dealtFaces.has(face.id)) {
        const dealtfaceIds = this._writeFaceIntoTexture(face.id, this.#data, this.#textureSize, this.#dataSize);
        for (const id of dealtfaceIds) dealtFaces.add(id);
      }
    }
    // console.timeEnd("Mesh Color texture write data");
    this._resolveSeams(1);
    // console.time("Mesh Color create GPU texture (CPU part)");
    this.#colorTexture = new DataTexture(this.#data, this.#textureSize, this.#textureSize, this.#textureOptions.format === MeshColorTextureFormat.RGBA ? RGBAFormat : RedFormat, this.getTextureType());
    if (FloatTextureLinearFilterSupport()) {
      this.#colorTexture.magFilter = LinearFilter;
      this.#colorTexture.minFilter = LinearFilter;
    }
    this.#colorTexture.needsUpdate = true;
    // console.timeEnd("Mesh Color create GPU texture (CPU part)");
  }

  _fromWorkerData(textureSize, dataSize, data, textureType) {
    this.#textureSize = textureSize;
    this.#dataSize = dataSize;
    this.#data = data;
    this.#textureOptions.type = textureType;
    this.#colorTexture = new DataTexture(this.#data, textureSize, textureSize, this.#textureOptions.format === MeshColorTextureFormat.RGBA ? RGBAFormat : RedFormat, this.getTextureType());
    if (FloatTextureLinearFilterSupport()) {
      this.#colorTexture.magFilter = LinearFilter;
      this.#colorTexture.minFilter = LinearFilter;
    }
    this.initTmpVariables();
    this.#colorTexture.needsUpdate = true;
  }

  // /!\ for serialisation only -----------
  get textureSize() {
    return this.#textureSize;
  }

  get dataSize() {
    return this.#dataSize;
  }

  get textureType() {
    return this.#textureOptions.type;
  }

  get data() {
    return this.#data;
  }

  get meshColor() {
    return this.#meshColor;
  }

  //-------------------------------
  initTmpVariables() {
    this.#tmpSampledVertices = new Array(this.#meshColor.getVertexColors().length);
    for (let i = 0; i < this.#tmpSampledVertices.length; i++) {
      this.#tmpSampledVertices[i] = { needsSampling: true, color: new MColor() };
    }
    const edge_colors = this.#meshColor.getEdgeColors();
    const faces = this.#meshColor.getFaceColors();
    const textureFaceMetaData = this.#meshColor.getTextureMetadata();
    const textureQuadFaceMetaData = this.#meshColor.getTextureQuadMetadata();
    this.#tmpSampledEdges = new Array(edge_colors.length);
    for (const face of faces) {
      const quadMetaData = textureQuadFaceMetaData.get(face.id);
      const maxFaceR = quadMetaData ? quadMetaData.R : textureFaceMetaData[face.id].R;
      const edge1 = edge_colors[face.edge_ids[0]];
      const edge2 = edge_colors[face.edge_ids[1]];
      const edge3 = edge_colors[face.edge_ids[2]];
      if (!this.#tmpSampledEdges[edge1.id] || !this.#tmpSampledEdges[edge1.id][maxFaceR]) {
        if (!this.#tmpSampledEdges[edge1.id]) this.#tmpSampledEdges[edge1.id] = new Array(maxFaceR + 1);
        else if (this.#tmpSampledEdges[edge1.id].length < maxFaceR + 1) this.#tmpSampledEdges[edge1.id].length = maxFaceR + 1;
        this.#tmpSampledEdges[edge1.id][maxFaceR] = { needsSampling: true, colors: new Array(maxFaceR - 1) };
        for (let i = 0; i < maxFaceR - 1; i++) {
          this.#tmpSampledEdges[edge1.id][maxFaceR].colors[i] = new MColor();
        }
      }
      if (!this.#tmpSampledEdges[edge2.id] || !this.#tmpSampledEdges[edge2.id][maxFaceR]) {
        if (!this.#tmpSampledEdges[edge2.id]) this.#tmpSampledEdges[edge2.id] = new Array(maxFaceR + 1);
        else if (this.#tmpSampledEdges[edge2.id].length < maxFaceR + 1) this.#tmpSampledEdges[edge2.id].length = maxFaceR + 1;
        this.#tmpSampledEdges[edge2.id][maxFaceR] = { needsSampling: true, colors: new Array(maxFaceR - 1) };
        for (let i = 0; i < maxFaceR - 1; i++) {
          this.#tmpSampledEdges[edge2.id][maxFaceR].colors[i] = new MColor();
        }
      }
      if (!this.#tmpSampledEdges[edge3.id] || !this.#tmpSampledEdges[edge3.id][maxFaceR]) {
        if (!this.#tmpSampledEdges[edge3.id]) this.#tmpSampledEdges[edge3.id] = new Array(maxFaceR + 1);
        else if (this.#tmpSampledEdges[edge3.id].length < maxFaceR + 1) this.#tmpSampledEdges[edge3.id].length = maxFaceR + 1;
        this.#tmpSampledEdges[edge3.id][maxFaceR] = { needsSampling: true, colors: new Array(maxFaceR - 1) };
        for (let i = 0; i < maxFaceR - 1; i++) {
          this.#tmpSampledEdges[edge3.id][maxFaceR].colors[i] = new MColor();
        }
      }
    }
  }

  setTmpVariablesToResample(faces) {
    const edge_colors = this.#meshColor.getEdgeColors();
    const textureFaceMetaData = this.#meshColor.getTextureMetadata();
    const textureQuadFaceMetaData = this.#meshColor.getTextureQuadMetadata();
    for (const face of faces) {
      const quadMetaData = textureQuadFaceMetaData.get(face.id);
      const maxFaceR = quadMetaData ? quadMetaData.R : textureFaceMetaData[face.id].R;
      const edge1 = edge_colors[face.edge_ids[0]];
      const edge2 = edge_colors[face.edge_ids[1]];
      const edge3 = edge_colors[face.edge_ids[2]];
      this.#tmpSampledVertices[edge1.vertex_ids[0]].needsSampling = true;
      this.#tmpSampledVertices[edge1.vertex_ids[1]].needsSampling = true;
      this.#tmpSampledVertices[edge2.vertex_ids[0]].needsSampling = true;
      this.#tmpSampledVertices[edge2.vertex_ids[1]].needsSampling = true;
      this.#tmpSampledVertices[edge3.vertex_ids[0]].needsSampling = true;
      this.#tmpSampledVertices[edge3.vertex_ids[1]].needsSampling = true;
      this.#tmpSampledEdges[edge1.id][maxFaceR].needsSampling = true;
      this.#tmpSampledEdges[edge2.id][maxFaceR].needsSampling = true;
      this.#tmpSampledEdges[edge3.id][maxFaceR].needsSampling = true;
    }
  }

  _resolveSeams(maxIter = 4) {
    // console.time("Mesh Color texture seams");
    if (!this.#data) return;
    // To make the texture almost seamless some sample colors
    // must be slightly modified
    // This is an iterative process as modifing data for one face might
    // influence the result of neighbouring faces
    let facesToUpdate = [...this.#faceUpdateStack];
    while (facesToUpdate.length > 0 && maxIter > 0) {
      for (const faceUpdateId of facesToUpdate) {
        this.#faceUpdateStack.delete(faceUpdateId);
        this._writeFaceIntoTexture(faceUpdateId.id, this.#data, this.#textureSize, this.#dataSize);
      }
      facesToUpdate = [...this.#faceUpdateStack];
      maxIter--;
    }
    // console.timeEnd("Mesh Color texture seams");
  }

  _computeInterpolationColor(c0_color, c1_color, c2_color) {
    this.#tmpInterColor.copy(c1_color).add(c2_color).sub(c0_color);
    return this.#tmpInterColor;
  }

  _correctInterpolationColor(inter_color, c0_color, c1_color, c2_color, delta_acc) {
    if (this.#textureOptions.type == MeshColorTextureType.Float) return false;
    let updated_colors = false;
    if (inter_color.r < 0 || inter_color.g < 0 || inter_color.b < 0 || inter_color.a < 0) {
      const delta = this.#tmpDeltaInterColor.copy(inter_color).multiplyScalar(-0.3333333);
      if (Math.sqrt(delta.r * delta.r + delta.g * delta.g + delta.b * delta.b + delta.a * delta.a) > delta_acc) {
        if (inter_color.r < 0) {
          c0_color.r -= delta.r;
          c1_color.r += delta.r;
          c2_color.r += delta.r;
          inter_color.r = 0;
        }
        if (inter_color.g < 0) {
          c0_color.g -= delta.g;
          c1_color.g += delta.g;
          c2_color.g += delta.g;
          inter_color.g = 0;
        }
        if (inter_color.b < 0) {
          c0_color.b -= delta.b;
          c1_color.b += delta.b;
          c2_color.b += delta.b;
          inter_color.b = 0;
        }
        if (inter_color.a < 0) {
          c0_color.a -= delta.a;
          c1_color.a += delta.a;
          c2_color.a += delta.a;
          inter_color.a = 0;
        }
        updated_colors = true;
      }
    }
    if (inter_color.r > 1 || inter_color.g > 1 || inter_color.b > 1 || inter_color.a > 1) {
      const delta = this.#tmpDeltaInterColor;
      delta.setRGBA(1 - inter_color.r, 1 - inter_color.g, 1 - inter_color.b, 1 - inter_color.a);
      delta.multiplyScalar(0.3333333);
      if (Math.sqrt(delta.r * delta.r + delta.g * delta.g + delta.b * delta.b) > delta_acc) {
        if (inter_color.r > 1) {
          c0_color.r -= delta.r;
          c1_color.r += delta.r;
          c2_color.r += delta.r;
          inter_color.r = 1;
        }
        if (inter_color.g > 1) {
          c0_color.g -= delta.g;
          c1_color.g += delta.g;
          c2_color.g += delta.g;
          inter_color.g = 1;
        }
        if (inter_color.b > 1) {
          c0_color.b -= delta.b;
          c1_color.b += delta.b;
          c2_color.b += delta.b;
          inter_color.b = 1;
        }
        if (inter_color.a > 1) {
          c0_color.a -= delta.a;
          c1_color.a += delta.a;
          c2_color.a += delta.a;
          inter_color.a = 1;
        }
        updated_colors = true;
      }
    }
    return updated_colors;
  }

  _computeFaceTextureSamples(face, toOherFace = false, skipEdgeSampling = null) {
    let maxFaceR = 0;
    const faceR = face.R;
    const edge_colors = this.#meshColor.getEdgeColors();
    const edge_1 = edge_colors[face.edge_ids[0]];
    const edge_2 = edge_colors[face.edge_ids[1]];
    const edge_3 = edge_colors[face.edge_ids[2]];
    const textureFaceMetaData = this.#meshColor.getTextureMetadata();
    const textureQuadFaceMetaData = this.#meshColor.getTextureQuadMetadata();
    const quadMetaData = textureQuadFaceMetaData.get(face.id);
    maxFaceR = quadMetaData ? quadMetaData.R : textureFaceMetaData[face.id].R;
    // Resample edge_1 if necessary
    if (this.#tmpSampledEdges[edge_1.id][maxFaceR].needsSampling && skipEdgeSampling != 1) {
      if (maxFaceR > 1) {
        if (edge_1.R < maxFaceR) {
          this.#meshColor.getEdgeColorAtRes(face.edge_ids[0], maxFaceR, this.#tmpSampledEdges[edge_1.id][maxFaceR].colors);
        } else {
          for (let i = 0; i < edge_1.colors.length; ++i) {
            this.#tmpCurrentSampler(this.meshColor.getEdgeColorSamplePosId(edge_1.id, i), this.#tmpSampledEdges[edge_1.id][maxFaceR].colors[i]);
          }
        }
      }
      this.#tmpSampledEdges[edge_1.id][maxFaceR].needsSampling = false;
    }
    // Resample edge_2 if necessary
    if (this.#tmpSampledEdges[edge_2.id][maxFaceR].needsSampling && skipEdgeSampling != 2) {
      if (maxFaceR > 1) {
        if (edge_2.R < maxFaceR) {
          this.#meshColor.getEdgeColorAtRes(face.edge_ids[1], maxFaceR, this.#tmpSampledEdges[edge_2.id][maxFaceR].colors);
        } else {
          for (let i = 0; i < edge_2.colors.length; ++i) {
            this.#tmpCurrentSampler(this.meshColor.getEdgeColorSamplePosId(edge_2.id, i), this.#tmpSampledEdges[edge_2.id][maxFaceR].colors[i]);
          }
        }
      }
      this.#tmpSampledEdges[edge_2.id][maxFaceR].needsSampling = false;
    }
    // Resample edge_3 if necessary
    if (this.#tmpSampledEdges[edge_3.id][maxFaceR].needsSampling && skipEdgeSampling != 3) {
      if (maxFaceR > 1) {
        if (edge_3.R < maxFaceR) {
          this.#meshColor.getEdgeColorAtRes(face.edge_ids[2], maxFaceR, this.#tmpSampledEdges[edge_3.id][maxFaceR].colors);
        } else {
          for (let i = 0; i < edge_3.colors.length; ++i) {
            this.#tmpCurrentSampler(this.meshColor.getEdgeColorSamplePosId(edge_3.id, i), this.#tmpSampledEdges[edge_3.id][maxFaceR].colors[i]);
          }
        }
      }
      this.#tmpSampledEdges[edge_3.id][maxFaceR].needsSampling = false;
    }
    // Resample face
    const faceColorContainer = toOherFace ? this.#tmpOtherFaceColors : this.#tmpFaceColors;
    if (maxFaceR > 2) {
      if (faceR < maxFaceR) {
        this.#meshColor.getFaceColorAtRes(face.id, maxFaceR, faceColorContainer);
      } else {
        if (faceColorContainer.length < face.colors.length) {
          resizeArray(faceColorContainer, face.colors.length, this.#createColor);
        }
        for (let i = 0; i < face.colors.length; ++i) this.#tmpCurrentSampler(this.meshColor.getFaceColorSamplePosId(face.id, i), faceColorContainer[i]);
      }
    }
  }

  _writeColorIntoData(data, index, color) {
    if (this.#textureOptions.type !== MeshColorTextureType.Float) {
      data[index] = Math.min(Math.max(0, color.r), 1) * this.#texNormalizationValue;
      if (this.#textureOptions.format === MeshColorTextureFormat.RGBA) {
        data[index + 1] = Math.min(Math.max(0, color.g), 1) * this.#texNormalizationValue;
        data[index + 2] = Math.min(Math.max(0, color.b), 1) * this.#texNormalizationValue;
        data[index + 3] = Math.min(Math.max(0, color.a), 1) * this.#texNormalizationValue;
      }
    } else {
      data[index] = color.r * this.#texNormalizationValue;
      if (this.#textureOptions.format === MeshColorTextureFormat.RGBA) {
        data[index + 1] = color.g * this.#texNormalizationValue;
        data[index + 2] = color.b * this.#texNormalizationValue;
        data[index + 3] = color.a * this.#texNormalizationValue;
      }
    }
  }

  _writeQuadFaceIntoTexture(quadFaceMetaData, data, textureSize, dataSize) {
    const faceColors = this.#meshColor.getFaceColors();
    const startFace = faceColors[quadFaceMetaData.startFaceId];
    const endFace = faceColors[quadFaceMetaData.endFaceId];
    const margin_length = this.#meshColor.getTextureMarginLength();
    const edge_colors = this.#meshColor.getEdgeColors();
    const vertex_colors = this.#meshColor.getVertexColors();
    // TODO Change this regarding common Edge
    // Here we can have a mismatch between texture edge order and
    // mesh color edge order
    const { commonEdge } = quadFaceMetaData;
    let stPermutEdge1 = 0;
    let stPermutEdge2 = 1;
    let stPermutEdge3 = 2;
    switch (commonEdge) {
      case 1: {
        stPermutEdge1 = 2;
        stPermutEdge2 = 0;
        stPermutEdge3 = 1;
        break;
      }
      case 2: {
        stPermutEdge1 = 0;
        stPermutEdge2 = 1;
        stPermutEdge3 = 2;
        break;
      }
      case 3: {
        stPermutEdge1 = 1;
        stPermutEdge2 = 2;
        stPermutEdge3 = 0;
        break;
      }
    }
    const stEdge1 = edge_colors[startFace.edge_ids[stPermutEdge1]];
    const stEdge2 = edge_colors[startFace.edge_ids[stPermutEdge2]];
    const stEdge3 = edge_colors[startFace.edge_ids[stPermutEdge3]];
    const v1 = startFace.edge_orientation[stPermutEdge1] ? vertex_colors[stEdge1.vertex_ids[0]] : vertex_colors[stEdge1.vertex_ids[1]];
    const v2 = startFace.edge_orientation[stPermutEdge2] ? vertex_colors[stEdge2.vertex_ids[0]] : vertex_colors[stEdge2.vertex_ids[1]];
    const v3 = startFace.edge_orientation[stPermutEdge3] ? vertex_colors[stEdge3.vertex_ids[0]] : vertex_colors[stEdge3.vertex_ids[1]];
    const commonEdgeId = startFace.edge_ids[commonEdge - 1];
    const endFaceCommonEdge = commonEdgeId == endFace.edge_ids[0] ? 1 : commonEdgeId == endFace.edge_ids[1] ? 2 : 3;
    let edPermutEdge1 = 0;
    let edPermutEdge3 = 2;
    switch (endFaceCommonEdge) {
      case 1: {
        edPermutEdge1 = 2;
        edPermutEdge3 = 1;
        break;
      }
      case 2: {
        edPermutEdge1 = 0;
        edPermutEdge3 = 2;
        break;
      }
      case 3: {
        edPermutEdge1 = 1;
        edPermutEdge3 = 0;
        break;
      }
    }
    const edEdge1 = edge_colors[endFace.edge_ids[edPermutEdge1]];
    //  let edEdge2 = edge_colors[endFace.edge_ids[edPermutEdge2]];
    const edEdge3 = edge_colors[endFace.edge_ids[edPermutEdge3]];
    const maxFaceR = quadFaceMetaData.R;
    const current_uv = quadFaceMetaData.uv_start_pos;
    this._computeFaceTextureSamples(startFace, false);
    this._computeFaceTextureSamples(endFace, true, endFaceCommonEdge);
    // Get resample edges and faces colors
    const stEdgeColors1 = this.#tmpSampledEdges[stEdge1.id][maxFaceR].colors;
    const stEdgeColors2 = this.#tmpSampledEdges[stEdge2.id][maxFaceR].colors;
    const stEdgeColors3 = this.#tmpSampledEdges[stEdge3.id][maxFaceR].colors;
    const edEdgeColors1 = this.#tmpSampledEdges[edEdge1.id][maxFaceR].colors;
    // let edEdgeColors2 = this.#tmpSampledEdges[edEdge2.id][maxFaceR].colors;
    const edEdgeColors3 = this.#tmpSampledEdges[edEdge3.id][maxFaceR].colors;
    const stFaceColors = this.#tmpFaceColors;
    const edFaceColors = this.#tmpOtherFaceColors;
    const v4 = endFace.edge_orientation[edPermutEdge1] ? vertex_colors[edEdge1.vertex_ids[0]] : vertex_colors[edEdge1.vertex_ids[1]];
    if (this.#tmpSampledVertices[v1.id].needsSampling) {
      this.#tmpCurrentSampler(v1.id, this.#tmpSampledVertices[v1.id].color);
      this.#tmpSampledVertices[v1.id].needsSampling = false;
    }
    if (this.#tmpSampledVertices[v2.id].needsSampling) {
      this.#tmpCurrentSampler(v2.id, this.#tmpSampledVertices[v2.id].color);
      this.#tmpSampledVertices[v2.id].needsSampling = false;
    }
    if (this.#tmpSampledVertices[v3.id].needsSampling) {
      this.#tmpCurrentSampler(v3.id, this.#tmpSampledVertices[v3.id].color);
      this.#tmpSampledVertices[v3.id].needsSampling = false;
    }
    if (this.#tmpSampledVertices[v4.id].needsSampling) {
      this.#tmpCurrentSampler(v4.id, this.#tmpSampledVertices[v4.id].color);
      this.#tmpSampledVertices[v4.id].needsSampling = false;
    }
    const v1Color = this.#tmpSampledVertices[v1.id].color;
    const v2Color = this.#tmpSampledVertices[v2.id].color;
    const v3Color = this.#tmpSampledVertices[v3.id].color;
    const v4Color = this.#tmpSampledVertices[v4.id].color;
    // Vertex 1
    this._writeColorIntoData(data, current_uv.x * dataSize + current_uv.y * textureSize * dataSize, v1Color);
    // Write margin pixels
    for (let m = 1; m <= margin_length; ++m) {
      this._writeColorIntoData(data, (current_uv.x - m) * dataSize + current_uv.y * textureSize * dataSize, v1Color);
      this._writeColorIntoData(data, current_uv.x * dataSize + (current_uv.y - m) * textureSize * dataSize, v1Color);
      this._writeColorIntoData(data, (current_uv.x - m) * dataSize + (current_uv.y - m) * textureSize * dataSize, v1Color);
      for (let k = 1; k < m; k++) {
        this._writeColorIntoData(data, (current_uv.x - m + k) * dataSize + (current_uv.y - m) * textureSize * dataSize, v1Color);
        this._writeColorIntoData(data, (current_uv.x - m) * dataSize + (current_uv.y - m + k) * textureSize * dataSize, v1Color);
      }
    }
    // Vertex 2
    this._writeColorIntoData(data, current_uv.x * dataSize + (current_uv.y + maxFaceR) * textureSize * dataSize, v2Color);
    // Write margin pixels
    for (let m = 1; m <= margin_length; ++m) {
      this._writeColorIntoData(data, (current_uv.x - m) * dataSize + (current_uv.y + maxFaceR) * textureSize * dataSize, v2Color);
      this._writeColorIntoData(data, current_uv.x * dataSize + (current_uv.y + m + maxFaceR) * textureSize * dataSize, v2Color);
      this._writeColorIntoData(data, (current_uv.x - m) * dataSize + (current_uv.y + m + maxFaceR) * textureSize * dataSize, v2Color);
      for (let k = 1; k < m; k++) {
        this._writeColorIntoData(data, (current_uv.x - m + k) * dataSize + (current_uv.y + m + maxFaceR) * textureSize * dataSize, v2Color);
        this._writeColorIntoData(data, (current_uv.x - m) * dataSize + (current_uv.y + m - k + maxFaceR) * textureSize * dataSize, v2Color);
      }
    }
    // Vertex 3
    this._writeColorIntoData(data, current_uv.x * dataSize + maxFaceR * dataSize + current_uv.y * textureSize * dataSize, v3Color);
    // Write margin pixels
    for (let m = 1; m <= margin_length; ++m) {
      this._writeColorIntoData(data, (current_uv.x + m) * dataSize + maxFaceR * dataSize + current_uv.y * textureSize * dataSize, v3Color);
      this._writeColorIntoData(data, current_uv.x * dataSize + maxFaceR * dataSize + (current_uv.y - m) * textureSize * dataSize, v3Color);
      this._writeColorIntoData(data, (current_uv.x + m) * dataSize + maxFaceR * dataSize + (current_uv.y - m) * textureSize * dataSize, v3Color);
      for (let k = 1; k < m; k++) {
        this._writeColorIntoData(data, (current_uv.x + m - k) * dataSize + maxFaceR * dataSize + (current_uv.y - m) * textureSize * dataSize, v3Color);
        this._writeColorIntoData(data, (current_uv.x + m) * dataSize + maxFaceR * dataSize + (current_uv.y - m + k) * textureSize * dataSize, v3Color);
      }
    }
    // Vertex 4
    this._writeColorIntoData(data, current_uv.x * dataSize + maxFaceR * dataSize + (current_uv.y + maxFaceR) * textureSize * dataSize, v4Color);
    // Write margin pixels
    for (let m = 1; m <= margin_length; ++m) {
      this._writeColorIntoData(data, (current_uv.x + maxFaceR + m) * dataSize + (current_uv.y + maxFaceR) * textureSize * dataSize, v4Color);
      this._writeColorIntoData(data, (current_uv.x + maxFaceR) * dataSize + (current_uv.y + m + maxFaceR) * textureSize * dataSize, v4Color);
      this._writeColorIntoData(data, (current_uv.x + maxFaceR + m) * dataSize + (current_uv.y + m + maxFaceR) * textureSize * dataSize, v4Color);
      for (let k = 1; k < m; k++) {
        this._writeColorIntoData(data, (current_uv.x + maxFaceR + m + k) * dataSize + (current_uv.y + m + maxFaceR) * textureSize * dataSize, v4Color);
        this._writeColorIntoData(data, (current_uv.x + maxFaceR + m) * dataSize + (current_uv.y + m - k + maxFaceR) * textureSize * dataSize, v4Color);
      }
    }
    // Faces color are stored by increasing x coords in the Mesh Color
    let x_shift = 1;
    let y_shift = 1;
    let shiftRNb = maxFaceR - 2;
    let HVectorX = 1;
    let HVectorY = 0;
    let VVectorX = 0;
    let VVectorY = 1;
    let originX = current_uv.x;
    let originY = current_uv.y;
    switch (commonEdge) {
      case 1: {
        HVectorX = 0;
        HVectorY = -1;
        VVectorX = 1;
        VVectorY = -1;
        originX = current_uv.x;
        originY = current_uv.y + maxFaceR;
        break;
      }
      case 2: {
        HVectorX = 1;
        HVectorY = 0;
        VVectorX = 0;
        VVectorY = 1;
        originX = current_uv.x;
        originY = current_uv.y;
        break;
      }
      case 3: {
        HVectorX = -1;
        HVectorY = 1;
        VVectorX = -1;
        VVectorY = 0;
        originX = current_uv.x + maxFaceR;
        originY = current_uv.y;
        break;
      }
    }
    // write start face
    for (let j = 0; j < (Math.max(maxFaceR - 1, 0) * Math.max(maxFaceR - 2, 0)) / 2; ++j) {
      const XCoord = originX + VVectorX * y_shift + x_shift * HVectorX;
      const YCoord = originY + VVectorY * y_shift + x_shift * HVectorY;
      this._writeColorIntoData(data, XCoord * dataSize + YCoord * textureSize * dataSize, stFaceColors[j]);
      if (x_shift >= shiftRNb) {
        shiftRNb--;
        x_shift = 1;
        y_shift++;
      } else {
        x_shift++;
      }
    }
    switch (endFaceCommonEdge) {
      case 1: {
        HVectorX = 0;
        HVectorY = 1;
        VVectorX = -1;
        VVectorY = 1;
        originX = current_uv.x + maxFaceR;
        originY = current_uv.y;
        break;
      }
      case 2: {
        HVectorX = -1;
        HVectorY = 0;
        VVectorX = 0;
        VVectorY = -1;
        originX = current_uv.x + maxFaceR;
        originY = current_uv.y + maxFaceR;
        break;
      }
      case 3: {
        HVectorX = 1;
        HVectorY = -1;
        VVectorX = 1;
        VVectorY = 0;
        originX = current_uv.x;
        originY = current_uv.y + maxFaceR;
        break;
      }
    }
    // write end face
    x_shift = 1;
    y_shift = 1;
    shiftRNb = maxFaceR - 2;
    for (let j = 0; j < (Math.max(maxFaceR - 1, 0) * Math.max(maxFaceR - 2, 0)) / 2; ++j) {
      const XCoord = originX + VVectorX * y_shift + x_shift * HVectorX;
      const YCoord = originY + VVectorY * y_shift + x_shift * HVectorY;
      this._writeColorIntoData(data, XCoord * dataSize + YCoord * textureSize * dataSize, edFaceColors[j]);
      if (x_shift >= shiftRNb) {
        shiftRNb--;
        x_shift = 1;
        y_shift++;
      } else {
        x_shift++;
      }
    }
    // Add Edge 1 colors
    for (let j = 0; j < Math.max(maxFaceR - 1, 0); ++j) {
      const sample_id = startFace.edge_orientation[stPermutEdge1] ? j : maxFaceR - 1 - 1 - j;
      this._writeColorIntoData(data, current_uv.x * dataSize + (current_uv.y + j + 1) * textureSize * dataSize, stEdgeColors1[sample_id]);
      for (let m = 1; m <= margin_length; ++m) this._writeColorIntoData(data, (current_uv.x - m) * dataSize + (current_uv.y + j + 1) * textureSize * dataSize, stEdgeColors1[sample_id]);
    }
    // Add Edge 2 colors
    for (let j = 0; j < Math.max(maxFaceR - 1, 0); ++j) {
      const sample_id = startFace.edge_orientation[stPermutEdge2] ? j : maxFaceR - 1 - 1 - j;
      this._writeColorIntoData(data, current_uv.x * dataSize + (j + 1) * dataSize + (current_uv.y + maxFaceR - 1 - j) * textureSize * dataSize, stEdgeColors2[sample_id]);
    }
    // Add Edge 3 colors
    for (let j = 0; j < Math.max(maxFaceR - 1, 0); ++j) {
      // Edge 3 is stored from v3 to v1
      const sample_id = !startFace.edge_orientation[stPermutEdge3] ? j : maxFaceR - 1 - 1 - j;
      this._writeColorIntoData(data, current_uv.x * dataSize + (j + 1) * dataSize + current_uv.y * textureSize * dataSize, stEdgeColors3[sample_id]);
      for (let m = 1; m <= margin_length; ++m) this._writeColorIntoData(data, current_uv.x * dataSize + (j + 1) * dataSize + (current_uv.y - m) * textureSize * dataSize, stEdgeColors3[sample_id]);
    }
    // Add Edge 4 colors
    for (let j = 0; j < Math.max(maxFaceR - 1, 0); ++j) {
      const sample_id = !endFace.edge_orientation[edPermutEdge1] ? j : maxFaceR - 1 - 1 - j;
      this._writeColorIntoData(data, (current_uv.x + maxFaceR) * dataSize + (current_uv.y + j + 1) * textureSize * dataSize, edEdgeColors1[sample_id]);
      for (let m = 1; m <= margin_length; ++m) this._writeColorIntoData(data, (current_uv.x + maxFaceR + m) * dataSize + (current_uv.y + j + 1) * textureSize * dataSize, edEdgeColors1[sample_id]);
    }
    // Add Edge 5 colors
    for (let j = 0; j < Math.max(maxFaceR - 1, 0); ++j) {
      const sample_id = !endFace.edge_orientation[edPermutEdge3] ? j : maxFaceR - 1 - 1 - j;
      this._writeColorIntoData(data, (current_uv.x + maxFaceR - 1 - j) * dataSize + (current_uv.y + maxFaceR) * textureSize * dataSize, edEdgeColors3[sample_id]);
      for (let m = 1; m <= margin_length; ++m) this._writeColorIntoData(data, (current_uv.x + maxFaceR - 1 - j) * dataSize + (current_uv.y + maxFaceR + m) * textureSize * dataSize, edEdgeColors3[sample_id]);
    }
  }

  _writeFaceIntoTexture(face_id, data, textureSize, dataSize) {
    this.#tmpCurrentSampler = this.meshColor.getColorSampler();
    const faceColors = this.#meshColor.getFaceColors();
    const face = faceColors[face_id];
    const textureQuadFaceMetaData = this.#meshColor.getTextureQuadMetadata();
    const quadFaceMetaData = textureQuadFaceMetaData.get(face_id);
    if (quadFaceMetaData) {
      this._writeQuadFaceIntoTexture(quadFaceMetaData, data, textureSize, dataSize);
      return [quadFaceMetaData.startFaceId, quadFaceMetaData.endFaceId];
    }
    const faceTexture = this.#meshColor.getTextureMetadata()[face.id];
    const margin_length = this.#meshColor.getTextureMarginLength();
    const edge_colors = this.#meshColor.getEdgeColors();
    const vertex_colors = this.#meshColor.getVertexColors();
    const edge_1 = edge_colors[face.edge_ids[0]];
    const edge_2 = edge_colors[face.edge_ids[1]];
    const edge_3 = edge_colors[face.edge_ids[2]];
    const maxFaceR = faceTexture.R;
    const current_uv = faceTexture.uv_start_pos;
    this._computeFaceTextureSamples(face, false);
    // Get resample edges and faces colors
    const edge_colors_1 = this.#tmpSampledEdges[edge_1.id][maxFaceR].colors;
    const edge_colors_2 = this.#tmpSampledEdges[edge_2.id][maxFaceR].colors;
    const edge_colors_3 = this.#tmpSampledEdges[edge_3.id][maxFaceR].colors;
    const face_colors = this.#tmpFaceColors;
    const v_1 = face.edge_orientation[0] ? vertex_colors[edge_1.vertex_ids[0]] : vertex_colors[edge_1.vertex_ids[1]];
    const v_2 = face.edge_orientation[1] ? vertex_colors[edge_2.vertex_ids[0]] : vertex_colors[edge_2.vertex_ids[1]];
    const v_3 = face.edge_orientation[2] ? vertex_colors[edge_3.vertex_ids[0]] : vertex_colors[edge_3.vertex_ids[1]];
    if (this.#tmpSampledVertices[v_1.id].needsSampling) {
      this.#tmpCurrentSampler(v_1.id, this.#tmpSampledVertices[v_1.id].color);
      this.#tmpSampledVertices[v_1.id].needsSampling = false;
    }
    if (this.#tmpSampledVertices[v_2.id].needsSampling) {
      this.#tmpCurrentSampler(v_2.id, this.#tmpSampledVertices[v_2.id].color);
      this.#tmpSampledVertices[v_2.id].needsSampling = false;
    }
    if (this.#tmpSampledVertices[v_3.id].needsSampling) {
      this.#tmpCurrentSampler(v_3.id, this.#tmpSampledVertices[v_3.id].color);
      this.#tmpSampledVertices[v_3.id].needsSampling = false;
    }
    const v1Color = this.#tmpSampledVertices[v_1.id].color;
    const v2Color = this.#tmpSampledVertices[v_2.id].color;
    const v3Color = this.#tmpSampledVertices[v_3.id].color;
    this._writeColorIntoData(data, current_uv.x * dataSize + current_uv.y * textureSize * dataSize, v1Color);
    // Write margin pixels
    for (let m = 1; m <= margin_length; ++m) {
      this._writeColorIntoData(data, (current_uv.x - m) * dataSize + current_uv.y * textureSize * dataSize, v1Color);
      this._writeColorIntoData(data, current_uv.x * dataSize + (current_uv.y - m) * textureSize * dataSize, v1Color);
      this._writeColorIntoData(data, (current_uv.x - m) * dataSize + (current_uv.y - m) * textureSize * dataSize, v1Color);
      for (let k = 1; k < m; k++) {
        this._writeColorIntoData(data, (current_uv.x - m + k) * dataSize + (current_uv.y - m) * textureSize * dataSize, v1Color);
        this._writeColorIntoData(data, (current_uv.x - m) * dataSize + (current_uv.y - m + k) * textureSize * dataSize, v1Color);
      }
    }
    this._writeColorIntoData(data, current_uv.x * dataSize + (current_uv.y + maxFaceR) * textureSize * dataSize, v2Color);
    // Write margin pixels
    for (let m = 1; m <= margin_length; ++m) {
      this._writeColorIntoData(data, (current_uv.x - m) * dataSize + (current_uv.y + maxFaceR) * textureSize * dataSize, v2Color);
      this._writeColorIntoData(data, current_uv.x * dataSize + (current_uv.y + m + maxFaceR) * textureSize * dataSize, v2Color);
      this._writeColorIntoData(data, (current_uv.x - m) * dataSize + (current_uv.y + m + maxFaceR) * textureSize * dataSize, v2Color);
      for (let k = 1; k < m; k++) {
        this._writeColorIntoData(data, (current_uv.x - m + k) * dataSize + (current_uv.y + m + maxFaceR) * textureSize * dataSize, v2Color);
        this._writeColorIntoData(data, (current_uv.x - m) * dataSize + (current_uv.y + m - k + maxFaceR) * textureSize * dataSize, v2Color);
      }
    }
    this._writeColorIntoData(data, current_uv.x * dataSize + maxFaceR * dataSize + current_uv.y * textureSize * dataSize, v3Color);
    // Write margin pixels
    for (let m = 1; m <= margin_length; ++m) {
      this._writeColorIntoData(data, (current_uv.x + m) * dataSize + maxFaceR * dataSize + current_uv.y * textureSize * dataSize, v3Color);
      this._writeColorIntoData(data, current_uv.x * dataSize + maxFaceR * dataSize + (current_uv.y - m) * textureSize * dataSize, v3Color);
      this._writeColorIntoData(data, (current_uv.x + m) * dataSize + maxFaceR * dataSize + (current_uv.y - m) * textureSize * dataSize, v3Color);
      for (let k = 1; k < m; k++) {
        this._writeColorIntoData(data, (current_uv.x + m - k) * dataSize + maxFaceR * dataSize + (current_uv.y - m) * textureSize * dataSize, v3Color);
        this._writeColorIntoData(data, (current_uv.x + m) * dataSize + maxFaceR * dataSize + (current_uv.y - m + k) * textureSize * dataSize, v3Color);
      }
    }
    // Case where there are no edge or face colors
    if (maxFaceR == 1) {
      const mixed_col = this._computeInterpolationColor(v1Color, v2Color, v3Color);
      const delta_acc = 0.01;
      const update_rel_faces = this._correctInterpolationColor(mixed_col, v1Color, v2Color, v3Color, delta_acc);
      if (update_rel_faces) {
        let related_faces = [];
        const v_1_rels = this.#meshColor.getVertexRelativeFaces(v_1.id);
        for (const rel_faces of v_1_rels) related_faces.push(rel_faces);
        const v_2_rels = this.#meshColor.getVertexRelativeFaces(v_2.id);
        for (const rel_faces of v_2_rels) related_faces.push(rel_faces);
        const v_3_rels = this.#meshColor.getVertexRelativeFaces(v_3.id);
        for (const rel_faces of v_3_rels) related_faces.push(rel_faces);
        related_faces = related_faces.filter((f) => f.id != face.id);
        for (const rel_faces of related_faces) this.#faceUpdateStack.add(rel_faces);
      }
      const inter_coords = (current_uv.x + maxFaceR) * dataSize + (current_uv.y + maxFaceR) * textureSize * dataSize;
      this._writeColorIntoData(data, inter_coords, mixed_col);
      return [face.id];
    }
    // Faces color are stored by increasing x coords in the Mesh Color
    let x_shift = 1;
    let y_shift = 1;
    let shiftRNb = maxFaceR - 2;
    for (let j = 0; j < (Math.max(maxFaceR - 1, 0) * Math.max(maxFaceR - 2, 0)) / 2; ++j) {
      this._writeColorIntoData(data, current_uv.x * dataSize + x_shift * dataSize + (current_uv.y + y_shift) * textureSize * dataSize, face_colors[j]);
      if (x_shift >= shiftRNb) {
        shiftRNb--;
        x_shift = 1;
        y_shift++;
      } else {
        x_shift++;
      }
    }
    // Add Edge 1 colors
    for (let j = 0; j < Math.max(maxFaceR - 1, 0); ++j) {
      const sample_id = face.edge_orientation[0] ? j : maxFaceR - 1 - 1 - j;
      this._writeColorIntoData(data, current_uv.x * dataSize + (current_uv.y + j + 1) * textureSize * dataSize, edge_colors_1[sample_id]);
      for (let m = 1; m <= margin_length; ++m) this._writeColorIntoData(data, (current_uv.x - m) * dataSize + (current_uv.y + j + 1) * textureSize * dataSize, edge_colors_1[sample_id]);
    }
    // Add Edge 2 colors
    for (let j = 0; j < Math.max(maxFaceR - 1, 0); ++j) {
      const sample_id = face.edge_orientation[1] ? j : maxFaceR - 1 - 1 - j;
      this._writeColorIntoData(data, current_uv.x * dataSize + (j + 1) * dataSize + (current_uv.y + maxFaceR - 1 - j) * textureSize * dataSize, edge_colors_2[sample_id]);
      // Adding extra texel for interpolation on the diagonal,c3 in the original paper
      let inter_color_coords = current_uv.x * dataSize + (j + 1) * dataSize + (current_uv.y + maxFaceR - 1 - j + 1) * textureSize * dataSize;
      const edge_related_faces = this.#meshColor.getEdgeRelativeFaces(edge_2.id);
      let inter_color;
      {
        // c3 = c1 + c2 - c0
        // c1 is inter_color
        const c1_color = j > 0 ? (face.edge_orientation[1] ? edge_colors_2[sample_id - 1] : edge_colors_2[sample_id + 1]) : v2Color;
        const c0_color = maxFaceR > 2 ? (j > 0 ? getFaceColorSample(face_colors, maxFaceR, j, maxFaceR - 1 - j) : face.edge_orientation[0] ? edge_colors_1[maxFaceR - 1 - 1] : edge_colors_1[0]) : face.edge_orientation[0] ? edge_colors_1[maxFaceR - 1 - 1] : edge_colors_1[0];
        const c2_color = edge_colors_2[sample_id];
        inter_color = this._computeInterpolationColor(c0_color, c1_color, c2_color);
        const update_rel_faces = this._correctInterpolationColor(inter_color, c0_color, c1_color, c2_color, 0.01);
        if (update_rel_faces) {
          let related_update_faces = new Array();
          if (edge_related_faces) {
            for (const rel_faces of edge_related_faces) related_update_faces.push(rel_faces);
          }
          if (j == 0) {
            const v_2_rels = this.#meshColor.getVertexRelativeFaces(v_2.id);
            for (const rel_faces of v_2_rels) related_update_faces.push(rel_faces);
          }
          related_update_faces = related_update_faces.filter((f) => f.id != face.id);
          for (const rel_faces of related_update_faces) this.#faceUpdateStack.add(rel_faces);
        }
      }
      this._writeColorIntoData(data, inter_color_coords, inter_color);
      for (let m = 1; m <= margin_length; ++m) this._writeColorIntoData(data, inter_color_coords + m * textureSize * dataSize, inter_color);
      // Last fragment add interpolated color between last edge color sample and vertex 3
      if (j == maxFaceR - 1 - 1) {
        inter_color_coords = current_uv.x * dataSize + (j + 1 + 1) * dataSize + (current_uv.y + maxFaceR - 1 - j) * textureSize * dataSize;
        const c1_color = edge_colors_2[sample_id];
        const c0_color = !face.edge_orientation[2] ? edge_colors_3[maxFaceR - 1 - 1] : edge_colors_3[0];
        const c2_color = v3Color;
        inter_color = this._computeInterpolationColor(c0_color, c1_color, c2_color);
        const update_rel_faces = this._correctInterpolationColor(inter_color, c0_color, c1_color, c2_color, 0.01);
        if (update_rel_faces) {
          let related_update_faces = new Array();
          if (edge_related_faces) {
            for (const rel_faces of edge_related_faces) related_update_faces.push(rel_faces);
          }
          const v_3_rels = this.#meshColor.getVertexRelativeFaces(v_3.id);
          for (const rel_faces of v_3_rels) related_update_faces.push(rel_faces);
          related_update_faces = related_update_faces.filter((f) => f.id != face.id);
          for (const rel_faces of related_update_faces) this.#faceUpdateStack.add(rel_faces);
        }
        this._writeColorIntoData(data, inter_color_coords, inter_color);
        for (let m = 1; m <= margin_length; ++m) {
          this._writeColorIntoData(data, inter_color_coords + m * textureSize * dataSize, inter_color);
          this._writeColorIntoData(data, inter_color_coords + m * dataSize, inter_color);
        }
      }
    }
    // Add Edge 3 colors
    for (let j = 0; j < Math.max(maxFaceR - 1, 0); ++j) {
      // Edge 3 is stored from v3 to v1
      const sample_id = !face.edge_orientation[2] ? j : maxFaceR - 1 - 1 - j;
      this._writeColorIntoData(data, current_uv.x * dataSize + (j + 1) * dataSize + current_uv.y * textureSize * dataSize, edge_colors_3[sample_id]);
      for (let m = 1; m <= margin_length; ++m) this._writeColorIntoData(data, current_uv.x * dataSize + (j + 1) * dataSize + (current_uv.y - m) * textureSize * dataSize, edge_colors_3[sample_id]);
    }
    return [face.id];
  }

  /**
   *
   * @returns {DataTexture}
   */
  getTexture() {
    return this.#colorTexture;
  }

  getMeshColor() {
    return this.#meshColor;
  }

  updateMeshColorTextureFace(faces) {
    if (!this.#data || !this.#colorTexture || faces.length === 0) return;
    // re init face to update for seams correction
    this.#faceUpdateStack = new Set();
    const dealtFaces = new Set();
    this.setTmpVariablesToResample(faces);
    // console.time("update texture write time")
    for (const face of faces) {
      if (!dealtFaces.has(face.id)) {
        const dealtfaceIds = this._writeFaceIntoTexture(face.id, this.#data, this.#textureSize, this.#dataSize);
        for (const id of dealtfaceIds) dealtFaces.add(id);
      }
    }
    this._resolveSeams(1);
    // console.timeEnd("update texture write time");
    this.#colorTexture.needsUpdate = true;
  }

  updateMeshColorTexture(mesh_color_changed = false) {
    if (mesh_color_changed) {
      this.updateMeshColorTextureFace(this.#meshColor.getFaceColors());
    }
  }

  dispose() {
    if (this.#colorTexture) this.#colorTexture.dispose();
  }
}
/* export function updateMeshColorTexture(renderer: WebGLRenderer, dstTexture: Texture) {
    let data = new Uint8Array(4);
    let t = new DataTexture(data,1,1);
    renderer.copyTextureToTexture(new Vector2(),t,dstTexture);
} */

class MeshColorTextureSerializer {
  /**
   * Serialize a BufferGeometry into a structure optimized for fast transfer between workers.
   */
  static toWorkerData(meshColorTexture, meshColorWorkerData) {
    const meshColorData = meshColorWorkerData ?? MeshColorSerializer.toWorkerData(meshColorTexture.meshColor);
    if (!meshColorData) {
      return null;
    }
    const workerData = {
      metadata: {
        type: "MeshColorTextureWorkerData",
        generator: "MeshColorTextureSerializer.toWorkerData"
      },
      data: {
        textureSize: meshColorTexture.textureSize,
        dataSize: meshColorTexture.dataSize,
        data: meshColorTexture.data,
        textureType: meshColorTexture.textureType,
        meshColor: meshColorData
      }
    };
    return workerData;
  }

  /**
   * Deserialize a new MeshColor from workerData
   */
  static fromWorkerData(workerData, meshColor) {
    if (workerData.metadata.type != "MeshColorTextureWorkerData") throw "MeshColorTextureSerializer: data is not of type MeshColorTexture";
    if (!workerData.data.data) throw "Cannot create MeshColorTexture, worker data is null";
    const { dataSize } = workerData.data;
    const { textureSize } = workerData.data;
    // console.time("texture data array creation");
    const textureData = workerData.data.textureType === MeshColorTextureType.Float ? new Float32Array(workerData.data.data) : workerData.data.textureType === MeshColorTextureType.Short ? new Uint16Array(workerData.data.data) : new Uint8Array(workerData.data.data);
    // console.timeEnd("texture data array creation");
    // console.time("texture face data reconstruct");
    // console.timeEnd("texture face data reconstruct");
    const meshColorTexture = new MeshColorTexture(meshColor ?? MeshColorSerializer.fromWorkerData(workerData.data.meshColor));
    meshColorTexture._fromWorkerData(textureSize, dataSize, textureData, workerData.data.textureType);
    return meshColorTexture;
  }

  /**
   * Returns all arraybuffers that can be transfered to a worker via shared memory
   */
  static getTransferables(workerData, transferMeshColor = true) {
    if (workerData.metadata.type != "MeshColorTextureWorkerData") throw "MeshColorTextureSerializer: data is not of type MeshColorTexture";
    if (transferMeshColor) {
      return [workerData.data.data.buffer, MeshColorSerializer.getTransferables(workerData.data.meshColor)];
    }
    return [workerData.data.data.buffer];
  }

  static async saveMeshColorTextureDataToArrayData(meshColorTexture, saveColorData = false) {
    const arraysToExport = new Array();
    arraysToExport.push(new Uint8Array(new Uint32Array([meshColorTexture.textureSize]).buffer));
    arraysToExport.push(new Uint8Array(new Uint32Array([meshColorTexture.dataSize]).buffer));
    arraysToExport.push(new Uint8Array([meshColorTexture.textureType == MeshColorTextureType.Byte ? 0 : meshColorTexture.textureType == MeshColorTextureType.Short ? 1 : meshColorTexture.textureType == MeshColorTextureType.Float ? 2 : 2]));
    arraysToExport.push(new Uint8Array([saveColorData ? 1 : 0]));
    if (saveColorData && meshColorTexture.data) {
      arraysToExport.push(meshColorTexture.data instanceof Uint8Array ? meshColorTexture.data : new Uint8Array(meshColorTexture.data.buffer));
    }
    // Get the total length of all arrays.
    let exportArrayLength = 0;
    arraysToExport.forEach((item) => {
      exportArrayLength += item.length;
    });
    // Create a new array with total length and merge all source arrays.
    const exportArray = new Uint8Array(exportArrayLength);
    let offset = 0;
    arraysToExport.forEach((item) => {
      exportArray.set(item, offset);
      offset += item.length;
    });
    return exportArray;
  }

  static async loadMeshColorTextureDataToArrayData(zipData, meshColor) {
    const view = new DataView(zipData.buffer);
    let currOffset = 0;
    const textureSize = view.getUint32(currOffset, true);
    currOffset += 4;
    const dataSize = view.getUint32(currOffset, true);
    currOffset += 4;
    const textureType = view.getUint8(currOffset);
    currOffset += 1;
    const saveColorData = view.getUint8(currOffset);
    // TODO check type.
    let texture_data = new Float32Array();
    if (saveColorData) {
      texture_data = new Float32Array(zipData.slice(currOffset, zipData.length).buffer);
    } else {
      texture_data = new Float32Array(dataSize * textureSize * textureSize);
    }
    const meshColorTexture = new MeshColorTexture(meshColor);
    meshColorTexture._fromWorkerData(textureSize, dataSize, texture_data, textureType == 0 ? MeshColorTextureType.Byte : textureType == 1 ? MeshColorTextureType.Short : textureType == 2 ? MeshColorTextureType.Float : MeshColorTextureType.Float);
    return meshColorTexture;
  }
}

export { MColor as M, OklabToRGB as O, RGBToOklab as R, Signal as S, MeshColorTexture as a, MeshColorTextureFormat as b, MeshColorTextureType as c, MeshColorSerializer as d, MeshColorTextureSerializer as e, MeshColorSkinned as f, getMeshColorFacesFromVertexSelection as g, strToU8 as h, MeshColor as i, MeshColorInternal as j, estimateMeshColorPointResolution as k, estimateMeshColorTextureResolution as l, getFaceColorSample as m, mergeMeshColorAndTextures as n, mixinMeshColor as o, strFromU8 as s, zipSync as z };
